import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  FileCheck, 
  FileSearch, 
  DollarSign, 
  ShoppingCart, 
  Truck, 
  Send, 
  Paperclip,
  Plus,
  ArrowRight,
  ArrowLeft,
  Save,
  Eye,
  User,
  Building,
  Calendar,
  Hash,
  Trash2,
  PlusCircle,
  Download,
  CheckCircle,
  Settings
} from 'lucide-react';
import { formatDZD } from '../utils/currency';
import { downloadDocumentPDF, previewDocumentPDF } from '../utils/pdfGenerator';
import DocumentPreview from './DocumentPreview';
import { useSettings } from '../contexts/SettingsContext';
import { useClients } from '../contexts/ClientsContext';
import { useDocuments } from '../contexts/DocumentsContext';
import { useEmployees } from '../contexts/EmployeesContext';
import { useProducts } from '../contexts/ProductsContext';
import './DocumentCreation.css';

const DocumentCreation = () => {
  const { settings, incrementDocumentCounter } = useSettings();
  const { clients: allClients, suppliers: allSuppliers, getActiveClients, getActiveSuppliers } = useClients();
  const { addDocument } = useDocuments();
  const { employees, getActiveEmployees } = useEmployees();
  const { products, getActiveProducts } = useProducts();
  const [currentStep, setCurrentStep] = useState('select'); // 'select', 'create', or 'preview'
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});

  // Générer le numéro automatiquement quand le type est sélectionné
  useEffect(() => {
    if (selectedType && !formData.number) {
      const documentNumber = generateDocumentNumber(selectedType.id);
      setFormData(prev => ({
        ...prev,
        number: documentNumber
      }));
    }
  }, [selectedType]);

  // Utiliser les clients et fournisseurs actifs du contexte partagé
  const clients = getActiveClients();
  const suppliers = getActiveSuppliers();
  // Utiliser les employés actifs du contexte partagé
  const activeEmployees = getActiveEmployees();
  // Utiliser les produits actifs du contexte partagé
  const activeProducts = getActiveProducts();

  // Sélectionner automatiquement le premier fournisseur pour les bons de commande
  useEffect(() => {
    if (selectedType) {
      // Pour les bons de commande, sélectionner le premier fournisseur
      if (selectedType.id === 'purchase-order' && !formData.supplier && suppliers.length > 0) {
        setFormData(prev => ({
          ...prev,
          supplier: suppliers[0].id.toString()
        }));
      }
      
      // Pour les autres documents, on peut éventuellement sélectionner un client par défaut
      // Pour l'instant on laisse l'utilisateur choisir
    }
  }, [selectedType, suppliers, formData.client, formData.supplier]);

  // Fonction pour générer les numéros de documents avec les préfixes configurés en temps réel
  const generateDocumentNumber = (docType) => {
    // Mapping des types de documents vers les préfixes configurés
    const prefixMap = {
      'invoice': settings?.documentPrefixes?.invoice || 'FAC',
      'proforma': settings?.documentPrefixes?.proforma || 'PRO',
      'quote-request': settings?.documentPrefixes?.quoteRequest || 'DDV',
      'quote': settings?.documentPrefixes?.quote || 'DEV',
      'purchase-order': settings?.documentPrefixes?.purchaseOrder || 'BC',
      'attachment': settings?.documentPrefixes?.attachment || 'ATT',
      'delivery-note': settings?.documentPrefixes?.deliveryNote || 'BL',
      'shipping-slip': settings?.documentPrefixes?.shippingSlip || 'BE',
      'reception-note': settings?.documentPrefixes?.receptionNote || 'BR',
      'return-slip': settings?.documentPrefixes?.returnSlip || 'BRT',
      'stock-sheet': settings?.documentPrefixes?.stockSheet || 'FS',
      'material-exit': settings?.documentPrefixes?.materialExit || 'BSM',
      'credit-note': settings?.documentPrefixes?.creditNote || 'AVO',
      'account-statement': settings?.documentPrefixes?.accountStatement || 'RLC',
      'advance-invoice': settings?.documentPrefixes?.advanceInvoice || 'ACO',
      'payment-receipt': settings?.documentPrefixes?.paymentReceipt || 'REC',
      'payment-schedule': settings?.documentPrefixes?.paymentSchedule || 'ECH',
      'commercial-contract': settings?.documentPrefixes?.commercialContract || 'CC',
      'service-agreement': settings?.documentPrefixes?.serviceAgreement || 'CS',
      'compliance-certificate': settings?.documentPrefixes?.complianceCertificate || 'CERT',
      'formal-notice': settings?.documentPrefixes?.formalNotice || 'MD',
      'reception-report': settings?.documentPrefixes?.receptionReport || 'PVR',
      'intervention-report': settings?.documentPrefixes?.interventionReport || 'RI',
      'non-compliance-sheet': settings?.documentPrefixes?.nonComplianceSheet || 'FNC',
      'tax-certificate': settings?.documentPrefixes?.taxCertificate || 'AF',
      'work-certificate': settings?.documentPrefixes?.workCertificate || 'CTE',
      'legal-notice': settings?.documentPrefixes?.legalNotice || 'NL',
      'payslip': settings?.documentPrefixes?.payslip || 'FP',
      'work-contract': settings?.documentPrefixes?.workContract || 'CT',
      'work-attestation': settings?.documentPrefixes?.workAttestation || 'AT',
      'leave-request': settings?.documentPrefixes?.leaveRequest || 'DC',
      'production-sheet': settings?.documentPrefixes?.productionSheet || 'FPR',
      'quality-control': settings?.documentPrefixes?.qualityControl || 'CQ',
      'manufacturing-order': settings?.documentPrefixes?.manufacturingOrder || 'BF',
      'failure-report': settings?.documentPrefixes?.failureReport || 'RD',
      'route-sheet': settings?.documentPrefixes?.routeSheet || 'FR',
      'delivery-report': settings?.documentPrefixes?.deliveryReport || 'RL',
      'transport-order': settings?.documentPrefixes?.transportOrder || 'BT',
      'rental-contract': settings?.documentPrefixes?.rentalContract || 'CL',
      'property-inspection': settings?.documentPrefixes?.propertyInspection || 'EDL',
      'rent-receipt': settings?.documentPrefixes?.rentReceipt || 'QL',
      'accident-report': settings?.documentPrefixes?.accidentReport || 'DA',
      'security-report': settings?.documentPrefixes?.securityReport || 'RS',
      'safety-compliance': settings?.documentPrefixes?.safetyCompliance || 'FCS',
      'maintenance-report': settings?.documentPrefixes?.maintenanceReport || 'RM',
      'maintenance-plan': settings?.documentPrefixes?.maintenancePlan || 'PM',
      'client-intervention': settings?.documentPrefixes?.clientIntervention || 'FIC',
      'product-warranty': settings?.documentPrefixes?.productWarranty || 'GAR',
      'it-audit': settings?.documentPrefixes?.itAudit || 'RAI',
      'software-install': settings?.documentPrefixes?.softwareInstall || 'FIL',
      'it-maintenance': settings?.documentPrefixes?.itMaintenance || 'CMIT',
      'backup': settings?.documentPrefixes?.backup || 'BAK',
      'purchase-request': settings?.documentPrefixes?.purchaseRequest || 'DA',
      'tender-call': settings?.documentPrefixes?.tenderCall || 'AO',
      'supplier-comparison': settings?.documentPrefixes?.supplierComparison || 'CF',
      'goods-reception': settings?.documentPrefixes?.goodsReception || 'RM',
      'training-plan': settings?.documentPrefixes?.trainingPlan || 'PF',
      'training-evaluation': settings?.documentPrefixes?.trainingEvaluation || 'EF',
      'training-certificate': settings?.documentPrefixes?.trainingCertificate || 'CF',
      'prospect-sheet': settings?.documentPrefixes?.prospectSheet || 'FP',
      'client-visit-report': settings?.documentPrefixes?.clientVisitReport || 'RVC',
      'price-catalog': settings?.documentPrefixes?.priceCatalog || 'CAT',
      'equipment-sheet': settings?.documentPrefixes?.equipmentSheet || 'FE',
      'calibration': settings?.documentPrefixes?.calibration || 'CAL',
      'material-loan': settings?.documentPrefixes?.materialLoan || 'PM',
      'dashboard': settings?.documentPrefixes?.dashboard || 'TDB',
      'financial-report': settings?.documentPrefixes?.financialReport || 'RF',
      'market-study': settings?.documentPrefixes?.marketStudy || 'EM',
      'benchmark': settings?.documentPrefixes?.benchmark || 'BEN'
    };

    const prefix = prefixMap[docType] || 'DOC';
    
    // Obtenir le compteur actuel pour ce type de document
    const counterMap = {
      'invoice': 'invoice',
      'proforma': 'proforma', 
      'quote-request': 'quoteRequest',
      'quote': 'quote',
      'purchase-order': 'purchaseOrder',
      'attachment': 'attachment',
      'delivery-note': 'deliveryNote',
      'shipping-slip': 'shippingSlip',
      'reception-note': 'receptionNote',
      'return-slip': 'returnSlip',
      'stock-sheet': 'stockSheet',
      'material-exit': 'materialExit',
      'credit-note': 'creditNote',
      'account-statement': 'accountStatement',
      'advance-invoice': 'advanceInvoice',
      'payment-receipt': 'paymentReceipt',
      'payment-schedule': 'paymentSchedule',
      'commercial-contract': 'commercialContract',
      'service-agreement': 'serviceAgreement',
      'compliance-certificate': 'complianceCertificate',
      'formal-notice': 'formalNotice',
      'reception-report': 'receptionReport',
      'intervention-report': 'interventionReport',
      'non-compliance-sheet': 'nonComplianceSheet',
      'tax-certificate': 'taxCertificate',
      'work-certificate': 'workCertificate',
      'legal-notice': 'legalNotice',
      'payslip': 'payslip',
      'work-contract': 'workContract',
      'work-attestation': 'workAttestation',
      'leave-request': 'leaveRequest',
      'production-sheet': 'productionSheet',
      'quality-control': 'qualityControl',
      'manufacturing-order': 'manufacturingOrder',
      'failure-report': 'failureReport',
      'route-sheet': 'routeSheet',
      'delivery-report': 'deliveryReport',
      'transport-order': 'transportOrder',
      'rental-contract': 'rentalContract',
      'property-inspection': 'propertyInspection',
      'rent-receipt': 'rentReceipt',
      'accident-report': 'accidentReport',
      'security-report': 'securityReport',
      'safety-compliance': 'safetyCompliance',
      'maintenance-report': 'maintenanceReport',
      'maintenance-plan': 'maintenancePlan',
      'client-intervention': 'clientIntervention',
      'product-warranty': 'productWarranty',
      'it-audit': 'itAudit',
      'software-install': 'softwareInstall',
      'it-maintenance': 'itMaintenance',
      'backup': 'backup',
      'purchase-request': 'purchaseRequest',
      'tender-call': 'tenderCall',
      'supplier-comparison': 'supplierComparison',
      'goods-reception': 'goodsReception',
      'training-plan': 'trainingPlan',
      'training-evaluation': 'trainingEvaluation',
      'training-certificate': 'trainingCertificate',
      'prospect-sheet': 'prospectSheet',
      'client-visit-report': 'clientVisitReport',
      'price-catalog': 'priceCatalog',
      'equipment-sheet': 'equipmentSheet',
      'calibration': 'calibration',
      'material-loan': 'materialLoan',
      'dashboard': 'dashboard',
      'financial-report': 'financialReport',
      'market-study': 'marketStudy',
      'benchmark': 'benchmark'
    };

    const counterKey = counterMap[docType];
    const currentCounter = counterKey ? 
      (settings?.invoicing?.documentCounters?.[counterKey] || 1) : 1;
    
    // Format du numéro avec 01, 02, etc.
    const sequence = currentCounter.toString().padStart(2, '0');
    
    return `${prefix}-${sequence}`;
  };

  const documentCategories = [
    {
      title: 'FACTURATION',
      color: '#3b82f6',
      documents: [
        {
          id: 'invoice',
          title: 'Créer une facture',
          subtitle: 'Facture standard',
          icon: FileText,
          color: '#3b82f6',
          description: 'Créez une facture conforme à la réglementation algérienne (TVA 19%)'
        },
        {
          id: 'proforma',
          title: 'Créer une facture proforma',
          subtitle: 'Document préétablie',
          icon: FileCheck,
          color: '#10b981',
          description: 'Facture préliminaire conforme au droit algérien'
        },
        {
          id: 'quote-request',
          title: 'Créer une demande de devis',
          subtitle: 'Demande de prix',
          icon: FileSearch,
          color: '#f59e0b',
          description: 'Demandez des devis à vos fournisseurs'
        }
      ]
    },
    {
      title: 'OFFRES COMMERCIALES',
      color: '#ec4899',
      documents: [
        {
          id: 'quote',
          title: 'Créer un devis',
          subtitle: 'Proposition commerciale',
          icon: DollarSign,
          color: '#ec4899',
          description: 'Proposez vos services et produits'
        },
        {
          id: 'purchase-order',
          title: 'Créer un bon de commande',
          subtitle: 'Commande officielle',
          icon: ShoppingCart,
          color: '#8b5cf6',
          description: 'Formalisez vos commandes'
        },
        {
          id: 'attachment',
          title: 'Créer un attachement',
          subtitle: 'Cahier des charges',
          icon: Paperclip,
          color: '#64748b',
          description: 'Spécifications techniques et besoins détaillés'
        }
      ]
    },
    {
      title: 'LOGISTIQUE',
      color: '#06b6d4',
      beta: true,
      documents: [
        {
          id: 'shipping-slip',
          title: 'Créer un bordereau d\'envoi',
          subtitle: 'Document de transfert',
          icon: Send,
          color: '#84cc16',
          description: 'Bordereau pour les expéditions'
        },
        {
          id: 'reception-note',
          title: 'Créer un bon de réception',
          subtitle: 'Accusé de réception',
          icon: CheckCircle,
          color: '#10b981',
          description: 'Confirmer la réception des marchandises'
        },
        {
          id: 'return-slip',
          title: 'Créer un bordereau de retour',
          subtitle: 'Retours marchandises',
          icon: ArrowLeft,
          color: '#dc2626',
          description: 'Retours de marchandises défectueuses'
        },
        {
          id: 'material-exit',
          title: 'Créer un bon de sortie matériel',
          subtitle: 'Sorties entrepôt',
          icon: ArrowRight,
          color: '#f59e0b',
          description: 'Sorties de matériel d\'entrepôt'
        }
      ]
    },
    {
      title: 'COMPTABILITÉ & FINANCE',
      color: '#f59e0b',
      documents: [
        {
          id: 'credit-note',
          title: 'Créer un avoir',
          subtitle: 'Note de crédit',
          icon: FileText,
          color: '#dc2626',
          description: 'Remboursements, retours et corrections de factures'
        },
        {
          id: 'account-statement',
          title: 'Créer un relevé de compte',
          subtitle: 'État des créances',
          icon: FileCheck,
          color: '#f59e0b',
          description: 'Suivi des paiements et soldes clients'
        },
        {
          id: 'advance-invoice',
          title: 'Créer une facture d\'acompte',
          subtitle: 'Paiement partiel',
          icon: DollarSign,
          color: '#059669',
          description: 'Facturation d\'avances avant livraison'
        }
      ]
    },
    {
      title: 'DOCUMENTS CONTRACTUELS',
      color: '#7c3aed',
      documents: [
        {
          id: 'commercial-contract',
          title: 'Créer un contrat commercial',
          subtitle: 'Accord formel',
          icon: FileText,
          color: '#7c3aed',
          description: 'Contrats et accords commerciaux avec clients'
        },
        {
          id: 'service-agreement',
          title: 'Créer une convention de service',
          subtitle: 'Accord de maintenance',
          icon: FileCheck,
          color: '#059669',
          description: 'Conventions de maintenance et support technique'
        },
        {
          id: 'compliance-certificate',
          title: 'Créer un certificat de conformité',
          subtitle: 'Validation qualité',
          icon: CheckCircle,
          color: '#dc2626',
          description: 'Certification de conformité des produits/services'
        }
      ]
    },
    {
      title: 'PAIEMENTS & RECOUVREMENT',
      color: '#dc2626',
      documents: [
        {
          id: 'payment-demand',
          title: 'Créer une mise en demeure',
          subtitle: 'Relance client',
          icon: FileText,
          color: '#dc2626',
          description: 'Relances formelles pour clients impayés'
        },
        {
          id: 'payment-receipt',
          title: 'Créer un reçu de paiement',
          subtitle: 'Accusé réception',
          icon: CheckCircle,
          color: '#059669',
          description: 'Accusés de réception des paiements'
        },
        {
          id: 'payment-schedule',
          title: 'Créer un échéancier',
          subtitle: 'Planning paiements',
          icon: Calendar,
          color: '#f59e0b',
          description: 'Planning détaillé des échéances de paiement'
        }
      ]
    },
    {
      title: 'PROJETS & TRAVAUX',
      color: '#059669',
      documents: [
        {
          id: 'reception-report',
          title: 'Créer un procès-verbal de réception',
          subtitle: 'Validation fin travaux',
          icon: CheckCircle,
          color: '#059669',
          description: 'Validation officielle de fin de travaux'
        },
        {
          id: 'intervention-report',
          title: 'Créer un rapport d\'intervention',
          subtitle: 'Compte-rendu technique',
          icon: FileText,
          color: '#3b82f6',
          description: 'Comptes-rendus techniques détaillés'
        },
        {
          id: 'non-compliance',
          title: 'Créer une fiche de non-conformité',
          subtitle: 'Signalement défauts',
          icon: FileCheck,
          color: '#dc2626',
          description: 'Signalement de défauts et non-conformités'
        }
      ]
    },
    {
      title: 'DOCUMENTS LÉGAUX',
      color: '#4f46e5',
      documents: [
        {
          id: 'tax-certificate',
          title: 'Créer une attestation fiscale',
          subtitle: 'Document administratif',
          icon: FileText,
          color: '#4f46e5',
          description: 'Attestations pour administration algérienne'
        },
        {
          id: 'work-certificate',
          title: 'Créer un certificat de travail effectué',
          subtitle: 'Preuve réalisation',
          icon: CheckCircle,
          color: '#059669',
          description: 'Preuve officielle de réalisation de travaux'
        },
        {
          id: 'legal-notice',
          title: 'Créer une notification légale',
          subtitle: 'Communication officielle',
          icon: FileCheck,
          color: '#dc2626',
          description: 'Notifications et communications légales'
        }
      ]
    },
    {
      title: 'RESSOURCES HUMAINES',
      color: '#8b5cf6',
      documents: [
        {
          id: 'payslip',
          title: 'Créer une fiche de paie',
          subtitle: 'Bulletin de salaire',
          icon: FileText,
          color: '#8b5cf6',
          description: 'Bulletins de salaire conformes au droit algérien'
        },
        {
          id: 'work-contract',
          title: 'Créer un contrat de travail',
          subtitle: 'Embauche CDI/CDD',
          icon: FileCheck,
          color: '#059669',
          description: 'Contrats d\'embauche conformes au Code du travail'
        },
        {
          id: 'employment-certificate',
          title: 'Créer une attestation de travail',
          subtitle: 'Certificat d\'emploi',
          icon: CheckCircle,
          color: '#3b82f6',
          description: 'Attestations d\'emploi et certificats de travail'
        },
        {
          id: 'leave-request',
          title: 'Créer une demande de congé',
          subtitle: 'Gestion absences',
          icon: Calendar,
          color: '#f59e0b',
          description: 'Demandes de congé et gestion des absences'
        }
      ]
    },
    {
      title: 'PRODUCTION & QUALITÉ',
      color: '#10b981',
      documents: [
        {
          id: 'production-sheet',
          title: 'Créer une fiche de production',
          subtitle: 'Suivi fabrication',
          icon: FileText,
          color: '#10b981',
          description: 'Suivi des processus de fabrication'
        },
        {
          id: 'quality-control',
          title: 'Créer un contrôle qualité',
          subtitle: 'Tests et validations',
          icon: CheckCircle,
          color: '#059669',
          description: 'Contrôles qualité et tests de validation'
        },
        {
          id: 'manufacturing-order',
          title: 'Créer un bon de fabrication',
          subtitle: 'Ordre de production',
          icon: FileCheck,
          color: '#3b82f6',
          description: 'Ordres de fabrication et production'
        },
        {
          id: 'failure-report',
          title: 'Créer un rapport de défaillance',
          subtitle: 'Analyse d\'incidents',
          icon: FileText,
          color: '#dc2626',
          description: 'Analyses d\'incidents et défaillances'
        }
      ]
    },
    {
      title: 'TRANSPORT & LIVRAISON',
      color: '#f59e0b',
      documents: [
        {
          id: 'route-sheet',
          title: 'Créer une feuille de route',
          subtitle: 'Planification tournées',
          icon: FileText,
          color: '#f59e0b',
          description: 'Planification des tournées de livraison'
        },
        {
          id: 'delivery-report',
          title: 'Créer un rapport de livraison',
          subtitle: 'Compte-rendu chauffeur',
          icon: Truck,
          color: '#059669',
          description: 'Comptes-rendus détaillés des chauffeurs'
        },
        {
          id: 'transport-voucher',
          title: 'Créer un bon de transport',
          subtitle: 'Document transporteur',
          icon: Send,
          color: '#3b82f6',
          description: 'Documents officiels pour transporteurs'
        }
      ]
    },
    {
      title: 'IMMOBILIER & LOCATION',
      color: '#6366f1',
      documents: [
        {
          id: 'rental-contract',
          title: 'Créer un contrat de location',
          subtitle: 'Bail commercial',
          icon: FileText,
          color: '#6366f1',
          description: 'Contrats de location et baux commerciaux'
        },
        {
          id: 'property-inspection',
          title: 'Créer un état des lieux',
          subtitle: 'Inventaire locaux',
          icon: CheckCircle,
          color: '#059669',
          description: 'États des lieux d\'entrée et de sortie'
        },
        {
          id: 'rent-receipt',
          title: 'Créer une quittance de loyer',
          subtitle: 'Reçu de paiement',
          icon: FileCheck,
          color: '#10b981',
          description: 'Quittances de loyer et reçus de paiement'
        }
      ]
    },
    {
      title: 'SÉCURITÉ & ASSURANCE',
      color: '#dc2626',
      documents: [
        {
          id: 'accident-report',
          title: 'Créer une déclaration d\'accident',
          subtitle: 'Sinistres',
          icon: FileText,
          color: '#dc2626',
          description: 'Déclarations d\'accidents et sinistres'
        },
        {
          id: 'security-report',
          title: 'Créer un rapport de sécurité',
          subtitle: 'Inspection sécurité',
          icon: CheckCircle,
          color: '#f59e0b',
          description: 'Rapports d\'inspection sécurité'
        },
        {
          id: 'safety-compliance',
          title: 'Créer une fiche conformité sécurité',
          subtitle: 'Normes sécurité',
          icon: FileCheck,
          color: '#059669',
          description: 'Conformité aux normes de sécurité'
        }
      ]
    },
    {
      title: 'SERVICES & MAINTENANCE',
      color: '#0ea5e9',
      documents: [
        {
          id: 'maintenance-report',
          title: 'Créer un rapport de maintenance',
          subtitle: 'Entretien équipements',
          icon: FileText,
          color: '#0ea5e9',
          description: 'Rapports d\'entretien et maintenance préventive'
        },
        {
          id: 'maintenance-planning',
          title: 'Créer une planification maintenance',
          subtitle: 'Calendrier préventif',
          icon: Calendar,
          color: '#059669',
          description: 'Planification des maintenances préventives'
        },
        {
          id: 'client-intervention',
          title: 'Créer une fiche intervention client',
          subtitle: 'Dépannage sur site',
          icon: CheckCircle,
          color: '#3b82f6',
          description: 'Interventions et dépannages chez clients'
        },
        {
          id: 'product-warranty',
          title: 'Créer une garantie produit',
          subtitle: 'Certificat garantie',
          icon: FileCheck,
          color: '#10b981',
          description: 'Certificats de garantie produits/services'
        }
      ]
    },
    {
      title: 'INFORMATIQUE & TÉLÉCOMS',
      color: '#7c3aed',
      documents: [
        {
          id: 'it-audit',
          title: 'Créer un rapport audit IT',
          subtitle: 'Audit systèmes',
          icon: FileText,
          color: '#7c3aed',
          description: 'Audits des systèmes informatiques'
        },
        {
          id: 'software-installation',
          title: 'Créer une fiche installation logiciel',
          subtitle: 'Déploiement',
          icon: CheckCircle,
          color: '#059669',
          description: 'Installations et déploiements logiciels'
        },
        {
          id: 'it-maintenance-contract',
          title: 'Créer un contrat maintenance IT',
          subtitle: 'Support technique',
          icon: FileCheck,
          color: '#3b82f6',
          description: 'Contrats de maintenance informatique'
        },
        {
          id: 'data-backup',
          title: 'Créer une sauvegarde données',
          subtitle: 'Procédure backup',
          icon: FileText,
          color: '#f59e0b',
          description: 'Procédures de sauvegarde et restauration'
        }
      ]
    },
    {
      title: 'ACHATS & APPROVISIONNEMENT',
      color: '#ea580c',
      documents: [
        {
          id: 'purchase-request',
          title: 'Créer une demande d\'achat',
          subtitle: 'Demande interne',
          icon: FileText,
          color: '#ea580c',
          description: 'Demandes d\'achat internes'
        },
        {
          id: 'tender-call',
          title: 'Créer un appel d\'offres',
          subtitle: 'Consultation fournisseurs',
          icon: FileCheck,
          color: '#059669',
          description: 'Appels d\'offres et consultations'
        },
        {
          id: 'supplier-comparison',
          title: 'Créer un comparatif fournisseurs',
          subtitle: 'Analyse prix',
          icon: CheckCircle,
          color: '#3b82f6',
          description: 'Comparatifs et analyses fournisseurs'
        },
        {
          id: 'goods-reception',
          title: 'Créer une réception marchandises',
          subtitle: 'Contrôle livraison',
          icon: Truck,
          color: '#10b981',
          description: 'Contrôles et réceptions de marchandises'
        }
      ]
    },
    {
      title: 'FORMATION & DÉVELOPPEMENT',
      color: '#c026d3',
      documents: [
        {
          id: 'training-plan',
          title: 'Créer un plan de formation',
          subtitle: 'Programme formation',
          icon: FileText,
          color: '#c026d3',
          description: 'Plans et programmes de formation'
        },
        {
          id: 'training-evaluation',
          title: 'Créer une évaluation formation',
          subtitle: 'Bilan session',
          icon: CheckCircle,
          color: '#059669',
          description: 'Évaluations et bilans de formation'
        },
        {
          id: 'training-certificate',
          title: 'Créer un certificat de formation',
          subtitle: 'Attestation',
          icon: FileCheck,
          color: '#3b82f6',
          description: 'Certificats et attestations de formation'
        }
      ]
    },
    {
      title: 'COMMERCE & VENTE',
      color: '#db2777',
      documents: [
        {
          id: 'prospect-sheet',
          title: 'Créer une fiche prospect',
          subtitle: 'Prospect commercial',
          icon: FileText,
          color: '#db2777',
          description: 'Fiches de prospects et leads commerciaux'
        },
        {
          id: 'client-visit-report',
          title: 'Créer un rapport visite client',
          subtitle: 'Compte-rendu visite',
          icon: CheckCircle,
          color: '#059669',
          description: 'Comptes-rendus de visites commerciales'
        },
        {
          id: 'price-catalog',
          title: 'Créer tarifs et catalogues',
          subtitle: 'Liste de prix',
          icon: FileCheck,
          color: '#f59e0b',
          description: 'Tarifs, catalogues et listes de prix'
        }
      ]
    },
    {
      title: 'ÉQUIPEMENTS & MATÉRIEL',
      color: '#65a30d',
      documents: [
        {
          id: 'equipment-sheet',
          title: 'Créer une fiche équipement',
          subtitle: 'Inventaire matériel',
          icon: FileText,
          color: '#65a30d',
          description: 'Fiches d\'inventaire des équipements'
        },
        {
          id: 'calibration',
          title: 'Créer un calibrage instruments',
          subtitle: 'Étalonnage',
          icon: CheckCircle,
          color: '#059669',
          description: 'Calibrages et étalonnages d\'instruments'
        },
        {
          id: 'equipment-loan',
          title: 'Créer un prêt de matériel',
          subtitle: 'Location temporaire',
          icon: FileCheck,
          color: '#3b82f6',
          description: 'Prêts et locations temporaires de matériel'
        }
      ]
    },
    {
      title: 'ANALYSE & REPORTING',
      color: '#0891b2',
      documents: [
        {
          id: 'dashboard',
          title: 'Créer un tableau de bord',
          subtitle: 'Indicateurs KPI',
          icon: FileText,
          color: '#0891b2',
          description: 'Tableaux de bord et indicateurs de performance'
        },
        {
          id: 'financial-report',
          title: 'Créer un rapport financier',
          subtitle: 'Analyse comptable',
          icon: FileCheck,
          color: '#059669',
          description: 'Rapports et analyses financières'
        },
        {
          id: 'market-study',
          title: 'Créer une étude de marché',
          subtitle: 'Analyse commerciale',
          icon: CheckCircle,
          color: '#3b82f6',
          description: 'Études de marché et analyses commerciales'
        },
        {
          id: 'benchmark',
          title: 'Créer un benchmark concurrentiel',
          subtitle: 'Étude comparative',
          icon: FileText,
          color: '#f59e0b',
          description: 'Analyses concurrentielles et benchmarks'
        }
      ]
    }
  ];

  const initializeFormData = (docType) => {
    const baseData = {
      client: '',
      date: new Date().toISOString().split('T')[0],
      items: [{ product: '', quantity: 1, price: 0, total: 0 }],
      notes: '',
      tvaRate: 19,
      guarantee: 0,
      stamp: 0
    };

    switch (docType) {
      case 'invoice':
        return {
          number: generateDocumentNumber('invoice'),
          date: new Date().toISOString().split('T')[0],
          client: '',
          clientAddress: '',
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          paymentTerms: 30,
          paymentMethod: 'virement',
          issuedBy: '',
          salesPerson: '',
          tvaRate: 19,
          showTva: true,
          showTap: false,
          showGuarantee: false,
          showStamp: false,
          tapRate: 0,
          issuerRC: '',
          clientNIF: '',
          clientNIS: '',
          clientAI: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          subtotalHT: 0,
          tva19: 0,
          tap2: 0,
          totalTTC: 0,
          amountInWords: '',
          guarantee: 0,
          stamp: 0,
          bankDetails: 'RIB: 123 456 789 012345678901 23',
          legalMentions: 'RC: 23/00-0123456 B23 - NIF: 123456789012345 - AI: 23123456789',
          notes: 'Facture conforme à la réglementation algérienne'
        };
      case 'proforma':
        return {
          number: generateDocumentNumber('proforma'),
          date: new Date().toISOString().split('T')[0],
          client: '',
          clientAddress: '',
          clientContact: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          subtotalHT: 0,
          tva19: 0,
          tap2: 0,
          totalTTC: 0,
          tvaRate: 19,
          guarantee: 0,
          stamp: 0,
          showTva: true,
          showTap: true,
          showGuarantee: false,
          showStamp: false,
          tapRate: 2,
          validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          validityDays: 30,
          plannedPaymentMethod: 'virement',
          downPaymentPercent: 30,
          deliveryTerms: 'franco',
          estimatedDelivery: '15 jours ouvrables',
          warranty: '12 mois pièces et main-d\'œuvre',
          proformaStatus: 'envoyee',
          includeTaxes: true,
          requiresApproval: false,
          deliveryTime: '15 jours ouvrables',
          paymentConditions: 'Paiement à la commande',
          conditions: 'Prix valables 30 jours - Livraison franco domicile',
          currency: 'DZD',
          exchangeRate: 1,
          signature: '',
          stampRequired: true,
          notes: 'Facture proforma - Devis détaillé avec prix fermes'
        };
      case 'quote-request':
        return {
          number: generateDocumentNumber('quote-request'),
          date: new Date().toISOString().split('T')[0],
          supplier: '',
          supplierAddress: '',
          supplierContact: '',
          requestedBy: '',
          department: '',
          priority: 'normale',
          responseDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          requestType: '',
          requestPriority: 'normale',
          approximateBudget: 0,
          deliveryLocation: '',
          desiredStartDate: '',
          preferredPaymentTerms: '30_jours',
          selectionCriteria: 'prix_qualite',
          supplierCount: 3,
          requiresOnSiteVisit: false,
          requiresCertifications: false,
          requiresWarranty: false,
          specifications: [{ description: '', quantity: 1, requirements: '', budget: 0 }],
          technicalRequirements: '',
          expectedDelivery: '',
          budgetRange: '',
          certifications: '',
          evaluationCriteria: 'prix, qualité, délai',
          tvaRate: 19,
          showTva: true,
          notes: 'Demande de devis détaillée avec cahier des charges'
        };
      case 'quote':
        return {
          number: generateDocumentNumber('quote'),
          date: new Date().toISOString().split('T')[0],
          quote: {
            type: 'produits',
            sector: 'industrie',
            validUntil: '',
            deliveryTime: '15 jours ouvrables',
            salesperson: '',
            source: 'appel_entrant',
            priority: 'normale',
            probability: '50%',
            paymentTerms: '30_jours',
            paymentMethod: 'virement',
            discount: 0,
            warranty: '12 mois pièces et main-d\'œuvre',
            status: 'en_cours',
            transportIncluded: false,
            installationIncluded: false,
            formationIncluded: false,
            description: '',
            advantages: ''
          },
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          tvaRate: 19,
          showTva: true,
          showGuarantee: false,
          showStamp: false,
          notes: 'Devis commercial avec analyse concurrentielle'
        };
      case 'purchase-order':
        return {
          number: generateDocumentNumber('purchase-order'),
          date: new Date().toISOString().split('T')[0],
          order: {
            type: 'produits',
            deliveryDate: '',
            deliveryLocation: '',
            supplier: '',
            supplierContact: '',
            supplierRef: '',
            requester: '',
            department: '',
            project: '',
            costCenter: '',
            budget: 0,
            paymentTerms: '30_jours',
            status: 'en_attente',
            approvedBy: '',
            transportIncluded: false,
            installationRequired: false,
            warrantyCovered: false,
            justification: '',
            specifications: ''
          },
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          tvaRate: 19,
          showTva: true,
          showGuarantee: false,
          showStamp: false,
          terms: '',
          approvedBy: '',
          deliveryAddress: '',
          notes: 'Bon de commande ferme et définitif'
        };
      case 'delivery-note':
        return {
          number: generateDocumentNumber('delivery-note'),
          date: new Date().toISOString().split('T')[0],
          sender: {
            name: '',
            address: ''
          },
          recipient: {
            name: '',
            address: ''
          },
          delivery: {
            orderReference: '',
            date: new Date().toISOString().split('T')[0],
            time: '',
            carrier: '',
            driver: '',
            vehicleRegistration: '',
            vehicleType: 'camion',
            conditions: 'franco',
            status: 'en_cours',
            receivedBy: '',
            signatureDate: '',
            fragile: false,
            refrigerated: false,
            hazardous: false,
            observations: ''
          },
          items: [{ reference: '', designation: '', quantityOrdered: 0, quantityDelivered: 0, unit: '', condition: 'bon' }],
          notes: 'Bon de livraison - Accusé de réception marchandises'
        };
      case 'shipping-slip':
        return {
          number: generateDocumentNumber('shipping-slip'),
          date: new Date().toISOString().split('T')[0],
          sender: {
            name: '',
            address: ''
          },
          recipient: {
            name: '',
            address: ''
          },
          shipment: {
            date: new Date().toISOString().split('T')[0],
            method: 'route',
            carrier: '',
            trackingNumber: '',
            packageType: 'carton',
            packageCount: 1,
            totalWeight: 0,
            volume: 0,
            declaredValue: 0,
            priority: 'normale',
            insurance: 'non',
            signatureRequired: false,
            fragile: false,
            returnable: false,
            instructions: ''
          },
          tvaRate: 19,
          showTva: true,
          showGuarantee: false,
          showStamp: false,
          items: [{ reference: '', designation: '', quantity: 0, unit: '', weight: 0, value: 0 }],
          notes: 'Bordereau d\'envoi avec suivi'
        };
      case 'reception-note':
        return {
          number: generateDocumentNumber('reception-note'),
          date: new Date().toISOString().split('T')[0],
          receiver: {
            name: '',
            address: ''
          },
          supplier: {
            name: ''
          },
          reception: {
            deliveryNoteRef: '',
            orderRef: '',
            date: new Date().toISOString().split('T')[0],
            time: '',
            receivedBy: '',
            controlledBy: '',
            overallCondition: 'bon',
            quantityCompliance: 'conforme',
            qualityCompliance: 'conforme',
            stockDestination: 'entrepôt_principal',
            status: 'acceptée',
            documentsComplete: false,
            packagingIntact: false,
            urgentProcessing: false,
            observations: ''
          },
          items: [{ reference: '', designation: '', quantityOrdered: 0, quantityReceived: 0, unit: '', condition: 'conforme', defects: '', location: '' }],
          notes: 'Bon de réception avec contrôle qualité'
        };
      case 'attachment':
        return {
          number: generateDocumentNumber('attachment'),
          date: new Date().toISOString().split('T')[0],
          attachment: {
            type: 'technique',
            domain: 'informatique',
            priority: 'normale',
            projectManager: '',
            client: '',
            budget: 0,
            startDate: '',
            endDate: '',
            duration: '',
            teamSize: 1,
            standards: '',
            confidentiality: 'normal',
            status: 'brouillon',
            version: '1.0',
            requiresApproval: false,
            technicalValidation: false,
            legalReview: false,
            objectives: '',
            technicalConstraints: '',
            deliverables: '',
            acceptanceCriteria: ''
          },
          items: [{ product: '', designation: '', quantity: 1, price: 0, total: 0 }],
          specifications: [{ section: '', requirement: '', details: '', priority: 'normale' }],
          tvaRate: 19,
          showTva: true,
          showGuarantee: false,
          showStamp: false,
          notes: 'Cahier des charges technique détaillé'
        };
      case 'credit-note':
        return {
          number: generateDocumentNumber('credit-note'),
          date: new Date().toISOString().split('T')[0],
          issuer: {
            name: '',
            nif: '',
            address: ''
          },
          client: {
            name: '',
            address: '',
            nif: ''
          },
          originalInvoice: {
            number: '',
            date: '',
            amount: 0
          },
          creditNote: {
            type: 'retour',
            amount: 0,
            vatRate: 19,
            vatAmount: 0,
            totalAmount: 0,
            refundMethod: 'virement',
            refundDelay: '15_jours',
            status: 'émis',
            affectsStock: false,
            taxDeductible: false,
            clientNotified: false,
            reason: '',
            refundConditions: ''
          },
          notes: 'Avoir émis selon réglementation fiscale algérienne'
        };
      case 'account-statement':
        return {
          number: generateDocumentNumber('account-statement'),
          date: new Date().toISOString().split('T')[0],
          company: {
            name: '',
            nif: '',
            address: ''
          },
          client: {
            name: '',
            code: '',
            nif: '',
            creditLimit: 0,
            paymentTerms: '30_jours',
            accountStatus: 'actif',
            creditExceeded: false,
            paymentDelay: false,
            legalAction: false,
            observations: '',
            recommendedActions: ''
          },
          period: {
            startDate: '',
            endDate: ''
          },
          balances: {
            opening: 0,
            totalInvoiced: 0,
            totalPaid: 0,
            totalCredits: 0,
            closing: 0
          },
          aging: {
            overdue: 0,
            days30: 0,
            days60: 0,
            days90Plus: 0
          },
          notes: 'Relevé de compte détaillé avec analyse de risque'
        };
      case 'advance-invoice':
        return {
          number: generateDocumentNumber('advance-invoice'),
          date: new Date().toISOString().split('T')[0],
          seller: {
            name: '',
            nif: '',
            address: ''
          },
          buyer: {
            name: '',
            address: '',
            nif: ''
          },
          advance: {
            contractReference: '',
            contractDate: '',
            totalContractAmount: 0,
            percentage: 30,
            amount: 0,
            vatRate: 19,
            vatAmount: 0,
            totalAmount: 0,
            type: 'signature',
            paymentTerm: 'immédiat',
            dueDate: '',
            paymentMethod: 'virement',
            status: 'facturé',
            expectedDeliveryDate: '',
            deductibleFromFinal: true,
            refundableIfCancelled: false,
            guaranteeRequired: false,
            purpose: '',
            refundConditions: ''
          },
          notes: 'Facture d\'acompte avec planning de facturation'
        };
      case 'return-slip':
        return {
          number: `BRT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          client: {
            name: '',
            address: ''
          },
          supplier: {
            name: ''
          },
          return: {
            originalDeliveryRef: '',
            originalInvoiceRef: '',
            date: new Date().toISOString().split('T')[0],
            reason: 'défectueux',
            type: 'échange',
            carrier: '',
            condition: 'emballage_original',
            inspector: '',
            cost: 0,
            transportCost: 0,
            status: 'en_cours',
            qualityCheck: false,
            restockable: false,
            creditNote: false,
            problemDescription: ''
          },
          items: [{ reference: '', designation: '', quantityReturned: 0, unit: '', condition: 'défectueux', reason: '' }],
          notes: 'Retour de marchandises suite à défaut constaté'
        };
      case 'stock-sheet':
        return {
          number: `FS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          warehouse: {
            name: '',
            location: '',
            manager: ''
          },
          product: {
            reference: '',
            name: '',
            category: 'marchandise',
            batchNumber: '',
            expirationDate: ''
          },
          movement: {
            type: 'entrée',
            quantity: 0,
            unit: 'unité',
            unitPrice: 0,
            totalValue: 0,
            documentRef: '',
            operator: '',
            approvedBy: '',
            observations: ''
          },
          stock: {
            previous: 0,
            new: 0
          },
          notes: 'Mouvement de stock - suivi inventaire'
        };
      case 'material-exit':
        return {
          number: `BSM-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          requester: {
            name: '',
            department: '',
            position: ''
          },
          exit: {
            project: '',
            date: new Date().toISOString().split('T')[0],
            purpose: 'utilisation',
            warehouse: '',
            warehouseKeeper: '',
            approvedBy: '',
            destination: '',
            workOrder: '',
            costCenter: '',
            expectedReturnDate: '',
            status: 'en_cours',
            returnExpected: false,
            conditionCheck: false,
            insuranceRequired: false,
            justification: ''
          },
          items: [{ reference: '', designation: '', quantity: 0, unit: '', condition: 'bon', purpose: '' }],
          notes: 'Sortie de matériel pour utilisation interne'
        };




      case 'reception-report':
        return {
          number: `PVR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          projectDetails: {
            name: '',
            reference: '',
            contractNumber: '',
            contractDate: '',
            contractAmount: 0,
            client: '',
            contractor: '',
            architect: '',
            controlOffice: ''
          },
          workLocation: {
            address: '',
            wilaya: '',
            commune: '',
            coordinates: '',
            accessConditions: ''
          },
          workDescription: {
            category: 'construction',
            nature: '',
            specifications: '',
            technicalStandards: 'DTR algériens',
            specialRequirements: ''
          },
          timeSchedule: {
            plannedStartDate: '',
            actualStartDate: '',
            plannedCompletionDate: '',
            actualCompletionDate: '',
            plannedDuration: '',
            actualDuration: '',
            delays: '',
            delayJustification: ''
          },
          workItems: [{ description: '', unit: '', plannedQuantity: 0, executedQuantity: 0, conformity: 'conforme', observations: '' }],
          qualityControl: {
            materialTests: [{ material: '', standard: '', result: 'conforme', testDate: '', laboratory: '' }],
            dimensionalChecks: [{ element: '', tolerance: '', measured: '', conformity: 'conforme' }],
            finishQuality: 'satisfaisante',
            overallAssessment: 'conforme'
          },
          receptionCommittee: {
            clientRepresentative: '',
            contractorRepresentative: '',
            architectRepresentative: '',
            controlOfficeRepresentative: '',
            otherParticipants: ''
          },
          findings: {
            workAccepted: true,
            reservations: [{ item: '', description: '', severity: 'mineure', deadline: '' }],
            defectsFound: [{ location: '', defect: '', cause: '', remedy: '', responsible: '' }],
            correctiveActions: [{ action: '', responsible: '', deadline: '', cost: 0 }],
            qualityGrade: 'satisfaisant'
          },
          warranties: {
            warrantyPeriod: '12 mois',
            warrantyStart: '',
            coverageDetails: '',
            maintenanceTerms: '',
            exclusions: ''
          },
          financialAspects: {
            finalPaymentDue: 0,
            retentionAmount: 0,
            retentionPercentage: 5,
            retentionReleaseDate: '',
            penaltiesApplied: 0,
            bonusesAwarded: 0
          },
          receptionDate: new Date().toISOString().split('T')[0],
          provisionalReception: false,
          definitivereception: false,
          nextInspectionDate: '',
          attachments: 'Plans conformes à l\'exécution, tests, photos',
          notes: 'Procès-verbal de réception conformément aux DTR algériens'
        };
      case 'intervention-report':
        return {
          number: `RI-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          interventionDetails: {
            type: 'maintenance',
            category: 'corrective',
            priority: 'normale',
            urgency: 'programmée',
            origin: 'demande_client',
            relatedTicket: '',
            contractReference: ''
          },
          client: {
            name: '',
            site: '',
            address: '',
            contact: '',
            phone: '',
            email: ''
          },
          requestInfo: {
            requestedBy: '',
            requestDate: '',
            requestTime: '',
            problemDescription: '',
            initialDiagnosis: '',
            expectedDuration: ''
          },
          team: {
            leadTechnician: '',
            assistantTechnicians: '',
            supervisor: '',
            externalExperts: '',
            totalTeamSize: 1
          },
          schedule: {
            plannedStartDate: '',
            plannedStartTime: '',
            actualStartDate: '',
            actualStartTime: '',
            actualEndDate: '',
            actualEndTime: '',
            totalDuration: '',
            travelTime: '',
            workTime: ''
          },
          equipment: {
            equipmentId: '',
            equipmentType: '',
            manufacturer: '',
            model: '',
            serialNumber: '',
            location: '',
            operatingHours: 0,
            lastMaintenanceDate: ''
          },
          diagnostic: {
            problemAnalysis: '',
            rootCause: '',
            failureMode: '',
            affectedComponents: '',
            severity: 'moyenne',
            impact: 'local'
          },
          workPerformed: [{ task: '', duration: '', technician: '', toolsUsed: '', result: 'réalisé' }],
          partsAndMaterials: [{ reference: '', designation: '', quantity: 0, unitPrice: 0, supplier: '', warranty: '' }],
          testAndValidation: {
            functionalTests: [{ test: '', procedure: '', result: 'conforme', observations: '' }],
            performanceMetrics: [{ metric: '', expected: '', measured: '', conformity: 'conforme' }],
            safetyChecks: [{ check: '', result: 'ok', certifiedBy: '' }],
            overallValidation: 'satisfaisant'
          },
          followUp: {
            followUpRequired: false,
            nextMaintenanceDate: '',
            recommendedActions: [{ action: '', priority: 'normale', deadline: '', responsible: '' }],
            sparePartsToOrder: '',
            trainingNeeds: ''
          },
          clientFeedback: {
            satisfaction: 'satisfait',
            comments: '',
            suggestions: '',
            additionalNeeds: ''
          },
          documentation: {
            photosBeforeAfter: false,
            technicalDrawings: false,
            testReports: false,
            warrantyDocuments: false,
            userManualUpdated: false
          },
          signatures: {
            clientSignature: '',
            clientDate: '',
            technicianSignature: '',
            technicianDate: '',
            supervisorApproval: '',
            supervisorDate: ''
          },
          financials: {
            laborCost: 0,
            materialsCost: 0,
            travelCost: 0,
            totalCost: 0,
            billingStatus: 'à_facturer'
          },
          notes: 'Rapport d\'intervention technique avec suivi qualité'
        };
      case 'non-compliance':
        return {
          number: `FNC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          identification: {
            detectedBy: '',
            detectorRole: '',
            detectionDate: new Date().toISOString().split('T')[0],
            detectionMethod: 'inspection',
            detectionContext: '',
            witnessNames: ''
          },
          projectInfo: {
            client: '',
            projectReference: '',
            contractNumber: '',
            phase: '',
            location: '',
            responsibleEngineer: ''
          },
          nonComplianceDetails: {
            type: 'qualité',
            category: 'matériaux',
            severity: 'moyenne',
            title: '',
            description: '',
            specificLocation: '',
            affectedQuantity: '',
            photosAvailable: false
          },
          standards: {
            standardViolated: '',
            requirementClause: '',
            specificationReference: '',
            toleranceExceeded: '',
            deviationMeasurement: ''
          },
          impact: {
            safetyRisk: 'faible',
            qualityImpact: 'mineur',
            scheduleImpact: 'aucun',
            costImpact: 0,
            clientImpact: 'négligeable',
            potentialConsequences: ''
          },
          analysis: {
            rootCause: '',
            contributingFactors: [{ factor: '', influence: 'faible', description: '' }],
            systematicIssue: false,
            similarOccurrences: '',
            investigationReport: ''
          },
          actions: {
            immediateActions: [{ action: '', responsible: '', completedDate: '', effectiveness: '' }],
            correctiveActions: [{ action: '', responsible: '', targetDate: '', cost: 0, status: 'planifiée' }],
            preventiveActions: [{ action: '', scope: '', responsible: '', implementation: '' }],
            verificationMethod: ''
          },
          responsibilities: {
            actionOwner: '',
            qualityManager: '',
            projectManager: '',
            externalExperts: '',
            approvalRequired: false
          },
          timeline: {
            targetCompletionDate: '',
            actualCompletionDate: '',
            verificationDate: '',
            followUpDates: [{ date: '', milestone: '', responsible: '' }],
            escalationDate: ''
          },
          monitoring: {
            status: 'ouverte',
            progressComments: '',
            lastUpdateDate: '',
            updatedBy: '',
            nextReviewDate: ''
          },
          closure: {
            closureDate: '',
            verifiedBy: '',
            verificationEvidence: '',
            effectivenessConfirmed: false,
            lessonsLearned: '',
            systemUpdates: ''
          },
          documentation: {
            attachments: '',
            referencedDocuments: '',
            distributionList: '',
            retentionPeriod: '5 ans'
          },
          notes: 'Fiche de non-conformité avec suivi systématique QHSE'
        };
      case 'tax-certificate':
        return {
          number: `AF-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          applicant: {
            name: '',
            legalForm: 'SARL',
            nif: '',
            nis: '',
            ai: '',
            commercialRegister: '',
            registrationDate: '',
            capital: 0,
            address: '',
            activity: '',
            activityCode: ''
          },
          certificateDetails: {
            type: 'fiscal',
            purpose: 'marchés_publics',
            requestReason: '',
            urgencyLevel: 'normale',
            deliveryMethod: 'retrait'
          },
          taxPeriod: {
            taxYear: new Date().getFullYear(),
            startDate: '',
            endDate: '',
            declarationPeriod: 'annuelle',
            lastDeclarationDate: ''
          },
          taxCompliance: {
            declarationStatus: 'à_jour',
            paymentStatus: 'à_jour',
            lastDeclarationNumber: '',
            declarationDate: '',
            totalTaxesDue: 0,
            totalTaxesPaid: 0,
            outstandingAmount: 0,
            penaltiesOwed: 0
          },
          businessData: {
            turnover: 0,
            taxableIncome: 0,
            employeeCount: 0,
            activitySector: '',
            exportPercentage: 0,
            investmentZone: ''
          },
          taxDetails: {
            irg: { due: 0, paid: 0, balance: 0 },
            ibs: { due: 0, paid: 0, balance: 0 },
            tva: { due: 0, paid: 0, balance: 0 },
            tap: { due: 0, paid: 0, balance: 0 },
            vf: { due: 0, paid: 0, balance: 0 },
            tof: { due: 0, paid: 0, balance: 0 }
          },
          verification: {
            lastInspectionDate: '',
            inspectionResult: 'conforme',
            adjustments: 0,
            litigationPending: false,
            appealInProgress: false
          },
          issuance: {
            issuingAuthority: 'Direction des Impôts',
            issuingOffice: '',
            authorizedBy: '',
            position: '',
            issueDate: new Date().toISOString().split('T')[0],
            validityPeriod: '12 mois',
            expiryDate: '',
            serialNumber: ''
          },
          legalFramework: {
            legalBasis: 'Code des impôts directs et taxes assimilées',
            applicableTexts: [{ text: 'Article 150', reference: 'CID' }],
            conditions: '',
            obligations: ''
          },
          restrictions: {
            geographicalScope: 'territoire national',
            activityLimitations: '',
            amountLimitations: '',
            specialConditions: ''
          },
          authentication: {
            officialSeal: true,
            digitalSignature: false,
            qrCode: '',
            verificationCode: '',
            securityFeatures: 'Filigrane + cachet officiel'
          },
          notes: 'Attestation fiscale conforme à la réglementation algérienne en vigueur'
        };
      case 'work-certificate':
        return {
          number: `CTE-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          contractor: {
            name: '',
            legalForm: 'SARL',
            nif: '',
            nis: '',
            address: '',
            phone: '',
            email: '',
            legalRepresentative: '',
            technicalDirector: ''
          },
          client: {
            name: '',
            address: '',
            contactPerson: '',
            phone: '',
            email: '',
            projectManager: ''
          },
          contractDetails: {
            reference: '',
            signatureDate: '',
            startDate: '',
            plannedCompletionDate: '',
            actualCompletionDate: '',
            contractAmount: 0,
            finalAmount: 0,
            amendments: [{ number: '', date: '', description: '', impact: 0 }]
          },
          workDescription: {
            category: 'construction',
            type: 'bâtiment',
            nature: '',
            specifications: '',
            location: '',
            totalSurface: 0,
            volumeBuilt: 0,
            numberOfLevels: 0
          },
          compliance: {
            buildingPermit: '',
            urbanismCertificate: '',
            conformityStandards: 'DTR algériens',
            environmentalCompliance: true,
            safetyStandards: 'réglementaires',
            accessibilityCompliance: true
          },
          qualityAssessment: {
            overallLevel: 'conforme',
            materialQuality: 'satisfaisante',
            workmanshipQuality: 'bonne',
            finishingQuality: 'satisfaisante',
            technicalPerformance: 'conforme',
            durabilityExpected: 'normale'
          },
          inspections: [{ date: '', inspector: '', scope: '', result: 'conforme', observations: '' }],
          tests: [{ test: '', standard: '', result: 'conforme', date: '', laboratory: '' }],
          warranties: {
            structuralWarranty: '10 ans',
            equipmentWarranty: '2 ans',
            finishingWarranty: '1 an',
            warrantyStart: '',
            maintenanceObligations: ''
          },
          performance: {
            timelyCompletion: true,
            budgetRespected: true,
            qualityTargetsMet: true,
            clientSatisfaction: 'satisfait',
            additionalBenefits: ''
          },
          references: {
            similarProjects: [{ name: '', year: '', client: '', amount: 0, description: '' }],
            totalExperience: '',
            specializations: '',
            certifications: ''
          },
          issuance: {
            issuingAuthority: 'Ordre des Architectes/Ingénieurs',
            authorizedBy: '',
            position: '',
            registrationNumber: '',
            issueDate: new Date().toISOString().split('T')[0],
            validityPeriod: 'permanent',
            usageScope: 'marchés publics et privés'
          },
          notes: 'Certificat de bonne exécution des travaux conforme aux standards'
        };
      case 'legal-notice':
        return {
          number: `NL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          parties: {
            sender: {
              name: '',
              legalForm: 'SARL',
              address: '',
              nif: '',
              legalRepresentative: '',
              lawyer: ''
            },
            recipient: {
              name: '',
              address: '',
              legalForm: '',
              identification: '',
              lastKnownAddress: ''
            }
          },
          noticeDetails: {
            type: 'contractuelle',
            category: 'mise_en_demeure',
            urgency: 'normale',
            subject: '',
            reference: '',
            relatedContract: ''
          },
          legalFramework: {
            legalBasis: '',
            applicableArticles: [{ article: '', law: '', content: '' }],
            jurisdiction: 'Tribunaux algériens',
            applicableLaw: 'Droit algérien',
            precedents: ''
          },
          factualBasis: {
            chronology: [{ date: '', event: '', evidence: '' }],
            obligations: [{ obligation: '', source: '', status: 'non_respectée' }],
            breaches: [{ breach: '', severity: '', consequences: '' }],
            damages: { type: '', amount: 0, calculation: '' }
          },
          demands: {
            primaryDemands: [{ demand: '', legal_basis: '', deadline: '' }],
            alternativeDemands: [{ demand: '', conditions: '' }],
            penalties: [{ penalty: '', amount: 0, calculation_method: '' }],
            interests: { rate: 0, calculation_start: '', legal_rate: true }
          },
          procedure: {
            formalNotice: true,
            deadline: '',
            consequences: '',
            gracePeriod: 0,
            escalationSteps: [{ step: '', timeline: '', action: '' }]
          },
          attachments: {
            contracts: [],
            correspondence: [],
            evidence: [],
            expertReports: [],
            legalTexts: []
          },
          delivery: {
            method: 'recommandé',
            recipientSignature: false,
            witnessRequired: false,
            bailiffService: false,
            electronicDelivery: false,
            deliveryDate: '',
            deliveryProof: ''
          },
          followUp: {
            responseRequired: true,
            responseDeadline: '',
            nextActions: '',
            legalProceedingsDate: '',
            alternativeResolution: 'médiation'
          },
          authentication: {
            notaryRequired: false,
            legalRepresentation: '',
            officialStamp: true,
            registrationRequired: false,
            archivingPeriod: '10 ans'
          },
          notes: 'Notification légale formelle avec valeur juridique'
        };
      case 'old-certificate':
        return {
          number: `CTE-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          supervisionBy: '',
          receivedBy: '',
          warrantyProvided: true,
          warrantyPeriod: '12 mois',
          certificationBody: '',
          technicalApproval: '',
          qualityControl: 'effectué',
          safetyCompliance: true,
          environmentalCompliance: true,
          finalInspection: true,
          clientSatisfaction: 'satisfait',
          notes: 'Certificat de travail effectué - conformité garantie'
        };
      case 'payslip':
        return {
          number: `FP-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          employer: {
            name: '',
            address: '',
            nif: '',
            nis: '',
            cnas: '',
            activity: '',
            conventionCollective: ''
          },
          employee: {
            name: '',
            firstName: '',
            employeeId: '',
            nas: '',
            position: '',
            department: '',
            classification: '',
            hireDate: '',
            seniority: ''
          },
          payPeriod: {
            type: 'mensuel',
            startDate: '',
            endDate: '',
            paymentDate: '',
            workingDays: 22,
            actualDaysWorked: 22,
            absenceDays: 0,
            sickLeaveDays: 0,
            vacationDays: 0
          },
          earnings: {
            baseSalary: 0,
            seniorityAllowance: 0,
            transportAllowance: 0,
            foodAllowance: 0,
            familyAllowance: 0,
            overtimeHours: 0,
            overtimeRate: 0,
            overtimePay: 0,
            nightWorkHours: 0,
            nightWorkPay: 0,
            bonus: 0,
            commission: 0,
            totalEarnings: 0
          },
          deductions: {
            socialSecurity: { rate: 9, amount: 0 },
            unemployment: { rate: 1.5, amount: 0 },
            retirement: { rate: 7, amount: 0 },
            incomeTax: { rate: 0, amount: 0, exemption: 0 },
            healthInsurance: { rate: 1.5, amount: 0 },
            advances: 0,
            loans: 0,
            other: 0,
            totalDeductions: 0
          },
          employerContributions: {
            socialSecurity: { rate: 26, amount: 0 },
            accidents: { rate: 1.25, amount: 0 },
            unemployment: { rate: 0.5, amount: 0 },
            formation: { rate: 1.5, amount: 0 },
            totalEmployerContributions: 0
          },
          netCalculation: {
            grossSalary: 0,
            taxableIncome: 0,
            netBeforeTax: 0,
            netSalary: 0,
            takeHome: 0
          },
          paymentDetails: {
            bankName: '',
            accountNumber: '',
            paymentMethod: 'virement',
            paymentDate: '',
            checkNumber: ''
          },
          legalMentions: {
            cnas: 'Affiliation obligatoire CNAS',
            retirement: 'Cotisation CNR obligatoire',
            taxes: 'Retenu selon barème IRG',
            socialLaws: 'Conforme Code du travail algérien'
          },
          notes: 'Bulletin de salaire conforme à la législation sociale algérienne'
        };
      case 'work-contract':
        return {
          number: `CT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          employer: {
            name: '',
            legalForm: 'SARL',
            address: '',
            nif: '',
            nis: '',
            activity: '',
            legalRepresentative: '',
            position: ''
          },
          employee: {
            name: '',
            firstName: '',
            birthDate: '',
            birthPlace: '',
            nationality: 'algérienne',
            address: '',
            phone: '',
            email: '',
            maritalStatus: 'célibataire',
            children: 0,
            education: '',
            experience: ''
          },
          contractDetails: {
            type: 'CDI',
            category: 'cadre',
            duration: '',
            renewalConditions: '',
            transformation: false,
            previousContract: '',
            collectiveAgreement: ''
          },
          position: {
            title: '',
            department: '',
            hierarchicalSuperior: '',
            teamSize: 0,
            classification: '',
            coefficient: 0,
            workLocation: ''
          },
          duration: {
            startDate: '',
            endDate: '',
            automaticRenewal: false,
            noticePeriod: '1 mois',
            trialPeriod: '3 mois',
            renewalTrialPeriod: '1 mois'
          },
          remuneration: {
            baseSalary: 0,
            thirteenthMonth: true,
            performanceBonus: false,
            transportAllowance: 0,
            mealAllowance: 0,
            housing: false,
            car: false,
            phone: false,
            otherBenefits: ''
          },
          workingTime: {
            weeklyHours: 40,
            schedule: '08h00-17h00',
            overtime: 'compensated',
            nightWork: false,
            shiftWork: false,
            flexibleSchedule: false,
            teleworking: false
          },
          leavePolitics: {
            annualLeave: 30,
            sickLeave: 'statutory',
            maternityLeave: '14 weeks',
            paternityLeave: '3 days',
            training: '5 days/year',
            exceptional: 'case by case'
          },
          responsibilities: {
            mainDuties: [{ duty: '', description: '', priority: 'élevée' }],
            objectives: [{ objective: '', deadline: '', measurement: '' }],
            autonomyLevel: 'élevée',
            decisionMaking: 'limitée',
            confidentiality: 'required'
          },
          obligations: {
            loyalty: true,
            confidentiality: true,
            nonCompete: false,
            nonCompeteDuration: '',
            nonSolicitation: false,
            intellectualProperty: true,
            exclusivity: true
          },
          termination: {
            terminationCauses: [{ cause: 'démission', notice: '1 mois' }],
            severancePay: 'statutory',
            nonCompeteCompensation: 0,
            returnOfProperty: 'required',
            finalSettlement: '30 days'
          },
          socialBenefits: {
            healthInsurance: true,
            retirement: 'CNAS',
            workAccidents: 'covered',
            training: 'annual plan',
            transportation: 'allowance',
            meals: 'restaurant tickets'
          },
          legalCompliance: {
            laborCode: 'Law 90-11',
            collectiveAgreement: '',
            internalRegulations: 'applicable',
            medicalExam: 'required',
            declaration: 'CNAS + CNR'
          },
          signatures: {
            employerDate: '',
            employeeDate: '',
            witnessName: '',
            witnessDate: '',
            registrationDate: ''
          },
          notes: 'Contrat de travail conforme au Code du travail algérien'
        };
      case 'employment-certificate':
        return {
          number: `AT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          employer: {
            name: '',
            legalForm: 'SARL',
            address: '',
            nif: '',
            nis: '',
            activity: '',
            legalRepresentative: '',
            hrManager: ''
          },
          employee: {
            name: '',
            firstName: '',
            employeeId: '',
            nas: '',
            birthDate: '',
            nationality: 'algérienne',
            position: '',
            department: '',
            classification: ''
          },
          employment: {
            contractType: 'CDI',
            startDate: '',
            endDate: '',
            currentStatus: 'en_service',
            terminationReason: '',
            notice: '',
            workLocation: ''
          },
          remuneration: {
            lastSalary: 0,
            averageSalary: 0,
            currency: 'DZD',
            salaryProgression: [{ period: '', amount: 0 }],
            benefits: '',
            thirteenthMonth: true
          },
          performance: {
            overallRating: 'satisfaisante',
            punctuality: 'excellente',
            reliability: 'bonne',
            teamwork: 'satisfaisante',
            initiative: 'bonne',
            professionalDevelopment: '',
            promotions: [{ date: '', from: '', to: '', reason: '' }]
          },
          responsibilities: {
            mainDuties: [{ duty: '', period: '', level: 'confirmé' }],
            achievements: [{ achievement: '', impact: '', year: '' }],
            projects: [{ project: '', role: '', duration: '', result: '' }],
            supervision: 0,
            autonomyLevel: 'élevée'
          },
          skills: {
            technical: [{ skill: '', level: 'confirmé', certified: false }],
            languages: [{ language: '', level: 'courant', certification: '' }],
            software: [{ software: '', level: 'avancé' }],
            training: [{ training: '', date: '', organization: '', duration: '' }]
          },
          conduct: {
            disciplinaryActions: 0,
            warnings: 0,
            absences: 'normales',
            punctuality: 'exemplaire',
            integrity: 'irréprochable',
            confidentiality: 'respectée'
          },
          certification: {
            purpose: 'administrative',
            requestedBy: 'employee',
            specificUse: '',
            confidentialityLevel: 'public',
            validityPeriod: '6 mois',
            replacementPolicy: 'unique'
          },
          legalCompliance: {
            dataProtection: 'conforme RGPD',
            employmentLaw: 'Code du travail algérien',
            references: 'autorisées',
            futureEmployment: 'recommandé'
          },
          issuance: {
            issuedBy: '',
            position: '',
            issueDate: new Date().toISOString().split('T')[0],
            signature: true,
            officialStamp: true,
            registrationNumber: ''
          },
          notes: 'Attestation de travail certifiée conforme et authentique'
        };
      case 'leave-request':
        return {
          number: `DCO-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          employee: {
            name: '',
            firstName: '',
            employeeId: '',
            position: '',
            department: '',
            supervisor: '',
            hireDate: '',
            seniority: ''
          },
          leaveDetails: {
            type: 'congé_annuel',
            category: 'repos',
            startDate: '',
            endDate: '',
            totalDays: 0,
            workingDaysAffected: 0,
            partialDays: false,
            halfDays: 0
          },
          entitlement: {
            annualEntitlement: 30,
            usedDays: 0,
            remainingDays: 30,
            carriedForward: 0,
            anticipatedDays: 0,
            compensationDays: 0
          },
          justification: {
            reason: '',
            urgency: 'normale',
            medicalCertificate: false,
            supportingDocuments: [],
            familyCircumstances: '',
            personalReasons: ''
          },
          workCoverage: {
            temporary: '',
            handoverCompleted: false,
            handoverDate: '',
            criticalTasks: [{ task: '', delegate: '', instructions: '' }],
            clientNotifications: false,
            projectImpact: 'aucun'
          },
          emergencyContacts: {
            primaryContact: '',
            primaryPhone: '',
            alternativeContact: '',
            alternativePhone: '',
            availability: 'limitée'
          },
          approval: {
            requestDate: new Date().toISOString().split('T')[0],
            supervisorApproval: false,
            supervisorDate: '',
            supervisorComments: '',
            hrApproval: false,
            hrDate: '',
            hrComments: '',
            finalApproval: false
          },
          administration: {
            payrollImpact: 'normal',
            benefitsImpact: 'maintained',
            returnDate: '',
            medicalClearance: false,
            attendanceUpdate: false,
            calendarBlocked: false
          },
          conditions: {
            modificationAllowed: true,
            cancellationPossible: true,
            partialReturn: false,
            extensionConditions: '',
            earlyReturnProcedure: ''
          },
          notes: 'Demande de congé conforme au Code du travail algérien'
        };
      case 'production-sheet':
        return {
          number: `FPR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          productInfo: {
            name: '',
            code: '',
            revision: '',
            category: 'produit_fini',
            specification: '',
            customerCode: '',
            internalReference: ''
          },
          productionOrder: {
            number: '',
            issueDate: '',
            requestedBy: '',
            priority: 'normale',
            dueDate: '',
            targetQuantity: 0,
            actualQuantity: 0,
            remainingQuantity: 0
          },
          batchInfo: {
            batchNumber: '',
            lotNumber: '',
            batchSize: 0,
            expiryDate: '',
            storageConditions: '',
            traceabilityCode: ''
          },
          schedule: {
            plannedStartDate: '',
            plannedStartTime: '',
            actualStartDate: '',
            actualStartTime: '',
            plannedEndDate: '',
            plannedEndTime: '',
            actualEndDate: '',
            actualEndTime: '',
            totalDuration: '',
            breakTime: '',
            effectiveTime: ''
          },
          team: {
            operator: '',
            supervisor: '',
            qualityController: '',
            teamSize: 1,
            shiftType: 'jour',
            experience: 'confirmé'
          },
          equipment: {
            primaryMachine: '',
            machineNumber: '',
            auxiliaryEquipment: [{ equipment: '', number: '', condition: 'ok' }],
            toolsUsed: [{ tool: '', number: '', calibrationDate: '' }],
            maintenanceStatus: 'ok'
          },
          materials: {
            rawMaterials: [{ material: '', reference: '', batchNumber: '', quantityUsed: 0, supplier: '' }],
            consumables: [{ item: '', quantity: 0, unitCost: 0 }],
            packaging: [{ type: '', quantity: 0, specifications: '' }],
            totalMaterialCost: 0
          },
          processSteps: [{ step: '', startTime: '', endTime: '', operator: '', parameters: '', result: 'ok' }],
          qualityControls: {
            inProcessChecks: [{ checkpoint: '', standard: '', result: '', time: '', inspector: '' }],
            finalInspection: { conducted: true, result: 'conforme', inspector: '', signoff: '' },
            tests: [{ test: '', method: '', result: '', conformity: 'ok' }],
            defects: [{ defect: '', quantity: 0, severity: 'mineure', action: '' }]
          },
          performance: {
            efficiency: 100,
            productivity: 0,
            scrapRate: 0,
            reworkRate: 0,
            conformityRate: 100,
            oeeRate: 100
          },
          environmental: {
            energyConsumption: 0,
            wasteGenerated: 0,
            waterUsage: 0,
            emissions: 0,
            recycling: 0
          },
          costs: {
            laborCost: 0,
            materialCost: 0,
            overheadCost: 0,
            totalCost: 0,
            unitCost: 0
          },
          signatures: {
            operatorSignature: '',
            supervisorSignature: '',
            qualitySignature: '',
            completionDate: ''
          },
          notes: 'Fiche de production avec traçabilité complète et indicateurs performance'
        };
      case 'quality-control':
        return {
          number: `CQ-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          productDetails: {
            name: '',
            code: '',
            batchNumber: '',
            lotNumber: '',
            productionDate: '',
            expiryDate: '',
            customerOrder: '',
            specification: ''
          },
          inspectionInfo: {
            type: 'réception',
            stage: 'final',
            sampling: 'statistique',
            sampleSize: 0,
            lotSize: 0,
            inspector: '',
            inspectionDate: '',
            shift: 'jour'
          },
          standards: {
            primaryStandard: 'ISO 9001',
            technicalStandard: '',
            customerSpecs: '',
            internalSpecs: '',
            regulatoryRequirements: '',
            revisionLevel: ''
          },
          testProgram: [{ 
            test: '', 
            method: '', 
            equipment: '', 
            standard: '', 
            acceptanceCriteria: '', 
            frequency: '',
            responsible: ''
          }],
          measurements: {
            dimensionalChecks: [{ dimension: '', specification: '', tolerance: '', measured: '', conformity: 'ok' }],
            physicalTests: [{ property: '', method: '', result: '', unit: '', limit: '', conformity: 'ok' }],
            chemicalTests: [{ parameter: '', method: '', result: '', specification: '', conformity: 'ok' }],
            functionalTests: [{ function: '', procedure: '', result: '', requirement: '', conformity: 'ok' }]
          },
          visualInspection: {
            surfaceFinish: 'acceptable',
            colorConformity: 'conforme',
            packagingIntegrity: 'satisfaisante',
            labeling: 'conforme',
            overallAppearance: 'acceptable',
            defectsObserved: [{ defect: '', location: '', severity: 'mineure', quantity: 0 }]
          },
          results: {
            overallResult: 'conforme',
            conformingUnits: 0,
            nonConformingUnits: 0,
            conformityRate: 100,
            criticalDefects: 0,
            majorDefects: 0,
            minorDefects: 0
          },
          nonConformities: [{ 
            description: '', 
            classification: 'majeure', 
            rootCause: '', 
            immediateAction: '',
            preventiveAction: '',
            responsible: '',
            deadline: ''
          }],
          decisions: {
            lotAccepted: true,
            conditionalAcceptance: false,
            rejected: false,
            reworkRequired: false,
            segregationRequired: false,
            customerNotification: false
          },
          followUp: {
            correctiveActions: [{ action: '', responsible: '', deadline: '', status: 'planifiée' }],
            preventiveActions: [{ action: '', scope: '', responsible: '', implementation: '' }],
            nextInspection: '',
            monitoringRequired: false,
            reviewDate: ''
          },
          equipment: {
            measuringDevices: [{ device: '', calibrationDate: '', nextCalibration: '', accuracy: '' }],
            environmentalConditions: { temperature: '', humidity: '', cleanliness: 'conforme' },
            testLocation: '',
            laboratoryAccreditation: ''
          },
          documentation: {
            photographs: false,
            testReports: false,
            certificates: false,
            customerReports: false,
            archiving: '5 ans'
          },
          signatures: {
            inspectorSignature: '',
            qualityManagerSignature: '',
            approvalDate: '',
            nextReviewDate: ''
          },
          notes: 'Contrôle qualité conforme aux standards internationaux avec traçabilité'
        };
      case 'manufacturing-order':
        return {
          number: `BF-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          orderInfo: {
            issueDate: new Date().toISOString().split('T')[0],
            issuedBy: '',
            department: 'production',
            priority: 'normale',
            urgency: 'standard',
            customerOrder: '',
            internalOrder: ''
          },
          productSpecification: {
            name: '',
            code: '',
            revision: '',
            drawing: '',
            specification: '',
            quality: 'standard',
            packaging: 'standard',
            labeling: 'conforme'
          },
          quantity: {
            ordered: 0,
            plannedProduction: 0,
            safetyStock: 0,
            minimumLot: 0,
            economicLot: 0,
            yield: 95
          },
          scheduling: {
            requestedDate: '',
            plannedStartDate: '',
            plannedEndDate: '',
            estimatedDuration: '',
            shift: 'jour',
            workCenter: '',
            sequence: 1
          },
          resources: {
            primaryMachine: '',
            backupMachine: '',
            tooling: [{ tool: '', quantity: 1, location: '', condition: 'ok' }],
            fixtures: [{ fixture: '', setup: '', calibration: '' }],
            manpower: { skilled: 1, semiskilled: 0, unskilled: 0 }
          },
          materials: {
            rawMaterials: [{ material: '', specification: '', quantity: 0, unit: '', supplier: '', stock: 0 }],
            subAssemblies: [{ component: '', quantity: 0, source: 'internal', leadTime: '' }],
            consumables: [{ item: '', consumption: 0, unit: '', stock: 0 }],
            packaging: [{ type: '', quantity: 0, specifications: '' }]
          },
          processRouting: [{ 
            operation: '', 
            workCenter: '', 
            machine: '', 
            setupTime: 0, 
            cycleTime: 0, 
            operator: '',
            tools: '',
            instructions: '',
            qualityChecks: ''
          }],
          qualityRequirements: {
            inspectionPlan: '',
            checkpoints: [{ checkpoint: '', standard: '', frequency: '', method: '' }],
            testRequirements: [{ test: '', specification: '', equipment: '' }],
            documentation: [{ document: '', required: true, location: '' }],
            certifications: ''
          },
          safety: {
            hazards: [{ hazard: '', risk: 'faible', mitigation: '' }],
            ppe: [{ equipment: '', mandatory: true, standard: '' }],
            procedures: [{ procedure: '', critical: true, training: '' }],
            emergencyContacts: ''
          },
          costing: {
            materialCost: 0,
            laborCost: 0,
            overheadCost: 0,
            setupCost: 0,
            totalCost: 0,
            unitCost: 0,
            marginTarget: 25
          },
          approval: {
            productionManager: false,
            qualityManager: false,
            materialPlanner: false,
            finalApproval: false,
            approvalDate: '',
            conditions: ''
          },
          tracking: {
            status: 'planifié',
            progress: 0,
            startedDate: '',
            completedDate: '',
            actualQuantity: 0,
            scrap: 0,
            rework: 0
          },
          notes: 'Bon de fabrication avec routage complet et suivi production'
        };
      case 'failure-report':
        return {
          number: `RD-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          equipment: {
            id: '',
            name: '',
            type: '',
            manufacturer: '',
            model: '',
            serialNumber: '',
            location: '',
            installationDate: '',
            operatingHours: 0,
            lastMaintenance: ''
          },
          failure: {
            type: 'panne',
            category: 'électrique',
            severity: 'moyenne',
            impact: 'local',
            detectionDate: new Date().toISOString().split('T')[0],
            detectionTime: '',
            detectedBy: '',
            reportedBy: '',
            reportDate: new Date().toISOString().split('T')[0]
          },
          conditions: {
            operatingMode: 'automatique',
            workload: 'normale',
            environmentalConditions: 'standard',
            operatorPresent: true,
            maintenanceRecent: false,
            modifications: false
          },
          symptoms: {
            initialSymptoms: '',
            progressiveSymptoms: '',
            alarms: [{ alarm: '', time: '', severity: 'info' }],
            observations: [{ observer: '', time: '', description: '' }],
            measurements: [{ parameter: '', value: '', unit: '', normal: '' }]
          },
          investigation: {
            immediateCause: '',
            rootCause: '',
            contributingFactors: [{ factor: '', influence: 'faible', description: '' }],
            investigationMethod: '5 pourquoi',
            evidenceCollected: [{ evidence: '', type: 'photo', location: '' }],
            witnessStatements: [{ witness: '', statement: '' }]
          },
          analysis: {
            failureMode: '',
            mechanismOfFailure: '',
            systemsAffected: [{ system: '', impact: 'partiel', description: '' }],
            riskAssessment: { probability: 'faible', severity: 'moyenne', risk: 'acceptable' },
            precedentsFound: false,
            similarFailures: ''
          },
          impact: {
            productionLoss: 0,
            downtimeHours: 0,
            qualityImpact: 'aucun',
            safetyImpact: 'aucun',
            environmentalImpact: 'aucun',
            customerImpact: 'aucun',
            financialImpact: 0
          },
          actions: {
            immediateActions: [{ action: '', responsible: '', time: '', effectiveness: '' }],
            temporarySolution: { implemented: false, description: '', limitations: '' },
            correctiveActions: [{ action: '', responsible: '', deadline: '', cost: 0, status: 'planifiée' }],
            preventiveActions: [{ action: '', scope: '', implementation: '', monitoring: '' }]
          },
          resources: {
            personnelInvolved: [{ person: '', role: '', hours: 0, cost: 0 }],
            materialsUsed: [{ material: '', quantity: 0, cost: 0, supplier: '' }],
            externalServices: [{ service: '', provider: '', cost: 0, duration: '' }],
            totalCost: 0
          },
          resolution: {
            resolved: false,
            resolutionDate: '',
            resolutionTime: '',
            solutionDescription: '',
            effectiveness: 'non_évaluée',
            followUpRequired: true,
            nextInspection: ''
          },
          lessons: {
            lessonsLearned: '',
            processImprovements: [{ improvement: '', priority: 'moyenne', implementation: '' }],
            trainingNeeds: [{ training: '', audience: '', urgency: 'normale' }],
            systemUpdates: [{ update: '', system: '', priority: 'normale' }]
          },
          documentation: {
            photos: false,
            videos: false,
            technicalDrawings: false,
            testReports: false,
            distributionList: '',
            retentionPeriod: '10 ans'
          },
          signatures: {
            reporterSignature: '',
            supervisorSignature: '',
            maintenanceManagerSignature: '',
            approvalDate: '',
            nextReviewDate: ''
          },
          notes: 'Rapport de défaillance avec analyse RCA complète et plan d\'actions'
        };
      case 'route-sheet':
        return {
          number: `FR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          driver: {
            name: '',
            licenseNumber: '',
            licenseExpiry: '',
            phone: '',
            experience: 'confirmé',
            certifications: [{ certification: '', expiry: '', authority: '' }],
            emergencyContact: ''
          },
          vehicle: {
            registration: '',
            type: 'camion',
            brand: '',
            model: '',
            capacity: 0,
            fuelType: 'diesel',
            insuranceExpiry: '',
            lastMaintenance: '',
            technicalControl: ''
          },
          route: {
            name: '',
            code: '',
            territory: '',
            totalDistance: 0,
            estimatedDuration: '',
            difficultyLevel: 'normale',
            alternativeRoute: '',
            restrictions: ''
          },
          schedule: {
            departureDate: new Date().toISOString().split('T')[0],
            departureTime: '',
            plannedReturnTime: '',
            estimatedReturnTime: '',
            breakTimes: [{ start: '', end: '', location: '', type: 'repos' }],
            drivingTime: '',
            restTime: ''
          },
          deliveries: [{ 
            order: '', 
            client: '', 
            address: '', 
            contactPerson: '', 
            phone: '',
            deliveryWindow: '',
            estimatedTime: '',
            priority: 'normale',
            specialInstructions: '',
            packages: 0,
            weight: 0,
            value: 0,
            signature: false
          }],
          cargo: {
            totalWeight: 0,
            totalVolume: 0,
            totalValue: 0,
            fragileItems: 0,
            hazardousItems: 0,
            temperatureControlled: false,
            specialHandling: ''
          },
          preTrip: {
            vehicleInspection: { completed: false, issues: '', inspector: '' },
            fuelLevel: 100,
            mileageStart: 0,
            documentsCheck: { completed: false, missing: '' },
            equipmentCheck: { gps: true, phone: true, uniform: true, tools: true },
            weatherConditions: 'favorable'
          },
          safetyPlan: {
            hazards: [{ hazard: '', mitigation: '', equipment: '' }],
            emergencyContacts: [{ contact: '', phone: '', role: 'dispatch' }],
            firstAid: { available: true, location: '', trained: true },
            communication: { frequency: '2h', method: 'telephone', backup: 'radio' }
          },
          compliance: {
            drivingHours: 0,
            restPeriods: 0,
            tachographCheck: false,
            weighBridgeRequired: false,
            customsDocuments: false,
            specialPermits: ''
          },
          tracking: {
            gpsEnabled: true,
            trackingSystem: '',
            checkpoints: [{ location: '', estimatedTime: '', actualTime: '', status: 'prévu' }],
            updates: [{ time: '', location: '', status: '', comment: '' }]
          },
          costs: {
            fuelBudget: 0,
            tollsCost: 0,
            otherExpenses: 0,
            totalBudget: 0,
            advanceGiven: 0
          },
          notes: 'Feuille de route optimisée avec suivi GPS et conformité réglementaire'
        };
      case 'delivery-report':
        return {
          number: generateDocumentNumber('delivery-report'),
          date: new Date().toISOString().split('T')[0],
          routeInfo: {
            routeNumber: '',
            routeName: '',
            plannedDate: '',
            actualDate: '',
            driver: '',
            vehicle: '',
            territory: ''
          },
          timing: {
            departureTime: '',
            returnTime: '',
            totalTime: '',
            drivingTime: '',
            breakTime: '',
            deliveryTime: '',
            delays: ''
          },
          performance: {
            deliveriesPlanned: 0,
            deliveriesCompleted: 0,
            deliveriesFailed: 0,
            deliverySuccessRate: 100,
            onTimeDeliveries: 0,
            customerSignatures: 0
          },
          deliveryDetails: [{ 
            order: '', 
            client: '', 
            address: '',
            plannedTime: '',
            actualTime: '',
            status: 'livré',
            packages: 0,
            signature: '',
            notes: '',
            issues: ''
          }],
          failedDeliveries: [{ 
            order: '', 
            client: '', 
            reason: '', 
            action: '', 
            rescheduled: '',
            compensation: 0
          }],
          vehicle: {
            mileageStart: 0,
            mileageEnd: 0,
            totalKm: 0,
            fuelStart: 100,
            fuelEnd: 0,
            fuelConsumed: 0,
            fuelCost: 0,
            condition: 'bon'
          },
          incidents: [{ 
            time: '', 
            location: '', 
            type: 'retard', 
            description: '', 
            action: '',
            impact: 'mineur',
            cost: 0
          }],
          expenses: {
            fuel: 0,
            tolls: 0,
            parking: 0,
            meals: 0,
            other: 0,
            total: 0,
            receipts: false
          },
          customerFeedback: [{ 
            client: '', 
            rating: 5, 
            comment: '', 
            complaint: false,
            compliment: false
          }],
          safety: {
            accidentsReported: 0,
            trafficViolations: 0,
            safetyIncidents: 0,
            ppeDamage: false,
            vehicleDamage: false
          },
          cargo: {
            damageReported: false,
            damages: [{ item: '', damage: '', value: 0, cause: '', responsible: '' }],
            temperatureDeviations: 0,
            securityBreaches: 0,
            inventoryDiscrepancies: 0
          },
          documentation: {
            deliveryNotes: 'complete',
            proofOfDelivery: 'obtained',
            photos: false,
            customerForms: 'signed',
            returnItems: [{ item: '', reason: '', value: 0 }]
          },
          nextActions: {
            followUpRequired: false,
            customerCalls: [{ client: '', reason: '', priority: 'normale' }],
            vehicleMaintenance: false,
            routeOptimization: '',
            driverTraining: ''
          },
          signatures: {
            driverSignature: '',
            supervisorSignature: '',
            completionTime: '',
            approvalDate: ''
          },
          notes: 'Rapport de livraison détaillé avec KPI performance et suivi qualité'
        };
      case 'transport-voucher':
        return {
          number: `BT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          shipper: {
            name: '',
            address: '',
            phone: '',
            email: '',
            contactPerson: '',
            licenseNumber: '',
            insurancePolicy: ''
          },
          carrier: {
            company: '',
            address: '',
            phone: '',
            email: '',
            contactPerson: '',
            licenseNumber: '',
            insurancePolicy: '',
            vehicleRegistration: ''
          },
          consignee: {
            name: '',
            address: '',
            phone: '',
            email: '',
            contactPerson: '',
            deliveryInstructions: '',
            specialRequirements: ''
          },
          transport: {
            mode: 'routier',
            type: 'livraison',
            urgency: 'normale',
            serviceLevel: 'standard',
            pickupDate: '',
            pickupTime: '',
            deliveryDate: '',
            deliveryTime: '',
            estimatedTransitTime: ''
          },
          route: {
            origin: '',
            destination: '',
            distance: 0,
            intermediateStops: [{ location: '', purpose: '', estimatedTime: '' }],
            routeRestrictions: '',
            preferredRoute: ''
          },
          cargo: {
            description: '',
            commodity: '',
            packagingType: 'carton',
            packages: 0,
            weight: 0,
            volume: 0,
            dimensions: { length: 0, width: 0, height: 0 },
            value: 0,
            currency: 'DZD'
          },
          specialHandling: {
            fragile: false,
            hazardous: false,
            perishable: false,
            temperatureControlled: false,
            temperatureRange: '',
            specialInstructions: '',
            handlingEquipment: ''
          },
          documentation: {
            invoiceRequired: true,
            packingList: true,
            certificate: false,
            customsDeclaration: false,
            additionalDocs: [{ document: '', required: true, provided: false }]
          },
          pricing: {
            transportCost: 0,
            fuelSurcharge: 0,
            handlingFee: 0,
            insuranceFee: 0,
            additionalCharges: 0,
            totalCost: 0,
            paymentTerms: '30 jours',
            paymentMethod: 'virement'
          },
          insurance: {
            required: false,
            coverage: 0,
            deductible: 0,
            policyNumber: '',
            insurer: '',
            claims: 'carrier'
          },
          tracking: {
            trackingNumber: '',
            trackingSystem: '',
            milestones: [{ milestone: '', estimatedTime: '', actualTime: '', status: 'prévu' }],
            notifications: { sms: false, email: false, app: false }
          },
          conditions: {
            deliveryConfirmation: 'signature',
            undeliverableAction: 'return',
            storageCharges: 0,
            demurrageCharges: 0,
            liabilityLimit: 0,
            claimsDeadline: '7 jours'
          },
          compliance: {
            permits: [{ permit: '', number: '', expiry: '', authority: '' }],
            regulations: [{ regulation: '', compliance: 'ok', notes: '' }],
            safetyRequirements: '',
            environmentalCompliance: 'standard'
          },
          signatures: {
            shipperSignature: '',
            carrierSignature: '',
            contractDate: '',
            pickupSignature: '',
            deliverySignature: ''
          },
          notes: 'Bon de transport avec contrat logistique et suivi temps réel'
        };
      case 'rental-contract':
        return {
          number: `CL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          landlord: {
            name: '',
            legalForm: 'physique',
            address: '',
            phone: '',
            email: '',
            nif: '',
            bankAccount: '',
            legalRepresentative: ''
          },
          tenant: {
            name: '',
            legalForm: 'entreprise',
            address: '',
            phone: '',
            email: '',
            nif: '',
            nis: '',
            activity: '',
            legalRepresentative: '',
            guarantor: ''
          },
          property: {
            type: 'commercial',
            description: '',
            address: '',
            wilaya: '',
            commune: '',
            cadastralReference: '',
            titleDeed: '',
            surface: 0,
            floors: 1,
            parking: 0,
            annexes: ''
          },
          lease: {
            type: 'commercial',
            purpose: 'bureau',
            startDate: '',
            duration: '12 mois',
            renewalTerms: 'tacite',
            noticePeriod: '3 mois',
            endDate: '',
            handoverDate: ''
          },
          financial: {
            monthlyRent: 0,
            rentIncludes: [{ service: 'eau', included: true }],
            charges: 0,
            chargesDetails: '',
            deposit: 0,
            advancePayment: 0,
            indexation: 'fixed',
            indexationRate: 0,
            paymentDay: 1,
            paymentMethod: 'virement'
          },
          obligations: {
            landlordObligations: [{ obligation: 'maintenance structure', description: '' }],
            tenantObligations: [{ obligation: 'entretien courant', description: '' }],
            usageRestrictions: '',
            alterationsAllowed: false,
            sublettingAllowed: false,
            transferAllowed: false
          },
          charges: {
            utilities: { water: 'tenant', electricity: 'tenant', gas: 'tenant', internet: 'tenant' },
            maintenance: { interior: 'tenant', exterior: 'landlord', hvac: 'shared' },
            insurance: { building: 'landlord', contents: 'tenant', liability: 'tenant' },
            taxes: { property: 'landlord', business: 'tenant' }
          },
          conditions: {
            stateOfPremises: 'good',
            inventoryRequired: true,
            keyDeposit: 0,
            accessHours: '24/7',
            parkingSpaces: 0,
            storageSpace: false,
            securitySystem: false
          },
          insurance: {
            landlordInsurance: { required: true, coverage: 'building', amount: 0 },
            tenantInsurance: { required: true, coverage: 'liability', amount: 0 },
            proofRequired: true,
            beneficiaryClause: true
          },
          termination: {
            earlyTermination: [{ cause: 'breach', notice: '30 days', penalty: 0 }],
            renewalProcess: 'automatic',
            depositsReturn: '30 days',
            stateAssessment: 'required',
            restorationRequired: 'original state'
          },
          legal: {
            jurisdiction: 'Tribunaux algériens',
            applicableLaw: 'Code civil algérien',
            disputeResolution: 'mediation first',
            registrationRequired: true,
            notarizationRequired: false,
            witnesses: [{ name: '', address: '', signature: false }]
          },
          signatures: {
            landlordSignature: '',
            tenantSignature: '',
            witnessSignatures: '',
            contractDate: '',
            registrationDate: '',
            registrationNumber: ''
          },
          notes: 'Contrat de location commercial conforme au droit algérien'
        };
      case 'property-inspection':
        return {
          number: `EL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          inspection: {
            type: 'entrée',
            purpose: 'contractuel',
            date: new Date().toISOString().split('T')[0],
            time: '',
            duration: '',
            weather: 'normal',
            lighting: 'naturelle'
          },
          property: {
            address: '',
            type: 'commercial',
            surface: 0,
            floors: 1,
            rooms: 0,
            buildingYear: '',
            lastRenovation: '',
            heatingType: ''
          },
          parties: {
            landlord: { name: '', present: true, representative: '' },
            tenant: { name: '', present: true, representative: '' },
            inspector: { name: '', qualification: '', certification: '' },
            witnesses: [{ name: '', role: '', signature: false }]
          },
          inventory: {
            rooms: [{ 
              name: '', 
              surface: 0, 
              flooring: '', 
              walls: '', 
              ceiling: '', 
              windows: '', 
              doors: '', 
              fixtures: '', 
              condition: 'bon' 
            }],
            equipment: [{ 
              item: '', 
              brand: '', 
              model: '', 
              serialNumber: '', 
              condition: 'bon', 
              working: true,
              accessories: ''
            }],
            utilities: {
              electricity: { functional: true, meters: '', safety: 'ok' },
              plumbing: { functional: true, leaks: false, pressure: 'normal' },
              heating: { functional: true, maintenance: '', efficiency: 'good' },
              internet: { available: true, speed: '', provider: '' }
            }
          },
          condition: {
            overall: 'bon',
            structural: 'satisfaisant',
            cleanliness: 'acceptable',
            maintenance: 'recent',
            safety: 'conforme',
            accessibility: 'standard'
          },
          observations: [{ 
            location: '', 
            item: '', 
            condition: '', 
            defect: '', 
            severity: 'mineure', 
            cause: '',
            photos: false,
            action: ''
          }],
          damages: [{ 
            location: '', 
            description: '', 
            extent: 'localisé', 
            cause: 'usure', 
            responsibility: 'tenant',
            estimatedCost: 0,
            urgent: false
          }],
          improvements: [{ 
            location: '', 
            improvement: '', 
            cost: 0, 
            priority: 'normale', 
            timeline: '',
            approval: 'required'
          }],
          safety: {
            fireExtinguishers: { present: true, expiry: '', tested: true },
            smokeDetectors: { present: true, functional: true, batteries: 'ok' },
            emergencyExits: { accessible: true, signage: 'visible', lighting: 'functional' },
            firstAid: { available: false, location: '', updated: false }
          },
          meters: {
            electricity: { reading: 0, date: '', photos: false },
            water: { reading: 0, date: '', photos: false },
            gas: { reading: 0, date: '', photos: false }
          },
          keys: {
            delivered: [{ type: 'entrance', quantity: 2, copies: 0 }],
            retained: [{ type: 'master', quantity: 1, location: 'management' }],
            badges: [{ type: 'access', quantity: 1, code: '' }]
          },
          documentation: {
            photos: { taken: false, count: 0, storage: 'digital' },
            videos: { taken: false, duration: 0, purpose: '' },
            plans: { available: true, updated: true, scale: '' },
            certificates: [{ type: 'conformity', valid: true, expiry: '' }]
          },
          agreements: {
            disputesNoted: false,
            discrepancies: '',
            reservations: [{ party: '', reservation: '', accepted: false }],
            amendments: '',
            futureActions: [{ action: '', responsible: '', deadline: '' }]
          },
          signatures: {
            landlordSignature: '',
            tenantSignature: '',
            inspectorSignature: '',
            witnessSignatures: '',
            date: '',
            copies: 2
          },
          notes: 'État des lieux détaillé avec inventaire complet et documentation photographique'
        };
      case 'rent-receipt':
        return {
          number: `QL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          landlord: {
            name: '',
            address: '',
            phone: '',
            email: '',
            nif: '',
            bankAccount: '',
            rib: ''
          },
          tenant: {
            name: '',
            address: '',
            phone: '',
            email: '',
            nif: '',
            activity: '',
            leaseNumber: ''
          },
          property: {
            address: '',
            type: 'commercial',
            description: '',
            surface: 0,
            cadastralReference: '',
            contractReference: ''
          },
          period: {
            description: '',
            startDate: '',
            endDate: '',
                          month: '',
              year: '',
              daysInPeriod: 30,
            occupancyDays: 30
          },
          rentDetails: {
            baseRent: 0,
            prorataCaclculation: false,
            prorataReason: '',
            adjustments: [{ description: '', amount: 0, type: 'credit' }],
            totalRent: 0
          },
          charges: {
            water: 0,
            electricity: 0,
            gas: 0,
            maintenance: 0,
            cleaning: 0,
            security: 0,
            parking: 0,
            other: [{ description: '', amount: 0 }],
            totalCharges: 0
          },
          previousPeriod: {
            previousBalance: 0,
            lateFees: 0,
            interestCharges: 0,
            adjustments: 0,
            totalPrevious: 0
          },
          currentTotal: {
            rentAndCharges: 0,
            previousAmount: 0,
            grandTotal: 0,
            tvaApplicable: false,
            tvaAmount: 0,
            totalWithTva: 0
          },
          payment: {
            amountPaid: 0,
            paymentDate: '',
            paymentMethod: 'virement',
            bankReference: '',
            checkNumber: '',
            cashReceived: false,
            partialPayment: false
          },
          balance: {
            remainingBalance: 0,
            advance: 0,
            nextPaymentDue: '',
            paymentStatus: 'payé',
            latePayment: false,
            dayslate: 0
          },
          reconciliation: {
            deposits: 0,
            guarantees: 0,
            securityDeposit: 0,
            keyDeposit: 0,
            totalDeposits: 0
          },
          legal: {
            latePaymentPenalty: 2,
            gracePeriod: 5,
            legalInterestRate: 8,
            jurisdictionalCourt: 'Tribunal de première instance',
            disputeResolution: 'amiable first'
          },
          taxInfo: {
            taxableRent: 0,
            exemptAmount: 0,
            tvaRate: 0,
            withholdingTax: 0,
            taxReference: ''
          },
          acknowledgment: {
            receiptValid: true,
            disputePeriod: '30 days',
            evidenceValue: 'legal',
            originalCopy: true,
            duplicatesIssued: 0
          },
          issuance: {
            issuedBy: '',
            issuedDate: new Date().toISOString().split('T')[0],
            signature: true,
            officialStamp: true,
            registrationNumber: ''
          },
          notes: 'Quittance de loyer officielle avec valeur libératoire complète'
        };
      case 'accident-report':
        return {
          number: `DAC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          incident: {
            date: new Date().toISOString().split('T')[0],
            time: '',
            location: '',
            specificArea: '',
            workStation: '',
            weather: 'normal',
            lightingConditions: 'adequate',
            reportedBy: '',
            reportDate: new Date().toISOString().split('T')[0]
          },
          accidentType: {
            category: 'travail',
            nature: 'blessure',
            cause: 'chute',
            severity: 'mineur',
            bodyPart: '',
            mechanism: '',
            objectInvolved: ''
          },
          injured: {
            name: '',
            firstName: '',
            employeeId: '',
            position: '',
            department: '',
            age: 0,
            experience: '',
            training: 'complete',
            medicalFitness: 'apt'
          },
          workContext: {
            activity: '',
            taskPerformed: '',
            equipment: '',
            materials: '',
            procedures: '',
            supervision: 'present',
            workload: 'normal',
            timeInShift: ''
          },
          witnesses: [{ 
            name: '', 
            position: '', 
            contact: '', 
            statement: '',
            signature: false
          }],
          description: {
            circumstances: '',
            sequenceOfEvents: '',
            environmentalFactors: '',
            humanFactors: '',
            equipmentFactors: '',
            organizationalFactors: ''
          },
          injuries: {
            type: '',
            severity: 'minor',
            location: '',
            description: '',
            firstAidGiven: true,
            firstAidBy: '',
            hospitalRequired: false,
            hospitalName: ''
          },
          medicalResponse: {
            treatment: '',
            treatedBy: '',
            transportMethod: '',
            hospitalTreatment: false,
            returnToWork: true,
            workRestrictions: '',
            followUpRequired: false
          },
          workImpact: {
            workStoppage: false,
            stoppageDuration: '',
            productionImpact: 'none',
            workersAffected: 0,
            lostTime: 0,
            returnDate: ''
          },
          investigation: {
            investigator: '',
            investigationDate: '',
            rootCause: '',
            contributingFactors: [{ factor: '', category: 'human', influence: 'minor' }],
            evidenceCollected: [{ evidence: '', type: 'photo', location: '' }],
            conclusion: ''
          },
          prevention: {
            immediateActions: [{ action: '', responsible: '', completed: false }],
            correctiveActions: [{ action: '', responsible: '', deadline: '', cost: 0 }],
            preventiveActions: [{ action: '', implementation: '', monitoring: '' }],
            training: [{ training: '', audience: '', schedule: '' }]
          },
          compliance: {
            reportedToAuthorities: false,
            authorityName: '',
            reportNumber: '',
            regulatoryRequirements: '',
            penaltiesApplied: false,
            improvementNotices: false
          },
          insurance: {
            notified: false,
            insuranceCompany: '',
            claimNumber: '',
            adjusterName: '',
            estimatedCost: 0,
            coverage: 'full'
          },
          documentation: {
            photos: false,
            medicalReports: false,
            witnessStatements: false,
            expertReports: false,
            legalDocuments: false,
            archivePeriod: '30 years'
          },
          followUp: {
            returnToWorkDate: '',
            medicalClearance: false,
            workModifications: '',
            monitoringPlan: '',
            lessonsLearned: '',
            systemUpdates: ''
          },
          signatures: {
            injuredSignature: '',
            supervisorSignature: '',
            safetyManagerSignature: '',
            hrSignature: '',
            reportDate: '',
            approvalDate: ''
          },
          notes: 'Déclaration d\'accident conforme CNAS avec investigation complète'
        };
      case 'security-report':
        return {
          number: `RS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          inspection: {
            type: 'routine',
            purpose: 'preventive',
            frequency: 'monthly',
            date: new Date().toISOString().split('T')[0],
            startTime: '',
            endTime: '',
            duration: '',
            weather: 'normal'
          },
          inspector: {
            name: '',
            qualification: '',
            certification: '',
            experience: '',
            organization: 'internal',
            licenseNumber: ''
          },
          area: {
            name: '',
            type: 'production',
            location: '',
            surface: 0,
            occupancy: 0,
            accessLevel: 'restricted',
            hazardClass: 'low'
          },
          scope: {
            physicalSecurity: true,
            fireSafety: true,
            occupationalHealth: true,
            environmental: true,
            equipment: true,
            procedures: true,
            emergency: true
          },
          assessments: [{ 
            category: 'fire safety', 
            element: '', 
            requirement: '', 
            status: 'compliant', 
            risk: 'low',
            observations: '',
            evidence: ''
          }],
          hazards: [{ 
            hazard: '', 
            location: '', 
            severity: 'low',
            probability: 'unlikely',
            risk: 'acceptable',
            exposure: '',
            controls: '',
            effectiveness: 'adequate'
          }],
          compliance: {
            regulations: [{ regulation: '', standard: '', compliance: 'yes', notes: '' }],
            permits: [{ permit: '', status: 'valid', expiry: '', renewal: '' }],
            certifications: [{ certificate: '', valid: true, expiry: '', issuer: '' }],
            training: [{ training: '', personnel: '', current: true, expiry: '' }]
          },
          equipment: {
            fireExtinguishers: { count: 0, condition: 'good', lastService: '', nextService: '' },
            emergencyLights: { count: 0, functional: 0, tested: true, lastTest: '' },
            alarms: { fireAlarm: 'functional', gasAlarm: 'functional', securityAlarm: 'functional' },
            eyewash: { count: 0, functional: 0, weekly: true, lastInspection: '' },
            firstAid: { kits: 0, stocked: true, accessible: true, lastCheck: '' }
          },
          emergency: {
            exits: { count: 0, clear: 0, marked: 0, illuminated: 0 },
            assembly: { designated: true, marked: true, accessible: true, capacity: 0 },
            procedures: { posted: true, current: true, trained: true, drills: 'regular' },
            communication: { systems: 'functional', backup: 'available', tested: true }
          },
          findings: {
            overallRating: 'satisfactory',
            criticalIssues: 0,
            majorIssues: 0,
            minorIssues: 0,
            observations: 0,
            improvements: 0
          },
          deficiencies: [{ 
            deficiency: '', 
            category: 'major', 
            location: '', 
            requirement: '',
            risk: 'medium',
            impact: '',
            urgency: 'high'
          }],
          recommendations: [{ 
            recommendation: '', 
            category: 'preventive', 
            priority: 'high',
            timeframe: 'immediate',
            cost: 0,
            benefit: '',
            responsible: ''
          }],
          actions: {
            immediate: [{ action: '', responsible: '', deadline: '', status: 'planned' }],
            shortTerm: [{ action: '', responsible: '', deadline: '', budget: 0 }],
            longTerm: [{ action: '', planning: '', investment: 0, roi: '' }]
          },
          monitoring: {
            followUpRequired: true,
            followUpDate: '',
            nextInspection: '',
            continuousMonitoring: [{ parameter: '', frequency: '', responsible: '' }],
            indicators: [{ kpi: '', target: '', current: '', trend: 'stable' }]
          },
          approval: {
            inspectorSignature: '',
            safetyManagerSignature: '',
            facilityManagerSignature: '',
            approved: false,
            approvalDate: '',
            conditions: ''
          },
          distribution: {
            management: true,
            safetyCommittee: true,
            departments: true,
            authorities: false,
            insurance: false
          },
          notes: 'Rapport de sécurité avec évaluation risques et plan d\'actions prioritaires'
        };
      case 'safety-compliance':
        return {
          number: `FCS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          equipment: {
            id: '',
            name: '',
            type: 'machinery',
            manufacturer: '',
            model: '',
            serialNumber: '',
            location: '',
            installationDate: '',
            commissioningDate: ''
          },
          compliance: {
            primaryStandard: 'ISO 45001',
            applicableStandards: [{ standard: '', version: '', scope: '', mandatory: true }],
            regulatoryRequirements: [{ regulation: '', authority: '', reference: '', compliance: 'yes' }],
            internalPolicies: [{ policy: '', version: '', applicable: true, trained: true }],
            industryBestPractices: ''
          },
          inspection: {
            type: 'certification',
            date: new Date().toISOString().split('T')[0],
            inspector: '',
            organization: '',
            accreditation: '',
            scope: 'full',
            methodology: 'visual + testing'
          },
          safetyFeatures: {
            guards: [{ guard: '', type: 'fixed', condition: 'good', compliant: true }],
            emergencyStops: [{ location: '', functional: true, accessible: true, tested: true }],
            safetyDevices: [{ device: '', type: 'interlock', functional: true, tested: true }],
            warnings: [{ warning: '', type: 'visual', visible: true, legible: true }],
            ppe: [{ equipment: '', required: true, available: true, condition: 'good' }]
          },
          testing: {
            functionalTests: [{ test: '', procedure: '', result: 'pass', date: '', operator: '' }],
            safetyTests: [{ test: '', standard: '', result: 'compliant', equipment: '', calibration: '' }],
            environmentalTests: [{ parameter: '', limit: '', measured: '', compliant: true }],
            performanceTests: [{ metric: '', target: '', achieved: '', variance: 0 }]
          },
          riskAssessment: {
            hazards: [{ hazard: '', severity: 'low', probability: 'unlikely', risk: 'acceptable', mitigation: '' }],
            residualRisk: 'acceptable',
            riskMatrix: 'low',
            acceptanceCriteria: 'met',
            reviewDate: ''
          },
          documentation: {
            technicalFile: true,
            riskAssessment: true,
            operatingInstructions: true,
            maintenanceProcedures: true,
            trainingRecords: true,
            certificates: [{ certificate: '', issuer: '', valid: true, expiry: '' }]
          },
          deficiencies: [{ 
            deficiency: '', 
            standard: '', 
            severity: 'minor', 
            impact: '',
            correctiveAction: '',
            responsible: '',
            deadline: '',
            cost: 0
          }],
          verification: {
            compliant: true,
            conditionalCompliance: false,
            majorNonCompliances: 0,
            minorNonCompliances: 0,
            observations: 0,
            overallAssessment: 'satisfactory'
          },
          certification: {
            certified: false,
            certificateNumber: '',
            issuingBody: '',
            issueDate: '',
            validUntil: '',
            scope: '',
            conditions: '',
            restrictions: ''
          },
          followUp: {
            nextInspection: '',
            surveillanceRequired: false,
            surveillanceFrequency: 'annual',
            monitoringPlan: [{ parameter: '', frequency: '', responsible: '', method: '' }],
            improvements: [{ improvement: '', benefit: '', cost: 0, timeline: '' }]
          },
          training: {
            operatorTraining: { required: true, completed: true, expiry: '', renewal: '' },
            maintenanceTraining: { required: true, completed: true, specialized: true },
            safetyTraining: { required: true, frequency: 'annual', lastDate: '', nextDate: '' },
            competencyAssessment: { conducted: true, passed: true, assessor: '', date: '' }
          },
          approval: {
            technicalApproval: true,
            safetyApproval: true,
            operationalApproval: false,
            approvedBy: '',
            approvalDate: '',
            restrictions: '',
            conditions: ''
          },
          notes: 'Fiche de conformité sécurité avec certification ISO 45001 et validation équipement'
        };

      case 'maintenance-report':
        return {
          number: `RMT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          workOrder: {
            number: '',
            type: 'préventive',
            priority: 'normale',
            requestDate: '',
            requestedBy: '',
            approvedBy: '',
            scheduledDate: '',
            emergencyWork: false
          },
          equipment: {
            id: '',
            name: '',
            type: 'machinery',
            manufacturer: '',
            model: '',
            serialNumber: '',
            location: '',
            operatingHours: 0,
            lastMaintenance: ''
          },
          maintenance: {
            type: 'préventive',
            category: 'mechanical',
            procedure: '',
            checklistUsed: '',
            standardReference: '',
            toolsRequired: [{ tool: '', type: 'hand tool', available: true }],
            safetyPrecautions: [{ precaution: '', implemented: true, verified: true }]
          },
          team: {
            leadTechnician: '',
            assistants: [{ name: '', specialty: '', hours: 0 }],
            contractor: '',
            supervision: '',
            competency: 'qualified',
            authorization: 'valid'
          },
          schedule: {
            plannedStart: '',
            actualStart: '',
            plannedEnd: '',
            actualEnd: '',
            totalHours: 0,
            delays: [{ reason: '', duration: 0, impact: 'minor' }],
            overtime: false
          },
          workPerformed: {
            tasksCompleted: [{ task: '', status: 'completed', duration: 0, notes: '' }],
            procedures: [{ procedure: '', followed: true, deviations: '', reason: '' }],
            inspections: [{ inspection: '', result: 'satisfactory', observations: '' }],
            measurements: [{ parameter: '', value: '', unit: '', status: 'normal' }]
          },
          materials: {
            partsReplaced: [{ part: '', partNumber: '', quantity: 1, cost: 0, supplier: '', warranty: '' }],
            fluids: [{ fluid: '', quantity: 0, unit: 'liters', grade: '', specification: '' }],
            consumables: [{ item: '', quantity: 0, cost: 0, purpose: '' }],
            totalCost: 0
          },
          testing: {
            functionalTests: [{ test: '', procedure: '', result: 'pass', parameters: '', operator: '' }],
            safetyTests: [{ test: '', standard: '', result: 'compliant', equipment: '' }],
            performanceTests: [{ metric: '', measured: '', target: '', variance: 0 }],
            calibration: [{ instrument: '', calibrated: true, nextCalibration: '', certificate: '' }]
          },
          condition: {
            beforeMaintenance: 'needs attention',
            afterMaintenance: 'excellent',
            componentsInspected: [{ component: '', condition: 'good', action: 'none', nextCheck: '' }],
            wearItems: [{ item: '', condition: '', estimatedLife: '', replacement: '' }],
            defectsFound: [{ defect: '', severity: 'minor', action: 'monitored', corrected: true }]
          },
          quality: {
            workQuality: 'excellent',
            specificationsFollowed: true,
            tolerancesAchieved: true,
            reworkRequired: false,
            qualityChecks: [{ check: '', result: 'acceptable', inspector: '' }],
            customerAcceptance: true
          },
          safety: {
            lockoutTagout: true,
            permitRequired: false,
            incidentsOccurred: false,
            nearMisses: [{ incident: '', action: '', prevention: '' }],
            ppeUsed: [{ equipment: '', condition: 'good', compliant: true }],
            environmentalImpact: 'none'
          },
          followUp: {
            nextMaintenance: '',
            frequency: 'monthly',
            recommendations: [{ recommendation: '', priority: 'normal', cost: 0, benefit: '' }],
            improvements: [{ improvement: '', implementation: '', timeline: '' }],
            sparePartsNeeded: [{ part: '', quantity: 1, urgency: 'normal', order: false }],
            trainingNeeds: [{ training: '', personnel: '', priority: 'normal' }]
          },
          costs: {
            labor: 0,
            materials: 0,
            contractor: 0,
            travel: 0,
            equipment: 0,
            total: 0,
            budgetCode: '',
            approved: true
          },
          reporting: {
            downtime: 0,
            availabilityImpact: 0,
            productionLoss: 0,
            efficiency: 100,
            reliability: 'high',
            mtbf: 0,
            kpis: [{ metric: '', value: '', target: '', status: 'on target' }]
          },
          documentation: {
            photosBefore: false,
            photosAfter: false,
            drawings: false,
            certificates: false,
            manuals: false,
            reports: false,
            retention: '10 years'
          },
          approval: {
            workCompleted: true,
            qualityApproved: true,
            supervisorSignature: '',
            clientSignature: '',
            approvalDate: '',
            remarks: ''
          },
          notes: 'Rapport de maintenance détaillé avec contrôles qualité et suivi performance'
        };
      case 'maintenance-planning':
        return {
          number: `PMT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          equipment: {
            id: '',
            name: '',
            type: 'machinery',
            manufacturer: '',
            model: '',
            location: '',
            criticality: 'high',
            operatingHours: 0,
            installationDate: ''
          },
          strategy: {
            maintenanceType: 'préventive',
            frequency: 'mensuelle',
            basis: 'time based',
            riskAssessment: 'medium',
            businessImpact: 'high',
            regulatory: false,
            manufacturer: true
          },
          schedule: {
            planningPeriod: '12 months',
            baseDate: new Date().toISOString().split('T')[0],
            lastMaintenance: '',
            nextMaintenance: '',
            frequency: 30,
            flexibilityWindow: 7,
            seasonalAdjustment: false
          },
          tasks: {
            routine: [{ task: '', frequency: 'monthly', duration: 0, skill: 'technician', tools: '' }],
            periodic: [{ task: '', frequency: 'quarterly', duration: 0, skill: 'specialist', cost: 0 }],
            seasonal: [{ task: '', season: 'spring', duration: 0, special: true, external: false }],
            conditional: [{ task: '', condition: '', trigger: '', urgency: 'normal' }]
          },
          resources: {
            primaryTechnician: '',
            backupTechnician: '',
            skillsRequired: [{ skill: '', level: 'intermediate', certified: true }],
            contractors: [{ contractor: '', specialty: '', availability: 'on call' }],
            teamSize: 1,
            supervision: 'self supervised'
          },
          materials: {
            spareParts: [{ part: '', partNumber: '', quantity: 1, cost: 0, leadTime: 30, critical: false }],
            consumables: [{ item: '', quantity: 0, unit: '', cost: 0, supplier: '' }],
            specialTools: [{ tool: '', required: false, available: true, calibration: '' }],
            totalBudget: 0
          },
          procedures: {
            workInstructions: [{ procedure: '', reference: '', version: '', safety: true }],
            standards: [{ standard: '', applicability: 'mandatory', compliance: true }],
            checklists: [{ checklist: '', items: 0, completion: 'required' }],
            documentation: [{ document: '', type: 'manual', current: true }]
          },
          compliance: {
            regulatory: [{ regulation: '', applicable: true, frequency: '', inspector: '' }],
            warranty: [{ warranty: '', requirement: '', impact: 'maintain', compliance: true }],
            insurance: [{ requirement: '', frequency: '', certification: '', cost: 0 }],
            safety: [{ requirement: '', standard: '', training: 'required' }]
          },
          optimization: {
            costReduction: [{ opportunity: '', saving: 0, implementation: '', effort: 'low' }],
            efficiency: [{ improvement: '', benefit: '', metric: '', target: 0 }],
            reliability: [{ enhancement: '', impact: 'positive', investment: 0 }],
            sustainability: [{ initiative: '', environmental: true, saving: '' }]
          },
          performance: {
            kpis: [{ metric: 'MTBF', target: 1000, current: 0, trend: 'improving' }],
            availability: { target: 95, current: 0, downtime: 0 },
            costs: { budgeted: 0, actual: 0, variance: 0, trend: 'stable' },
            quality: { defectRate: 0, reworkRate: 0, customerSatisfaction: 95 }
          },
          risks: {
            operational: [{ risk: '', probability: 'low', impact: 'medium', mitigation: '' }],
            safety: [{ hazard: '', severity: 'low', controls: '', monitoring: '' }],
            financial: [{ risk: '', cost: 0, probability: 'unlikely', contingency: '' }],
            environmental: [{ impact: '', severity: 'minor', prevention: '', monitoring: '' }]
          },
          approval: {
            planApproved: false,
            approvedBy: '',
            approvalDate: '',
            budgetApproved: false,
            reviewDate: '',
            nextReview: ''
          },
          tracking: {
            completionRate: 0,
            delayedTasks: 0,
            budgetUtilization: 0,
            effectivenessScore: 0,
            lastUpdate: '',
            nextUpdate: ''
          },
          notes: 'Plan de maintenance préventive optimisé avec suivi performance et coûts'
        };
      case 'client-intervention':
        return {
          number: `FIC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          request: {
            requestDate: new Date().toISOString().split('T')[0],
            requestTime: '',
            requestedBy: '',
            requestMethod: 'phone',
            priority: 'normale',
            urgency: 'standard',
            sla: '24h',
            contractType: 'maintenance'
          },
          client: {
            name: '',
            address: '',
            contact: '',
            phone: '',
            email: '',
            contractNumber: '',
            serviceLevel: 'standard',
            specialInstructions: ''
          },
          intervention: {
            type: 'dépannage',
            category: 'corrective',
            equipmentType: '',
            location: '',
            accessRequirements: '',
            safetyRequirements: '',
            permitRequired: false
          },
          scheduling: {
            scheduledDate: '',
            scheduledTime: '',
            estimatedDuration: '',
            technician: '',
            backupTechnician: '',
            travelTime: '',
            arrivalWindow: '2h'
          },
          problem: {
            description: '',
            symptoms: '',
            reportedBy: '',
            occurrence: 'first time',
            impact: 'minor',
            businessCriticality: 'low',
            environmentalConditions: ''
          },
          diagnosis: {
            initialAssessment: '',
            rootCause: '',
            investigation: [{ step: '', finding: '', conclusion: '' }],
            testing: [{ test: '', result: '', equipment: '' }],
            confirmation: '',
            additionalIssues: [{ issue: '', severity: 'minor', action: '' }]
          },
          solution: {
            actionTaken: '',
            method: 'repair',
            difficulty: 'standard',
            standards: [{ standard: '', followed: true, deviation: '' }],
            qualityCheck: true,
            testing: [{ test: '', result: 'pass', notes: '' }]
          },
          materials: {
            partsUsed: [{ part: '', partNumber: '', quantity: 1, cost: 0, warranty: '', source: 'stock' }],
            consumables: [{ item: '', quantity: 0, cost: 0, purpose: '' }],
            specialTools: [{ tool: '', purpose: '', owned: true, cost: 0 }],
            totalCost: 0
          },
          time: {
            arrivalTime: '',
            startTime: '',
            endTime: '',
            workDuration: '',
            travelTime: '',
            waitTime: '',
            totalTime: '',
            overtime: false
          },
          results: {
            problemResolved: true,
            partialSolution: false,
            temporaryFix: false,
            followUpRequired: false,
            equipmentStatus: 'operational',
            performanceRestored: true,
            clientNotified: true
          },
          quality: {
            workQuality: 'excellent',
            clientSatisfaction: 'très satisfait',
            issuesReported: false,
            reworkNeeded: false,
            qualityScore: 10,
            feedback: ''
          },
          followUp: {
            required: false,
            type: 'monitoring',
            scheduledDate: '',
            responsible: '',
            recommendations: [{ recommendation: '', priority: 'normal', cost: 0 }],
            preventiveMeasures: [{ measure: '', implementation: '', timeline: '' }]
          },
          documentation: {
            photos: false,
            reports: false,
            manuals: false,
            warranties: false,
            certificates: false,
            clientCopy: true
          },
          billing: {
            billable: true,
            contractCovered: false,
            laborCost: 0,
            materialCost: 0,
            travelCost: 0,
            totalAmount: 0,
            invoiceGenerated: false,
            paymentTerms: '30 days'
          },
          approval: {
            workCompleted: true,
            clientApproval: true,
            clientSignature: '',
            technicianSignature: '',
            approvalDate: '',
            remarks: ''
          },
          notes: 'Fiche intervention client avec diagnostic complet et satisfaction garantie'
        };
      case 'product-warranty':
        return {
          number: `GP-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          product: {
            name: '',
            model: '',
            brand: '',
            category: '',
            serialNumber: '',
            lotNumber: '',
            manufactureDate: '',
            specifications: ''
          },
          purchase: {
            client: '',
            purchaseDate: '',
            invoiceNumber: '',
            dealer: '',
            purchasePrice: 0,
            currency: 'DZD',
            proofOfPurchase: true
          },
          warranty: {
            type: 'manufacturer',
            period: '12 mois',
            startDate: '',
            expiryDate: '',
            transferable: true,
            international: false,
            extendable: false
          },
          coverage: {
            type: 'complète',
            defects: true,
            manufacturing: true,
            materials: true,
            workmanship: true,
            functional: true,
            parts: true,
            labor: true
          },
          conditions: {
            normalUse: true,
            properInstallation: true,
            authorizedService: true,
            originalParts: true,
            maintenance: 'required',
            documentation: 'complete'
          },
          exclusions: {
            misuse: true,
            accident: true,
            negligence: true,
            unauthorizedRepair: true,
            modification: true,
            wearItems: [{ item: '', period: '' }]
          },
          service: {
            serviceCenter: '',
            contactInfo: '',
            supportHours: '8h-17h',
            emergencyContact: '',
            onSiteService: false,
            pickupService: false
          },
          process: {
            claimProcedure: '',
            documentationRequired: [{ document: '', mandatory: true }],
            inspectionRequired: false,
            approvalTime: '5-10 days',
            repairTime: '7-15 days'
          },
          terms: {
            limitations: '',
            liability: 'product value',
            consequentialDamages: false,
            jurisdiction: 'Algeria',
            governingLaw: 'Algerian Consumer Protection Law'
          },
          registration: {
            registrationRequired: false,
            registrationDeadline: '',
            benefits: [{ benefit: '', description: '' }],
            contact: ''
          },
          notes: 'Garantie produit avec couverture complète et service après-vente'
        };
      case 'it-audit':
        return {
          number: `RAI-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          audit: {
            type: 'security',
            scope: 'infrastructure',
            objective: 'compliance verification',
            standard: 'ISO 27001',
            frequency: 'annual',
            duration: '5 days',
            methodology: 'risk-based'
          },
          auditor: {
            name: '',
            organization: 'internal',
            certification: 'CISSP',
            experience: '',
            team: [{ member: '', role: '', expertise: '' }],
            independence: 'confirmed'
          },
          systems: {
            network: { audited: true, scope: 'full', criticality: 'high', findings: 0 },
            servers: { audited: true, scope: 'critical', criticality: 'high', findings: 0 },
            databases: { audited: true, scope: 'sensitive', criticality: 'high', findings: 0 },
            applications: { audited: true, scope: 'business-critical', criticality: 'medium', findings: 0 },
            endpoints: { audited: true, scope: 'sample', criticality: 'medium', findings: 0 },
            cloud: { audited: false, scope: '', criticality: 'low', findings: 0 }
          },
          security: {
            accessControl: { status: 'compliant', findings: 0, risk: 'low' },
            authentication: { status: 'compliant', findings: 0, risk: 'low' },
            encryption: { status: 'non-compliant', findings: 2, risk: 'medium' },
            backup: { status: 'compliant', findings: 0, risk: 'low' },
            monitoring: { status: 'partially compliant', findings: 1, risk: 'medium' },
            incidentResponse: { status: 'compliant', findings: 0, risk: 'low' }
          },
          vulnerabilities: [{ 
            vulnerability: '', 
            severity: 'medium', 
            cvss: 6.5, 
            system: '', 
            impact: 'data exposure',
            exploitability: 'medium',
            remediation: '',
            timeline: '30 days'
          }],
          compliance: {
            framework: 'ISO 27001',
            requirements: [{ requirement: '', status: 'compliant', evidence: '', notes: '' }],
            gaps: [{ gap: '', severity: 'minor', action: '', deadline: '' }],
            overallStatus: 'substantially compliant',
            score: 85
          },
          risks: {
            identified: [{ risk: '', probability: 'medium', impact: 'high', level: 'medium', treatment: 'mitigate' }],
            register: 'updated',
            appetite: 'low',
            tolerance: 'medium',
            residual: 'acceptable'
          },
          controls: {
            existing: [{ control: '', effectiveness: 'adequate', testing: 'annual', status: 'operating' }],
            deficient: [{ control: '', deficiency: '', impact: 'medium', remediation: '' }],
            recommended: [{ control: '', justification: '', cost: 0, benefit: 'high' }]
          },
          findings: {
            critical: 0,
            high: 1,
            medium: 3,
            low: 5,
            total: 9,
            categoryBreakdown: [{ category: 'access control', count: 2 }]
          },
          recommendations: [{ 
            recommendation: '', 
            priority: 'high', 
            category: 'technical',
            effort: 'medium',
            cost: 0,
            timeline: '60 days',
            owner: '',
            benefit: 'risk reduction'
          }],
          actionPlan: {
            immediate: [{ action: '', responsible: '', deadline: '', status: 'planned' }],
            shortTerm: [{ action: '', responsible: '', deadline: '', budget: 0 }],
            longTerm: [{ action: '', planning: '', investment: 0 }],
            monitoring: [{ kpi: '', target: '', frequency: 'monthly' }]
          },
          timeline: {
            planningPhase: '1 week',
            fieldwork: '3 weeks',
            reporting: '1 week',
            followUp: '2 weeks',
            nextAudit: ''
          },
          documentation: {
            procedures: true,
            evidences: true,
            interviews: true,
            configurations: true,
            logs: true,
            retention: '7 years'
          },
          reporting: {
            executiveSummary: true,
            detailedFindings: true,
            technicalAppendix: true,
            actionPlan: true,
            distribution: [{ recipient: '', level: 'confidential' }]
          },
          notes: 'Audit IT complet avec analyse risques et plan d\'actions sécurisé'
        };

      case 'payment-schedule':
        return {
          number: `ECH-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          creditor: {
            name: '',
            nif: ''
          },
          debtor: {
            name: '',
            contact: ''
          },
          schedule: {
            totalDebt: 0,
            invoiceNumbers: '',
            originalDueDate: '',
            signatureDate: new Date().toISOString().split('T')[0],
            installmentCount: 3,
            frequency: 'mensuel',
            firstPaymentDate: '',
            installmentAmount: 0,
            initialPayment: 0,
            paymentMethod: 'virement',
            penaltyRate: 0,
            gracePeriod: 5,
            status: 'en_cours',
            guarantees: 'aucune',
            automaticDefault: false,
            interestSuspension: false,
            notarizedAgreement: false,
            specialConditions: '',
            defaultConsequences: ''
          },
          notes: 'Échéancier de paiement avec conditions détaillées'
        };

      // DOCUMENTS CONTRACTUELS Documents
      case 'commercial-contract':
        return {
          number: `CC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          party1: {
            name: '',
            legalForm: 'SARL',
            nif: '',
            representative: '',
            title: '',
            address: ''
          },
          party2: {
            name: '',
            legalForm: 'SARL',
            nif: '',
            representative: '',
            address: ''
          },
          contract: {
            type: 'vente',
            object: '',
            totalAmount: 0,
            currency: 'DZD',
            signatureDate: new Date().toISOString().split('T')[0],
            effectiveDate: '',
            duration: '12_mois',
            expirationDate: '',
            paymentTerms: '30_jours',
            penaltyRate: 0,
            competentCourt: 'Tribunal de commerce d\'Alger',
            applicableLaw: 'algérien',
            renewalClause: false,
            confidentialityClause: false,
            exclusivityClause: false,
            party1Obligations: '',
            party2Obligations: '',
            specialConditions: ''
          },
          notes: 'Contrat commercial avec conditions juridiques algériennes'
        };

      case 'service-agreement':
        return {
          number: `CS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          provider: {
            name: '',
            nif: '',
            address: ''
          },
          client: {
            name: '',
            site: '',
            contact: ''
          },
          service: {
            type: 'maintenance',
            slaLevel: 'standard',
            responseTime: 4,
            resolutionTime: 24,
            availability: 99,
            frequency: 'mensuelle',
            monthlyCost: 0,
            interventionCost: 0,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            noticePeriod: 30,
            slaPenalty: 0,
            partsIncluded: false,
            remoteSupport: false,
            emergencyService: false,
            scope: '',
            exclusions: ''
          },
          notes: 'Convention de service avec SLA et conditions techniques'
        };

      case 'compliance-certificate':
        return {
          number: `CRT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          certifier: {
            name: '',
            accreditation: '',
            address: ''
          },
          manufacturer: {
            name: '',
            address: '',
            nif: ''
          },
          product: {
            name: '',
            model: '',
            serialNumber: '',
            manufacturingDate: ''
          },
          compliance: {
            standard: 'ISO_9001',
            standardReference: '',
            certificationType: 'produit',
            certificationDate: new Date().toISOString().split('T')[0],
            expirationDate: '',
            validity: '3',
            testLaboratory: '',
            testReportNumber: '',
            testDate: '',
            authorizedSignatory: '',
            signatoryPosition: '',
            conformityDeclaration: false,
            surveillanceRequired: false,
            internationalRecognition: false,
            certifiedCharacteristics: '',
            usageConditions: ''
          },
          notes: 'Certificat de conformité selon standards internationaux'
        };

      default:
        return baseData;
    }
  };

  const handleDocumentSelect = (document) => {
    setSelectedType(document);
    setFormData(initializeFormData(document.id));
    setCurrentStep('create');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      // Handle different item types
      if (prev.items) {
        const newItems = [...prev.items];
        newItems[index] = {
          ...newItems[index],
          [field]: value
        };

        // Recalculate item total
        if (field === 'quantity' || field === 'price') {
          newItems[index].total = (newItems[index].quantity || 1) * (newItems[index].price || 0);
        }

        return {
          ...prev,
          items: newItems
        };
      } else if (prev.specifications) {
        const newSpecs = [...prev.specifications];
        newSpecs[index] = {
          ...newSpecs[index],
          [field]: value
        };
        return { ...prev, specifications: newSpecs };
      } else if (prev.packages) {
        const newPackages = [...prev.packages];
        newPackages[index] = {
          ...newPackages[index],
          [field]: value
        };
        return { ...prev, packages: newPackages };
      }
      
      return prev;
    });
  };

  const addItem = () => {
    setFormData(prev => {
      // Check which type of items array this document uses
      if (prev.items) {
        return {
          ...prev,
          items: [...prev.items, { product: '', quantity: 1, price: 0, total: 0 }]
        };
      } else if (prev.specifications) {
        return {
          ...prev,
          specifications: [...prev.specifications, { description: '', quantity: 1, requirements: '', budget: 0 }]
        };
      } else if (prev.packages) {
        return {
          ...prev,
          packages: [...prev.packages, { type: 'carton', quantity: 1, weight: 0, dimensions: '', contents: '', value: 0 }]
        };
      } else {
        // Default for documents that use items
        return {
          ...prev,
          items: [{ product: '', quantity: 1, price: 0, total: 0 }]
        };
      }
    });
  };

  const removeItem = (index) => {
    setFormData(prev => {
      if (prev.items && prev.items.length > 1) {
        return {
          ...prev,
          items: prev.items.filter((_, i) => i !== index)
        };
      } else if (prev.specifications && prev.specifications.length > 1) {
        return {
          ...prev,
          specifications: prev.specifications.filter((_, i) => i !== index)
        };
      } else if (prev.packages && prev.packages.length > 1) {
        return {
          ...prev,
          packages: prev.packages.filter((_, i) => i !== index)
        };
      }
      return prev; // Don't remove if only one item
    });
  };

  const handleProductSelect = (index, productId) => {
    const product = activeProducts.find(p => p.id === parseInt(productId));
    
    if (product && formData.items) {
      const newItems = [...formData.items];
      newItems[index] = {
        ...newItems[index],
        product: productId, // Store as string to match select value
        designation: product.name || '', // Add product name as designation
        category: product.category || '', // Add product category
        price: product.price,
        total: newItems[index].quantity * product.price
      };

      // Update form data
      setFormData({
        ...formData,
        items: newItems
      });
    }
  };

  const handleSave = async () => {
    // Validation des données minimales
    if (!formData.number) {
      alert('Veuillez saisir un numéro de document');
      return;
    }

    if (!formData.date) {
      alert('Veuillez saisir une date');
      return;
    }

    // Pour les documents de facturation, vérification des articles
    if (['invoice', 'proforma', 'quote', 'purchase-order', 'delivery-note', 'shipping-slip'].includes(selectedType.id)) {
      if (!formData.items || formData.items.length === 0 || !formData.items[0].product) {
        alert('Veuillez ajouter au moins un article/produit');
        return;
      }
    }

    // Récupération des données de la partie contractante (client ou fournisseur)
    let selectedPartner;
    const isSupplierDocument = selectedType.id === 'purchase-order' || selectedType.id === 'quote-request';
    
    if (isSupplierDocument) {
      selectedPartner = suppliers.find(s => s.id.toString() === formData.supplier);
      if (!selectedPartner) {
        alert('Veuillez sélectionner un fournisseur');
        return;
      }
    } else {
      selectedPartner = clients.find(c => c.id.toString() === formData.client);
      if (!selectedPartner) {
        alert('Veuillez sélectionner un client');
        return;
      }
    }

    try {
      // Incrémenter le compteur pour ce type de document
      incrementDocumentCounter(selectedType.id);

      // Préparer les données pour l'archivage
      const documentData = {
        type: selectedType.id,
        number: formData.number,
        title: formData.title || selectedType.title,
        client: selectedPartner.name,
        clientId: selectedPartner.id,
        date: formData.date,
        dueDate: formData.dueDate || formData.validUntil,
        amount: formData.total || 0,
        status: 'pending',
        category: selectedType.category || 'FACTURATION',
        items: formData.items
      };

      // Save the document to the DocumentsContext
      addDocument(documentData);
      
      alert(`Document ${selectedType.title} sauvegardé et archivé avec succès !`);
      handleBack();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde du document. Veuillez réessayer.');
    }
  };



  const handleBackFromPreview = () => {
    setCurrentStep('create');
  };

  // Fonction pour valider les informations fiscales du client
  const validateClientFiscalInfo = (clientData) => {
    const missingFields = [];
    
    if (!clientData?.fiscalId) missingFields.push('ID Fiscal');
    if (!clientData?.article) missingFields.push('Article');
    if (!clientData?.rc) missingFields.push('RC N°');
    if (!clientData?.contactPhone && !clientData?.phone) missingFields.push('Contact Tel');
    
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };

  const handlePreview = () => {
    // Validation des données minimales
    if (!formData.number) {
      alert('Veuillez saisir un numéro de document');
      return;
    }

    if (!formData.date) {
      alert('Veuillez saisir une date');
      return;
    }

    // Vérifier que le partenaire approprié est sélectionné selon le type de document
    if (selectedType.id === 'purchase-order' || selectedType.id === 'quote-request') {
      const selectedSupplier = suppliers.find(s => s.id.toString() === formData.supplier);
      if (!selectedSupplier) {
        alert('Veuillez sélectionner un fournisseur');
        return;
      }
    } else {
      const selectedClient = clients.find(c => c.id.toString() === formData.client);
      if (!selectedClient) {
        alert('Veuillez sélectionner un client');
        return;
      }
    }

    // Rediriger vers l'aperçu
    setCurrentStep('preview');
  };

  const handleGeneratePDF = () => {
    // Validation des données minimales
    if (!formData.number) {
      alert('Veuillez saisir un numéro de document');
      return;
    }

    if (!formData.date) {
      alert('Veuillez saisir une date');
      return;
    }

    // Pour les documents de facturation, vérification des articles
    if (['invoice', 'proforma', 'quote', 'purchase-order', 'delivery-note', 'shipping-slip'].includes(selectedType.id)) {
      if (!formData.items || formData.items.length === 0 || !formData.items[0].product) {
        alert('Veuillez ajouter au moins un article/produit');
        return;
      }
    }

    // Récupération des données de la partie contractante (client ou fournisseur)
    let selectedPartner;
    const isSupplierDocument = selectedType.id === 'purchase-order' || selectedType.id === 'quote-request';
    
    if (isSupplierDocument) {
      selectedPartner = suppliers.find(s => s.id.toString() === formData.supplier);
      if (!selectedPartner) {
        alert('Veuillez sélectionner un fournisseur');
        return;
      }
    } else {
      selectedPartner = clients.find(c => c.id.toString() === formData.client);
      if (!selectedPartner) {
        alert('Veuillez sélectionner un client');
        return;
      }
    }

    // Validation des informations fiscales pour les factures et documents commerciaux
    const documentsRequiringFiscalInfo = ['invoice', 'proforma', 'quote', 'purchase-order'];
    if (documentsRequiringFiscalInfo.includes(selectedType.id)) {
      const validation = validateClientFiscalInfo(selectedPartner);
      
      if (!validation.isValid) {
        const missingInfo = validation.missingFields.join(', ');
        alert(`⚠️ Informations fiscales manquantes pour ce ${isSupplierDocument ? 'fournisseur' : 'client'}:\n\n${missingInfo}\n\nCes informations sont obligatoires pour générer les documents PDF.\nVeuillez compléter ces informations dans la gestion des ${isSupplierDocument ? 'fournisseurs' : 'clients'} avant de continuer.`);
        return;
      }
    }
    
    // First switch to preview mode, then the PDF can be generated from there
    // where the document-paper element exists
    setCurrentStep('preview');
  };

  const handleBack = () => {
    setCurrentStep('select');
    setSelectedType(null);
    setFormData({});
  };

  // Preview mode
  if (currentStep === 'preview' && selectedType) {
    // Récupération des données du partenaire sélectionné (client ou fournisseur)
    const isSupplierDocument = selectedType.id === 'purchase-order' || selectedType.id === 'quote-request';
    let selectedPartner;
    
    if (isSupplierDocument) {
      selectedPartner = suppliers.find(s => s.id.toString() === formData.supplier);
    } else {
      selectedPartner = clients.find(c => c.id.toString() === formData.client);
    }
    
    return (
      <DocumentPreview 
        documentType={selectedType.id}
        formData={formData}
        clientData={selectedPartner}
        partnerType={isSupplierDocument ? 'supplier' : 'client'}
        onBack={handleBackFromPreview}
        onSave={handleSave}
      />
    );
  }

  if (currentStep === 'create' && selectedType) {
    // Cette variable n'est plus nécessaire ici car nous avons les onglets
    // mais on la garde pour la rétrocompatibilité
    
    return (
      <div className="document-creation fade-in">
        <div className="page-header">
          <div className="header-navigation">
            <button className="btn-back" onClick={handleBack}>
              <ArrowLeft size={20} />
              Retour
            </button>
            <div className="header-info">
              <selectedType.icon size={24} style={{ color: selectedType.color }} />
              <h1 className="page-title">{selectedType.title}</h1>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={handlePreview}>
              <Eye size={16} />
              Aperçu
            </button>
            <button className="btn btn-success" onClick={handleGeneratePDF}>
              <Download size={16} />
              Générer PDF
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              <Save size={16} />
              Sauvegarder
            </button>
          </div>
        </div>

        <div className="document-creation-container">
          <div className="document-form">
          {/* Document Header */}
          <div className="form-section">
            <h3 className="section-title">
              <Hash size={20} />
              Informations générales
            </h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Numéro du document</label>
                <input
                  type="text"
                  value={formData.number || ''}
                  readOnly
                  className="readonly-input"
                />
              </div>
              
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => {
                    handleInputChange('date', e.target.value);
                    // Recalcul automatique de la date d'échéance pour les factures
                    if (selectedType.id === 'invoice' && e.target.value) {
                      const invoiceDate = new Date(e.target.value);
                      const dueDate = new Date(invoiceDate);
                      const paymentDays = formData.paymentTerms || 30;
                      dueDate.setDate(invoiceDate.getDate() + paymentDays);
                      handleInputChange('dueDate', dueDate.toISOString().split('T')[0]);
                    }
                  }}
                />
              </div>


            </div>
          </div>

          {/* Client/Supplier/Recipient Section */}
          {/* FACTURATION Documents - Specialized Forms */}
          {selectedType.id === 'invoice' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Facture standard algérienne
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Délai de paiement (jours)</label>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={formData.paymentTerms || 30}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 0;
                      handleInputChange('paymentTerms', days);
                      // Calcul automatique de la date d'échéance
                      const dateToUse = formData.date || new Date().toISOString().split('T')[0];
                      if (days >= 0) {
                        const invoiceDate = new Date(dateToUse);
                        const dueDate = new Date(invoiceDate);
                        dueDate.setDate(invoiceDate.getDate() + days);
                        handleInputChange('dueDate', dueDate.toISOString().split('T')[0]);
                      }
                    }}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select
                    value={formData.paymentMethod || 'virement'}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="cheque">Chèque</option>
                    <option value="especes">Espèces (max 100,000 DZD)</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="effet">Effet de commerce</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Établie par</label>
                  <select
                    value={formData.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                  >
                    <option value="">Sélectionner un employé...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Commercial</label>
                  <select
                    value={formData.salesPerson || ''}
                    onChange={(e) => handleInputChange('salesPerson', e.target.value)}
                  >
                    <option value="">Sélectionner un commercial...</option>
                    {activeEmployees.filter(emp => emp.status === 'active' && (emp.department === 'Commercial' || emp.position.toLowerCase().includes('commercial'))).map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                    {activeEmployees.filter(emp => emp.status === 'active' && emp.department !== 'Commercial' && !emp.position.toLowerCase().includes('commercial')).map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'proforma' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Facture proforma
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Valable jusqu'au</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => handleInputChange('validUntil', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Délai de livraison estimé</label>
                  <select
                    value={formData.estimatedDelivery || '15 jours ouvrables'}
                    onChange={(e) => handleInputChange('estimatedDelivery', e.target.value)}
                  >
                    <option value="Immédiat">Immédiat</option>
                    <option value="24 heures">24 heures</option>
                    <option value="2-3 jours ouvrables">2-3 jours ouvrables</option>
                    <option value="1 semaine">1 semaine</option>
                    <option value="10 jours ouvrables">10 jours ouvrables</option>
                    <option value="15 jours ouvrables">15 jours ouvrables</option>
                    <option value="3 semaines">3 semaines</option>
                    <option value="1 mois">1 mois</option>
                    <option value="6 semaines">6 semaines</option>
                    <option value="2 mois">2 mois</option>
                    <option value="3 mois">3 mois</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select
                    value={formData.paymentMethod || 'virement'}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="cheque">Chèque</option>
                    <option value="especes">Espèces (max 100,000 DZD)</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="effet">Effet de commerce</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Établie par</label>
                  <select
                    value={formData.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                  >
                    <option value="">Sélectionner un employé...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'quote-request' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileSearch size={20} />
                Demande de devis fournisseur
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Type de demande</label>
                  <input
                    type="text"
                    value={formData.requestType || ''}
                    onChange={(e) => handleInputChange('requestType', e.target.value)}
                    placeholder="Saisissez le type de demande"
                  />
                </div>
                <div className="form-group">
                  <label>Réponse souhaitée avant le</label>
                  <input
                    type="date"
                    value={formData.responseDate}
                    onChange={(e) => handleInputChange('responseDate', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Date livraison souhaitée</label>
                  <input
                    type="date"
                    value={formData.desiredStartDate || ''}
                    onChange={(e) => handleInputChange('desiredStartDate', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Lieu de livraison</label>
                  <input
                    type="text"
                    value={formData.deliveryLocation || ''}
                    onChange={(e) => handleInputChange('deliveryLocation', e.target.value)}
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            </div>
          )}

          {/* OFFRES COMMERCIALES Documents - Specialized Forms */}
          {selectedType.id === 'quote' && (
            <div className="form-section">
              <h3 className="section-title">
                <DollarSign size={20} />
                Devis commercial
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Valable jusqu'au</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => handleInputChange('validUntil', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Délai de réalisation</label>
                  <select
                    value={formData.deliveryTime || '15 jours ouvrables'}
                    onChange={(e) => handleInputChange('deliveryTime', e.target.value)}
                  >
                    <option value="Immédiat">Immédiat</option>
                    <option value="24 heures">24 heures</option>
                    <option value="2-3 jours ouvrables">2-3 jours ouvrables</option>
                    <option value="1 semaine">1 semaine</option>
                    <option value="10 jours ouvrables">10 jours ouvrables</option>
                    <option value="15 jours ouvrables">15 jours ouvrables</option>
                    <option value="3 semaines">3 semaines</option>
                    <option value="1 mois">1 mois</option>
                    <option value="6 semaines">6 semaines</option>
                    <option value="2 mois">2 mois</option>
                    <option value="3 mois">3 mois</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <select
                    value={formData.paymentTerms || '30'}
                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                  >
                    <option value="comptant">Comptant</option>
                    <option value="15">15 jours</option>
                    <option value="30">30 jours</option>
                    <option value="45">45 jours</option>
                    <option value="60">60 jours</option>
                    <option value="90">90 jours</option>
                    <option value="acompte">50% à la commande, solde à la livraison</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Commercial responsable</label>
                  <select
                    value={formData.salesPerson || ''}
                    onChange={(e) => handleInputChange('salesPerson', e.target.value)}
                  >
                    <option value="">Sélectionner un commercial...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type de projet</label>
                  <select
                    value={formData.projectType || 'standard'}
                    onChange={(e) => handleInputChange('projectType', e.target.value)}
                  >
                    <option value="standard">Standard</option>
                    <option value="sur-mesure">Sur mesure</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="consulting">Consulting</option>
                    <option value="formation">Formation</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Garantie</label>
                  <select
                    value={formData.warranty || '12 mois'}
                    onChange={(e) => handleInputChange('warranty', e.target.value)}
                  >
                    <option value="6 mois">6 mois</option>
                    <option value="12 mois">12 mois</option>
                    <option value="24 mois">24 mois</option>
                    <option value="36 mois">36 mois</option>
                    <option value="aucune">Aucune garantie</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'purchase-order' && (
            <div className="form-section">
              <h3 className="section-title">
                <ShoppingCart size={20} />
                Bon de commande
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Date de livraison souhaitée</label>
                  <input
                    type="date"
                    value={formData.deliveryDate}
                    onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Lieu de livraison</label>
                  <textarea
                    value={formData.deliveryAddress || ''}
                    onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                    placeholder="Adresse complète de livraison..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <select
                    value={formData.paymentTerms || '30'}
                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                  >
                    <option value="comptant">Comptant</option>
                    <option value="15">15 jours</option>
                    <option value="30">30 jours</option>
                    <option value="45">45 jours</option>
                    <option value="60">60 jours</option>
                    <option value="acompte">50% à la commande, solde à la livraison</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Responsable commande</label>
                  <select
                    value={formData.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                  >
                    <option value="">Sélectionner un responsable...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Référence du devis</label>
                  <input
                    type="text"
                    value={formData.quoteReference || ''}
                    onChange={(e) => handleInputChange('quoteReference', e.target.value)}
                    placeholder="Référence du devis associé (optionnel)"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'attachment' && (
            <div className="form-section">
              <h3 className="section-title">
                <Paperclip size={20} />
                Attachement
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Objet</label>
                  <input
                    type="text"
                    value={formData.subject || ''}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Objet de l'attachement"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'attachement</label>
                  <select
                    value={formData.attachmentType || 'specification'}
                    onChange={(e) => handleInputChange('attachmentType', e.target.value)}
                  >
                    <option value="specification">Spécification technique</option>
                    <option value="requirement">Cahier des charges</option>
                    <option value="procedure">Procédure</option>
                    <option value="manual">Manuel</option>
                    <option value="contract">Document contractuel</option>
                    <option value="certification">Certification</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priorité</label>
                  <select
                    value={formData.priority || 'normale'}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <option value="normale">Normale</option>
                    <option value="importante">Importante</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Version</label>
                  <input
                    type="text"
                    value={formData.version || 'v1.0'}
                    onChange={(e) => handleInputChange('version', e.target.value)}
                    placeholder="v1.0"
                  />
                </div>
                <div className="form-group">
                  <label>Responsable</label>
                  <select
                    value={formData.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                  >
                    <option value="">Sélectionner un responsable...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.status || 'draft'}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="draft">Brouillon</option>
                    <option value="review">En révision</option>
                    <option value="approved">Approuvé</option>
                    <option value="final">Final</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3 className="section-title">
              <User size={20} />
              {selectedType.id === 'purchase-order' || selectedType.id === 'quote-request' ? 'FOURNISSEUR' : 'CLIENT'}
            </h3>
            
            {/* Show supplier form for purchase-order and quote-request, client form for others */}
            {(selectedType.id === 'purchase-order' || selectedType.id === 'quote-request') ? (
              <>
                <div className="form-group">
                  <label>Sélectionner un fournisseur</label>
                  <select
                    value={formData.supplier || ''}
                    onChange={(e) => handleInputChange('supplier', e.target.value)}
                  >
                    <option value="">-- Sélectionner --</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name} - FRN-{String(supplier.id).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.supplier && (
                  <div className="client-info">
                    {(() => {
                      const selectedSupplier = suppliers.find(s => s.id === parseInt(formData.supplier));
                      return selectedSupplier ? (
                        <div className="client-details">
                          <p>{selectedSupplier.email}</p>
                          <p>{selectedSupplier.address}, {selectedSupplier.city}</p>
                          {selectedSupplier.phone && (
                            <p>Tél: {selectedSupplier.phone}</p>
                          )}
                          <div className="fiscal-info">
                            {selectedSupplier.fiscalId && (
                              <small>ID Fiscal: {selectedSupplier.fiscalId}</small>
                            )}
                            {selectedSupplier.article && (
                              <small> | Article: {selectedSupplier.article}</small>
                            )}
                            {selectedSupplier.rc && (
                              <small> | RC N°: {selectedSupplier.rc}</small>
                            )}
                            {selectedSupplier.contactPhone && selectedSupplier.contactPhone !== selectedSupplier.phone && (
                              <small> | Contact Tel: {selectedSupplier.contactPhone}</small>
                            )}
                            {selectedSupplier.specialties && (
                              <small> | Spécialités: {selectedSupplier.specialties.join(', ')}</small>
                            )}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Sélectionner un client</label>
                  <select
                    value={formData.client || ''}
                    onChange={(e) => handleInputChange('client', e.target.value)}
                  >
                    <option value="">-- Sélectionner --</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} - CLI-{String(client.id).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.client && (
                  <div className="client-info">
                    {(() => {
                      const selectedClient = clients.find(c => c.id === parseInt(formData.client));
                      return selectedClient ? (
                        <div className="client-details">
                          <p>{selectedClient.email}</p>
                          <p>{selectedClient.address}, {selectedClient.city}</p>
                          {selectedClient.phone && (
                            <p>Tél: {selectedClient.phone}</p>
                          )}
                          <div className="fiscal-info">
                            {selectedClient.fiscalId && (
                              <small>ID Fiscal: {selectedClient.fiscalId}</small>
                            )}
                            {selectedClient.article && (
                              <small> | Article: {selectedClient.article}</small>
                            )}
                            {selectedClient.rc && (
                              <small> | RC N°: {selectedClient.rc}</small>
                            )}
                            {selectedClient.contactPhone && selectedClient.contactPhone !== selectedClient.phone && (
                              <small> | Contact Tel: {selectedClient.contactPhone}</small>
                            )}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </>
            )}

            {/* Additional fields for logistics documents */}
            {(selectedType.id === 'purchase-order' || selectedType.id === 'delivery-note') && (
              <div className="form-group">
                <label>Adresse de livraison</label>
                <textarea
                  value={formData.deliveryAddress}
                  onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                  placeholder="Adresse complète de livraison..."
                  rows="3"
                />
              </div>
            )}
          </div>

          {/* Dynamic Items/Products Section */}
          {formData.items && (
            <div className="form-section">
              <h3 className="section-title">
                <Building size={20} />
                {selectedType.id === 'quote-request' ? 'Produits/Services demandés' :
                 selectedType.id === 'quote' ? 'Produits/Services proposés' :
                 selectedType.id === 'purchase-order' ? 'Articles à commander' :
                 selectedType.id === 'delivery-note' ? 'Articles livrés' :
                 selectedType.id === 'shipping-slip' ? 'Articles expédiés' : 'Produits/Services'}
              </h3>

              <div className="items-container">
                {formData.items?.map((item, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <div className="form-group">
                        <label>Produit/Service</label>
                        <select
                          value={item.product || ''}
                          onChange={(e) => handleProductSelect(index, e.target.value)}
                        >
                          <option value="">Sélectionner...</option>
                          {activeProducts.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {product.category} - {formatDZD(product.price)}/{product.unit}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Quantité</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label>Prix unitaire (DZD)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label>Total</label>
                        <input
                          type="text"
                          value={formatDZD(item.total)}
                          readOnly
                          className="readonly"
                        />
                      </div>
                    </div>

                    <button
                      className="btn-remove"
                      onClick={() => removeItem(index)}
                      disabled={formData.items && formData.items.length === 1}
                      title="Supprimer cette ligne"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <button className="btn btn-secondary add-item" onClick={addItem}>
                  <PlusCircle size={16} />
                  Ajouter une ligne
                </button>
              </div>
            </div>
          )}

          {/* Specifications Section for quote-request */}
          {formData.specifications && (
            <div className="form-section">
              <h3 className="section-title">
                <Building size={20} />
                Spécifications techniques
              </h3>

              <div className="items-container">
                {formData.specifications?.map((spec, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <div className="form-group">
                        <label>Description</label>
                        <input
                          type="text"
                          value={spec.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Description du besoin"
                        />
                      </div>

                      <div className="form-group">
                        <label>Quantité</label>
                        <input
                          type="number"
                          min="1"
                          value={spec.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label>Exigences</label>
                        <input
                          type="text"
                          value={spec.requirements}
                          onChange={(e) => handleItemChange(index, 'requirements', e.target.value)}
                          placeholder="Spécifications techniques"
                        />
                      </div>

                      <div className="form-group">
                        <label>Budget estimé (DZD)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={spec.budget}
                          onChange={(e) => handleItemChange(index, 'budget', parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    <button
                      className="btn-remove"
                      onClick={() => removeItem(index)}
                      disabled={formData.specifications && formData.specifications.length === 1}
                      title="Supprimer cette ligne"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <button className="btn btn-secondary add-item" onClick={addItem}>
                  <PlusCircle size={16} />
                  Ajouter une spécification
                </button>
              </div>
            </div>
          )}

          {/* Packages Section for shipping-slip */}
          {formData.packages && (
            <div className="form-section">
              <h3 className="section-title">
                <Building size={20} />
                Détail des colis
              </h3>

              <div className="items-container">
                {formData.packages?.map((pkg, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <div className="form-group">
                        <label>Type</label>
                        <select
                          value={pkg.type}
                          onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                        >
                          <option value="carton">Carton</option>
                          <option value="palette">Palette</option>
                          <option value="sac">Sac</option>
                          <option value="caisse">Caisse</option>
                          <option value="enveloppe">Enveloppe</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Quantité</label>
                        <input
                          type="number"
                          min="1"
                          value={pkg.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label>Poids (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={pkg.weight}
                          onChange={(e) => handleItemChange(index, 'weight', parseFloat(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label>Dimensions</label>
                        <input
                          type="text"
                          value={pkg.dimensions}
                          onChange={(e) => handleItemChange(index, 'dimensions', e.target.value)}
                          placeholder="L x l x h (cm)"
                        />
                      </div>

                      <div className="form-group">
                        <label>Contenu</label>
                        <input
                          type="text"
                          value={pkg.contents}
                          onChange={(e) => handleItemChange(index, 'contents', e.target.value)}
                          placeholder="Description du contenu"
                        />
                      </div>

                      <div className="form-group">
                        <label>Valeur (DZD)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pkg.value}
                          onChange={(e) => handleItemChange(index, 'value', parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    <button
                      className="btn-remove"
                      onClick={() => removeItem(index)}
                      disabled={formData.packages && formData.packages.length === 1}
                      title="Supprimer cette ligne"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <button className="btn btn-secondary add-item" onClick={addItem}>
                  <PlusCircle size={16} />
                  Ajouter un colis
                </button>
              </div>
            </div>
          )}

          {/* PAIEMENTS & RECOUVREMENT Documents */}
          {selectedType.id === 'payment-demand' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Mise en demeure
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Créancier (Nom)</label>
                  <input
                    type="text"
                    value={formData.creditor?.name || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, name: e.target.value})}
                    placeholder="Nom de l'entreprise créancière"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique</label>
                  <select
                    value={formData.creditor?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">Entreprise individuelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Créancier</label>
                  <input
                    type="text"
                    value={formData.creditor?.nif || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Débiteur (Client)</label>
                  <input
                    type="text"
                    value={formData.debtor?.name || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, name: e.target.value})}
                    placeholder="Nom du client débiteur"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse débiteur</label>
                  <input
                    type="text"
                    value={formData.debtor?.address || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, address: e.target.value})}
                    placeholder="Adresse complète du débiteur"
                  />
                </div>
                <div className="form-group">
                  <label>Contact débiteur</label>
                  <input
                    type="text"
                    value={formData.debtor?.contact || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, contact: e.target.value})}
                    placeholder="Téléphone/Email"
                  />
                </div>
                <div className="form-group">
                  <label>Facture(s) impayée(s)</label>
                  <input
                    type="text"
                    value={formData.debt?.invoiceNumbers || ''}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, invoiceNumbers: e.target.value})}
                    placeholder="Numéros des factures (séparés par virgule)"
                  />
                </div>
                <div className="form-group">
                  <label>Date de facturation</label>
                  <input
                    type="date"
                    value={formData.debt?.invoiceDate || ''}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, invoiceDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date d'échéance</label>
                  <input
                    type="date"
                    value={formData.debt?.dueDate || ''}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, dueDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant principal (DZD)</label>
                  <input
                    type="number"
                    value={formData.debt?.principalAmount || 0}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, principalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Pénalités de retard (DZD)</label>
                  <input
                    type="number"
                    value={formData.debt?.penalties || 0}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, penalties: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Intérêts de retard (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.debt?.interestRate || 0}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, interestRate: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Montant total dû (DZD)</label>
                  <input
                    type="number"
                    value={formData.debt?.totalAmount || 0}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, totalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre de relances</label>
                  <input
                    type="number"
                    value={formData.demand?.reminderCount || 1}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, reminderCount: parseInt(e.target.value)})}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Type de mise en demeure</label>
                  <select
                    value={formData.demand?.type || 'amiable'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, type: e.target.value})}
                  >
                    <option value="amiable">Amiable</option>
                    <option value="formelle">Formelle</option>
                    <option value="dernière">Dernière relance</option>
                    <option value="judiciaire">Avant poursuites judiciaires</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Délai de paiement accordé</label>
                  <select
                    value={formData.demand?.paymentDeadline || '15_jours'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, paymentDeadline: e.target.value})}
                  >
                    <option value="8_jours">8 jours</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                    <option value="immédiat">Immédiat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de signification</label>
                  <select
                    value={formData.demand?.deliveryMethod || 'recommandé'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, deliveryMethod: e.target.value})}
                  >
                    <option value="recommandé">Recommandé avec AR</option>
                    <option value="huissier">Huissier de justice</option>
                    <option value="main_propre">Remise en main propre</option>
                    <option value="email">Email avec accusé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Frais de recouvrement (DZD)</label>
                  <input
                    type="number"
                    value={formData.demand?.recoveryFees || 0}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, recoveryFees: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Référence juridique</label>
                  <select
                    value={formData.demand?.legalReference || 'code_civil'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, legalReference: e.target.value})}
                  >
                    <option value="code_civil">Code civil algérien</option>
                    <option value="code_commerce">Code de commerce</option>
                    <option value="conditions_générales">Conditions générales de vente</option>
                    <option value="contrat">Dispositions contractuelles</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.demand?.formalNotice || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, formalNotice: e.target.checked})}
                    />
                    Mise en demeure formelle
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.demand?.legalAction || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, legalAction: e.target.checked})}
                    />
                    Menace de poursuites judiciaires
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.demand?.paymentPlan || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, paymentPlan: e.target.checked})}
                    />
                    Possibilité d'échéancier
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Motif du retard (si connu)</label>
                <textarea
                  value={formData.demand?.delayReason || ''}
                  onChange={(e) => handleInputChange('demand', {...formData.demand, delayReason: e.target.value})}
                  placeholder="Raison invoquée par le débiteur pour le retard de paiement"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Conditions de règlement proposées</label>
                <textarea
                  value={formData.demand?.settlementConditions || ''}
                  onChange={(e) => handleInputChange('demand', {...formData.demand, settlementConditions: e.target.value})}
                  placeholder="Modalités de paiement acceptées (virement, chèque, espèces, échéancier...)"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'payment-receipt' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Reçu de paiement
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Bénéficiaire (Nom)</label>
                  <input
                    type="text"
                    value={formData.recipient?.name || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, name: e.target.value})}
                    placeholder="Nom de l'entreprise bénéficiaire"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Bénéficiaire</label>
                  <input
                    type="text"
                    value={formData.recipient?.nif || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse bénéficiaire</label>
                  <input
                    type="text"
                    value={formData.recipient?.address || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, address: e.target.value})}
                    placeholder="Adresse complète"
                  />
                </div>
                <div className="form-group">
                  <label>Payeur (Client)</label>
                  <input
                    type="text"
                    value={formData.payer?.name || ''}
                    onChange={(e) => handleInputChange('payer', {...formData.payer, name: e.target.value})}
                    placeholder="Nom du client payeur"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse payeur</label>
                  <input
                    type="text"
                    value={formData.payer?.address || ''}
                    onChange={(e) => handleInputChange('payer', {...formData.payer, address: e.target.value})}
                    placeholder="Adresse du payeur"
                  />
                </div>
                <div className="form-group">
                  <label>Contact payeur</label>
                  <input
                    type="text"
                    value={formData.payer?.contact || ''}
                    onChange={(e) => handleInputChange('payer', {...formData.payer, contact: e.target.value})}
                    placeholder="Téléphone/Email du payeur"
                  />
                </div>
                <div className="form-group">
                  <label>Date de paiement</label>
                  <input
                    type="date"
                    value={formData.payment?.date || ''}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de paiement</label>
                  <input
                    type="time"
                    value={formData.payment?.time || ''}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, time: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant reçu (DZD)</label>
                  <input
                    type="number"
                    value={formData.payment?.amount || 0}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, amount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Devise</label>
                  <select
                    value={formData.payment?.currency || 'DZD'}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, currency: e.target.value})}
                  >
                    <option value="DZD">Dinar algérien (DZD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="USD">Dollar US (USD)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select
                    value={formData.payment?.method || 'virement'}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, method: e.target.value})}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="chèque">Chèque</option>
                    <option value="espèces">Espèces</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="traite">Traite</option>
                    <option value="compensation">Compensation</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Référence de paiement</label>
                  <input
                    type="text"
                    value={formData.payment?.reference || ''}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, reference: e.target.value})}
                    placeholder="Numéro de chèque, virement, etc."
                  />
                </div>
                <div className="form-group">
                  <label>Banque émettrice</label>
                  <input
                    type="text"
                    value={formData.payment?.bank || ''}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, bank: e.target.value})}
                    placeholder="Nom de la banque"
                  />
                </div>
                <div className="form-group">
                  <label>Facture(s) réglée(s)</label>
                  <input
                    type="text"
                    value={formData.payment?.invoiceNumbers || ''}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, invoiceNumbers: e.target.value})}
                    placeholder="Numéros des factures payées"
                  />
                </div>
                <div className="form-group">
                  <label>Montant facturé initial (DZD)</label>
                  <input
                    type="number"
                    value={formData.payment?.originalAmount || 0}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, originalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Solde restant (DZD)</label>
                  <input
                    type="number"
                    value={formData.payment?.remainingBalance || 0}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, remainingBalance: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Type de règlement</label>
                  <select
                    value={formData.payment?.settlementType || 'total'}
                    onChange={(e) => handleInputChange('payment', {...formData.payment, settlementType: e.target.value})}
                  >
                    <option value="total">Règlement total</option>
                    <option value="partiel">Règlement partiel</option>
                    <option value="acompte">Acompte</option>
                    <option value="solde">Solde final</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reçu par</label>
                  <input
                    type="text"
                    value={formData.receipt?.receivedBy || ''}
                    onChange={(e) => handleInputChange('receipt', {...formData.receipt, receivedBy: e.target.value})}
                    placeholder="Nom de la personne qui a reçu le paiement"
                  />
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={formData.receipt?.position || ''}
                    onChange={(e) => handleInputChange('receipt', {...formData.receipt, position: e.target.value})}
                    placeholder="Fonction dans l'entreprise"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.payment?.verified || false}
                      onChange={(e) => handleInputChange('payment', {...formData.payment, verified: e.target.checked})}
                    />
                    Paiement vérifié
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.payment?.deposited || false}
                      onChange={(e) => handleInputChange('payment', {...formData.payment, deposited: e.target.checked})}
                    />
                    Chèque déposé en banque
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.receipt?.duplicate || false}
                      onChange={(e) => handleInputChange('receipt', {...formData.receipt, duplicate: e.target.checked})}
                    />
                    Duplicata émis
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations</label>
                <textarea
                  value={formData.payment?.observations || ''}
                  onChange={(e) => handleInputChange('payment', {...formData.payment, observations: e.target.value})}
                  placeholder="Remarques particulières sur ce paiement"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'payment-schedule' && (
            <div className="form-section">
              <h3 className="section-title">
                <Calendar size={20} />
                Échéancier de paiement
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Créancier (Nom)</label>
                  <input
                    type="text"
                    value={formData.creditor?.name || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, name: e.target.value})}
                    placeholder="Nom de l'entreprise créancière"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Créancier</label>
                  <input
                    type="text"
                    value={formData.creditor?.nif || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Débiteur (Client)</label>
                  <input
                    type="text"
                    value={formData.debtor?.name || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, name: e.target.value})}
                    placeholder="Nom du client débiteur"
                  />
                </div>
                <div className="form-group">
                  <label>Contact débiteur</label>
                  <input
                    type="text"
                    value={formData.debtor?.contact || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, contact: e.target.value})}
                    placeholder="Téléphone/Email du débiteur"
                  />
                </div>
                <div className="form-group">
                  <label>Montant total de la dette (DZD)</label>
                  <input
                    type="number"
                    value={formData.schedule?.totalDebt || 0}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, totalDebt: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Facture(s) concernée(s)</label>
                  <input
                    type="text"
                    value={formData.schedule?.invoiceNumbers || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, invoiceNumbers: e.target.value})}
                    placeholder="Numéros des factures"
                  />
                </div>
                <div className="form-group">
                  <label>Date d'échéance initiale</label>
                  <input
                    type="date"
                    value={formData.schedule?.originalDueDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, originalDueDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de signature de l'échéancier</label>
                  <input
                    type="date"
                    value={formData.schedule?.signatureDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, signatureDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Nombre d'échéances</label>
                  <input
                    type="number"
                    value={formData.schedule?.installmentCount || 3}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, installmentCount: parseInt(e.target.value)})}
                    placeholder="3"
                  />
                </div>
                <div className="form-group">
                  <label>Fréquence des paiements</label>
                  <select
                    value={formData.schedule?.frequency || 'mensuel'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, frequency: e.target.value})}
                  >
                    <option value="hebdomadaire">Hebdomadaire</option>
                    <option value="bi_mensuel">Bi-mensuel</option>
                    <option value="mensuel">Mensuel</option>
                    <option value="bimestriel">Bimestriel</option>
                    <option value="trimestriel">Trimestriel</option>
                    <option value="personnalisé">Personnalisé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Premier paiement le</label>
                  <input
                    type="date"
                    value={formData.schedule?.firstPaymentDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, firstPaymentDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant par échéance (DZD)</label>
                  <input
                    type="number"
                    value={formData.schedule?.installmentAmount || 0}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, installmentAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Acompte initial (DZD)</label>
                  <input
                    type="number"
                    value={formData.schedule?.initialPayment || 0}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, initialPayment: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Mode de paiement accepté</label>
                  <select
                    value={formData.schedule?.paymentMethod || 'virement'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, paymentMethod: e.target.value})}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="chèque">Chèque</option>
                    <option value="prélèvement">Prélèvement automatique</option>
                    <option value="espèces">Espèces</option>
                    <option value="mixte">Plusieurs modes</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Pénalités en cas de retard (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.schedule?.penaltyRate || 0}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, penaltyRate: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Délai de grâce (jours)</label>
                  <input
                    type="number"
                    value={formData.schedule?.gracePeriod || 5}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, gracePeriod: parseInt(e.target.value)})}
                    placeholder="5"
                  />
                </div>
                <div className="form-group">
                  <label>Statut de l'échéancier</label>
                  <select
                    value={formData.schedule?.status || 'en_cours'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, status: e.target.value})}
                  >
                    <option value="proposé">Proposé</option>
                    <option value="accepté">Accepté</option>
                    <option value="en_cours">En cours</option>
                    <option value="respecté">Respecté</option>
                    <option value="défaillant">En défaillance</option>
                    <option value="soldé">Soldé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Garanties demandées</label>
                  <select
                    value={formData.schedule?.guarantees || 'aucune'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, guarantees: e.target.value})}
                  >
                    <option value="aucune">Aucune</option>
                    <option value="aval">Aval</option>
                    <option value="caution">Caution personnelle</option>
                    <option value="hypothèque">Hypothèque</option>
                    <option value="nantissement">Nantissement</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.automaticDefault || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, automaticDefault: e.target.checked})}
                    />
                    Déchéance automatique du terme
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.interestSuspension || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, interestSuspension: e.target.checked})}
                    />
                    Suspension des intérêts de retard
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.notarizedAgreement || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, notarizedAgreement: e.target.checked})}
                    />
                    Accord notarié
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Conditions particulières</label>
                <textarea
                  value={formData.schedule?.specialConditions || ''}
                  onChange={(e) => handleInputChange('schedule', {...formData.schedule, specialConditions: e.target.value})}
                  placeholder="Conditions spéciales, clauses particulières, modalités d'exécution..."
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Conséquences en cas de défaillance</label>
                <textarea
                  value={formData.schedule?.defaultConsequences || ''}
                  onChange={(e) => handleInputChange('schedule', {...formData.schedule, defaultConsequences: e.target.value})}
                  placeholder="Actions qui seront entreprises en cas de non-respect de l'échéancier"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* DOCUMENTS CONTRACTUELS Documents */}
          {selectedType.id === 'commercial-contract' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Contrat commercial
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Partie contractante 1 (Nom)</label>
                  <input
                    type="text"
                    value={formData.party1?.name || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, name: e.target.value})}
                    placeholder="Nom de l'entreprise contractante"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique Partie 1</label>
                  <select
                    value={formData.party1?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">Entreprise individuelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Partie 1</label>
                  <input
                    type="text"
                    value={formData.party1?.nif || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Représentant légal Partie 1</label>
                  <input
                    type="text"
                    value={formData.party1?.representative || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, representative: e.target.value})}
                    placeholder="Nom du représentant légal"
                  />
                </div>
                <div className="form-group">
                  <label>Titre représentant Partie 1</label>
                  <input
                    type="text"
                    value={formData.party1?.title || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, title: e.target.value})}
                    placeholder="Gérant, Directeur Général, etc."
                  />
                </div>
                <div className="form-group">
                  <label>Adresse Partie 1</label>
                  <input
                    type="text"
                    value={formData.party1?.address || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, address: e.target.value})}
                    placeholder="Adresse complète"
                  />
                </div>
                <div className="form-group">
                  <label>Partie contractante 2 (Client)</label>
                  <input
                    type="text"
                    value={formData.party2?.name || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, name: e.target.value})}
                    placeholder="Nom du client"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique Partie 2</label>
                  <select
                    value={formData.party2?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">Entreprise individuelle</option>
                    <option value="particulier">Particulier</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Partie 2</label>
                  <input
                    type="text"
                    value={formData.party2?.nif || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Représentant légal Partie 2</label>
                  <input
                    type="text"
                    value={formData.party2?.representative || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, representative: e.target.value})}
                    placeholder="Nom du représentant"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse Partie 2</label>
                  <input
                    type="text"
                    value={formData.party2?.address || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, address: e.target.value})}
                    placeholder="Adresse complète du client"
                  />
                </div>
                <div className="form-group">
                  <label>Type de contrat</label>
                  <select
                    value={formData.contract?.type || 'vente'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, type: e.target.value})}
                  >
                    <option value="vente">Contrat de vente</option>
                    <option value="prestation">Contrat de prestation</option>
                    <option value="distribution">Contrat de distribution</option>
                    <option value="partenariat">Contrat de partenariat</option>
                    <option value="fourniture">Contrat de fourniture</option>
                    <option value="maintenance">Contrat de maintenance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Objet du contrat</label>
                  <input
                    type="text"
                    value={formData.contract?.object || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, object: e.target.value})}
                    placeholder="Description de l'objet du contrat"
                  />
                </div>
                <div className="form-group">
                  <label>Montant total (DZD)</label>
                  <input
                    type="number"
                    value={formData.contract?.totalAmount || 0}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, totalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Devise</label>
                  <select
                    value={formData.contract?.currency || 'DZD'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, currency: e.target.value})}
                  >
                    <option value="DZD">Dinar algérien (DZD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="USD">Dollar US (USD)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de signature</label>
                  <input
                    type="date"
                    value={formData.contract?.signatureDate || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, signatureDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date d'entrée en vigueur</label>
                  <input
                    type="date"
                    value={formData.contract?.effectiveDate || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, effectiveDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Durée du contrat</label>
                  <select
                    value={formData.contract?.duration || '12_mois'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, duration: e.target.value})}
                  >
                    <option value="3_mois">3 mois</option>
                    <option value="6_mois">6 mois</option>
                    <option value="12_mois">12 mois</option>
                    <option value="24_mois">24 mois</option>
                    <option value="36_mois">36 mois</option>
                    <option value="indéterminée">Durée indéterminée</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'expiration</label>
                  <input
                    type="date"
                    value={formData.contract?.expirationDate || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, expirationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Modalités de paiement</label>
                  <select
                    value={formData.contract?.paymentTerms || '30_jours'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, paymentTerms: e.target.value})}
                  >
                    <option value="comptant">Comptant</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                    <option value="45_jours">45 jours</option>
                    <option value="60_jours">60 jours</option>
                    <option value="échelonné">Paiement échelonné</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Pénalités de retard (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.contract?.penaltyRate || 0}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, penaltyRate: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Tribunal compétent</label>
                  <input
                    type="text"
                    value={formData.contract?.competentCourt || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, competentCourt: e.target.value})}
                    placeholder="Tribunal de commerce d'Alger"
                  />
                </div>
                <div className="form-group">
                  <label>Droit applicable</label>
                  <select
                    value={formData.contract?.applicableLaw || 'algérien'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, applicableLaw: e.target.value})}
                  >
                    <option value="algérien">Droit algérien</option>
                    <option value="français">Droit français</option>
                    <option value="international">Droit international</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.contract?.renewalClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, renewalClause: e.target.checked})}
                    />
                    Clause de renouvellement automatique
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.contract?.confidentialityClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, confidentialityClause: e.target.checked})}
                    />
                    Clause de confidentialité
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.contract?.exclusivityClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, exclusivityClause: e.target.checked})}
                    />
                    Clause d'exclusivité
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Obligations de la Partie 1</label>
                <textarea
                  value={formData.contract?.party1Obligations || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, party1Obligations: e.target.value})}
                  placeholder="Détailler les obligations et responsabilités de la première partie"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Obligations de la Partie 2</label>
                <textarea
                  value={formData.contract?.party2Obligations || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, party2Obligations: e.target.value})}
                  placeholder="Détailler les obligations et responsabilités de la seconde partie"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Conditions particulières</label>
                <textarea
                  value={formData.contract?.specialConditions || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, specialConditions: e.target.value})}
                  placeholder="Conditions spéciales, clauses particulières, modalités d'exécution..."
                  rows="3"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'service-agreement' && (
            <div className="form-section">
              <h3 className="section-title">
                <Settings size={20} />
                Convention de service
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Prestataire (Nom)</label>
                  <input
                    type="text"
                    value={formData.provider?.name || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, name: e.target.value})}
                    placeholder="Nom de l'entreprise prestataire"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Prestataire</label>
                  <input
                    type="text"
                    value={formData.provider?.nif || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse prestataire</label>
                  <input
                    type="text"
                    value={formData.provider?.address || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, address: e.target.value})}
                    placeholder="Adresse complète du prestataire"
                  />
                </div>
                <div className="form-group">
                  <label>Client bénéficiaire</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Nom du client bénéficiaire"
                  />
                </div>
                <div className="form-group">
                  <label>Site client</label>
                  <input
                    type="text"
                    value={formData.client?.site || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, site: e.target.value})}
                    placeholder="Localisation du site client"
                  />
                </div>
                <div className="form-group">
                  <label>Contact client</label>
                  <input
                    type="text"
                    value={formData.client?.contact || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, contact: e.target.value})}
                    placeholder="Personne de contact chez le client"
                  />
                </div>
                <div className="form-group">
                  <label>Type de service</label>
                  <select
                    value={formData.service?.type || 'maintenance'}
                    onChange={(e) => handleInputChange('service', {...formData.service, type: e.target.value})}
                  >
                    <option value="maintenance">Maintenance préventive</option>
                    <option value="support">Support technique</option>
                    <option value="dépannage">Service de dépannage</option>
                    <option value="formation">Formation</option>
                    <option value="consulting">Conseil et expertise</option>
                    <option value="installation">Installation</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau de service (SLA)</label>
                  <select
                    value={formData.service?.slaLevel || 'standard'}
                    onChange={(e) => handleInputChange('service', {...formData.service, slaLevel: e.target.value})}
                  >
                    <option value="basique">Basique (8x5)</option>
                    <option value="standard">Standard (12x5)</option>
                    <option value="étendu">Étendu (16x6)</option>
                    <option value="premium">Premium (24x7)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Temps de réponse (heures)</label>
                  <input
                    type="number"
                    value={formData.service?.responseTime || 4}
                    onChange={(e) => handleInputChange('service', {...formData.service, responseTime: parseInt(e.target.value)})}
                    placeholder="4"
                  />
                </div>
                <div className="form-group">
                  <label>Temps de résolution (heures)</label>
                  <input
                    type="number"
                    value={formData.service?.resolutionTime || 24}
                    onChange={(e) => handleInputChange('service', {...formData.service, resolutionTime: parseInt(e.target.value)})}
                    placeholder="24"
                  />
                </div>
                <div className="form-group">
                  <label>Disponibilité garantie (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.service?.availability || 99}
                    onChange={(e) => handleInputChange('service', {...formData.service, availability: parseFloat(e.target.value)})}
                    placeholder="99"
                  />
                </div>
                <div className="form-group">
                  <label>Fréquence des interventions</label>
                  <select
                    value={formData.service?.frequency || 'mensuelle'}
                    onChange={(e) => handleInputChange('service', {...formData.service, frequency: e.target.value})}
                  >
                    <option value="hebdomadaire">Hebdomadaire</option>
                    <option value="bi_mensuelle">Bi-mensuelle</option>
                    <option value="mensuelle">Mensuelle</option>
                    <option value="trimestrielle">Trimestrielle</option>
                    <option value="semestrielle">Semestrielle</option>
                    <option value="annuelle">Annuelle</option>
                    <option value="à_la_demande">À la demande</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Coût mensuel (DZD)</label>
                  <input
                    type="number"
                    value={formData.service?.monthlyCost || 0}
                    onChange={(e) => handleInputChange('service', {...formData.service, monthlyCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Coût intervention (DZD)</label>
                  <input
                    type="number"
                    value={formData.service?.interventionCost || 0}
                    onChange={(e) => handleInputChange('service', {...formData.service, interventionCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.service?.startDate || ''}
                    onChange={(e) => handleInputChange('service', {...formData.service, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin</label>
                  <input
                    type="date"
                    value={formData.service?.endDate || ''}
                    onChange={(e) => handleInputChange('service', {...formData.service, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Période de préavis (jours)</label>
                  <input
                    type="number"
                    value={formData.service?.noticePeriod || 30}
                    onChange={(e) => handleInputChange('service', {...formData.service, noticePeriod: parseInt(e.target.value)})}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label>Pénalités SLA (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.service?.slaPenalty || 0}
                    onChange={(e) => handleInputChange('service', {...formData.service, slaPenalty: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.service?.partsIncluded || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, partsIncluded: e.target.checked})}
                    />
                    Pièces de rechange incluses
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.service?.remoteSupport || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, remoteSupport: e.target.checked})}
                    />
                    Support à distance inclus
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.service?.emergencyService || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, emergencyService: e.target.checked})}
                    />
                    Service d'urgence 24h/24
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Périmètre d'intervention</label>
                <textarea
                  value={formData.service?.scope || ''}
                  onChange={(e) => handleInputChange('service', {...formData.service, scope: e.target.value})}
                  placeholder="Détailler le périmètre des services couverts par la convention"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Exclusions</label>
                <textarea
                  value={formData.service?.exclusions || ''}
                  onChange={(e) => handleInputChange('service', {...formData.service, exclusions: e.target.value})}
                  placeholder="Services et situations non couverts par la convention"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'compliance-certificate' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Certificat de conformité
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Organisme certificateur</label>
                  <input
                    type="text"
                    value={formData.certifier?.name || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, name: e.target.value})}
                    placeholder="Nom de l'organisme certificateur"
                  />
                </div>
                <div className="form-group">
                  <label>Accréditation organisme</label>
                  <input
                    type="text"
                    value={formData.certifier?.accreditation || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, accreditation: e.target.value})}
                    placeholder="Numéro d'accréditation ALGERAC"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse organisme</label>
                  <input
                    type="text"
                    value={formData.certifier?.address || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, address: e.target.value})}
                    placeholder="Adresse de l'organisme"
                  />
                </div>
                <div className="form-group">
                  <label>Fabricant/Fournisseur</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.name || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, name: e.target.value})}
                    placeholder="Nom du fabricant ou fournisseur"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse fabricant</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.address || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, address: e.target.value})}
                    placeholder="Adresse du fabricant"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Fabricant</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.nif || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Produit/Service certifié</label>
                  <input
                    type="text"
                    value={formData.product?.name || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, name: e.target.value})}
                    placeholder="Désignation du produit ou service"
                  />
                </div>
                <div className="form-group">
                  <label>Modèle/Référence</label>
                  <input
                    type="text"
                    value={formData.product?.model || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, model: e.target.value})}
                    placeholder="Modèle ou référence commerciale"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de série/Lot</label>
                  <input
                    type="text"
                    value={formData.product?.serialNumber || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, serialNumber: e.target.value})}
                    placeholder="Numéro de série ou de lot"
                  />
                </div>
                <div className="form-group">
                  <label>Date de fabrication</label>
                  <input
                    type="date"
                    value={formData.product?.manufacturingDate || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, manufacturingDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Norme de référence</label>
                  <select
                    value={formData.compliance?.standard || 'ISO_9001'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, standard: e.target.value})}
                  >
                    <option value="ISO_9001">ISO 9001 - Qualité</option>
                    <option value="ISO_14001">ISO 14001 - Environnement</option>
                    <option value="ISO_45001">ISO 45001 - Sécurité</option>
                    <option value="CE">Marquage CE</option>
                    <option value="NF">Norme NF</option>
                    <option value="AFNOR">AFNOR</option>
                    <option value="IANOR">IANOR (Algérie)</option>
                    <option value="autre">Autre norme</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Référence norme</label>
                  <input
                    type="text"
                    value={formData.compliance?.standardReference || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, standardReference: e.target.value})}
                    placeholder="Ex: ISO 9001:2015"
                  />
                </div>
                <div className="form-group">
                  <label>Type de certification</label>
                  <select
                    value={formData.compliance?.certificationType || 'produit'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, certificationType: e.target.value})}
                  >
                    <option value="produit">Certification produit</option>
                    <option value="système">Certification système</option>
                    <option value="service">Certification service</option>
                    <option value="personnel">Certification personnel</option>
                    <option value="processus">Certification processus</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de certification</label>
                  <input
                    type="date"
                    value={formData.compliance?.certificationDate || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, certificationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date d'expiration</label>
                  <input
                    type="date"
                    value={formData.compliance?.expirationDate || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, expirationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Validité (années)</label>
                  <select
                    value={formData.compliance?.validity || '3'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, validity: e.target.value})}
                  >
                    <option value="1">1 an</option>
                    <option value="2">2 ans</option>
                    <option value="3">3 ans</option>
                    <option value="5">5 ans</option>
                    <option value="permanente">Permanente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Laboratoire d'essais</label>
                  <input
                    type="text"
                    value={formData.compliance?.testLaboratory || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, testLaboratory: e.target.value})}
                    placeholder="Nom du laboratoire d'essais"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de rapport d'essai</label>
                  <input
                    type="text"
                    value={formData.compliance?.testReportNumber || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, testReportNumber: e.target.value})}
                    placeholder="Référence du rapport d'essai"
                  />
                </div>
                <div className="form-group">
                  <label>Date des essais</label>
                  <input
                    type="date"
                    value={formData.compliance?.testDate || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, testDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Signataire autorisé</label>
                  <input
                    type="text"
                    value={formData.compliance?.authorizedSignatory || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, authorizedSignatory: e.target.value})}
                    placeholder="Nom du signataire autorisé"
                  />
                </div>
                <div className="form-group">
                  <label>Fonction signataire</label>
                  <input
                    type="text"
                    value={formData.compliance?.signatoryPosition || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, signatoryPosition: e.target.value})}
                    placeholder="Directeur technique, Responsable qualité..."
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.conformityDeclaration || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, conformityDeclaration: e.target.checked})}
                    />
                    Déclaration de conformité incluse
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.surveillanceRequired || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, surveillanceRequired: e.target.checked})}
                    />
                    Surveillance périodique requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.internationalRecognition || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, internationalRecognition: e.target.checked})}
                    />
                    Reconnaissance internationale
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Caractéristiques certifiées</label>
                <textarea
                  value={formData.compliance?.certifiedCharacteristics || ''}
                  onChange={(e) => handleInputChange('compliance', {...formData.compliance, certifiedCharacteristics: e.target.value})}
                  placeholder="Détailler les caractéristiques, performances ou propriétés certifiées"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Conditions d'utilisation</label>
                <textarea
                  value={formData.compliance?.usageConditions || ''}
                  onChange={(e) => handleInputChange('compliance', {...formData.compliance, usageConditions: e.target.value})}
                  placeholder="Conditions et limites d'utilisation du certificat"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* COMPTABILITÉ & FINANCE Documents */}
          {selectedType.id === 'credit-note' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Avoir (Note de crédit)
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Émetteur (Nom)</label>
                  <input
                    type="text"
                    value={formData.issuer?.name || ''}
                    onChange={(e) => handleInputChange('issuer', {...formData.issuer, name: e.target.value})}
                    placeholder="Nom de l'entreprise émettrice"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Émetteur</label>
                  <input
                    type="text"
                    value={formData.issuer?.nif || ''}
                    onChange={(e) => handleInputChange('issuer', {...formData.issuer, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse émetteur</label>
                  <input
                    type="text"
                    value={formData.issuer?.address || ''}
                    onChange={(e) => handleInputChange('issuer', {...formData.issuer, address: e.target.value})}
                    placeholder="Adresse complète de l'émetteur"
                  />
                </div>
                <div className="form-group">
                  <label>Client bénéficiaire</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Nom du client bénéficiaire"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse client</label>
                  <input
                    type="text"
                    value={formData.client?.address || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, address: e.target.value})}
                    placeholder="Adresse du client"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Client</label>
                  <input
                    type="text"
                    value={formData.client?.nif || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale client"
                  />
                </div>
                <div className="form-group">
                  <label>Facture d'origine</label>
                  <input
                    type="text"
                    value={formData.originalInvoice?.number || ''}
                    onChange={(e) => handleInputChange('originalInvoice', {...formData.originalInvoice, number: e.target.value})}
                    placeholder="Numéro de la facture d'origine"
                  />
                </div>
                <div className="form-group">
                  <label>Date facture d'origine</label>
                  <input
                    type="date"
                    value={formData.originalInvoice?.date || ''}
                    onChange={(e) => handleInputChange('originalInvoice', {...formData.originalInvoice, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant facture d'origine (DZD)</label>
                  <input
                    type="number"
                    value={formData.originalInvoice?.amount || 0}
                    onChange={(e) => handleInputChange('originalInvoice', {...formData.originalInvoice, amount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'avoir</label>
                  <select
                    value={formData.creditNote?.type || 'retour'}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, type: e.target.value})}
                  >
                    <option value="retour">Retour de marchandises</option>
                    <option value="remise">Remise commerciale</option>
                    <option value="correction">Correction d'erreur</option>
                    <option value="annulation">Annulation partielle</option>
                    <option value="défaut">Défaut de conformité</option>
                    <option value="ristourne">Ristourne de fin d'année</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Montant de l'avoir (DZD)</label>
                  <input
                    type="number"
                    value={formData.creditNote?.amount || 0}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, amount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>TVA sur avoir (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.creditNote?.vatRate || 19}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, vatRate: parseFloat(e.target.value)})}
                    placeholder="19"
                  />
                </div>
                <div className="form-group">
                  <label>Montant TVA (DZD)</label>
                  <input
                    type="number"
                    value={formData.creditNote?.vatAmount || 0}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, vatAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Montant total TTC (DZD)</label>
                  <input
                    type="number"
                    value={formData.creditNote?.totalAmount || 0}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, totalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Mode de remboursement</label>
                  <select
                    value={formData.creditNote?.refundMethod || 'virement'}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, refundMethod: e.target.value})}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="chèque">Chèque</option>
                    <option value="espèces">Espèces</option>
                    <option value="compensation">Compensation</option>
                    <option value="crédit_compte">Crédit sur compte client</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Délai de remboursement</label>
                  <select
                    value={formData.creditNote?.refundDelay || '15_jours'}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, refundDelay: e.target.value})}
                  >
                    <option value="immédiat">Immédiat</option>
                    <option value="8_jours">8 jours</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut de l'avoir</label>
                  <select
                    value={formData.creditNote?.status || 'émis'}
                    onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, status: e.target.value})}
                  >
                    <option value="émis">Émis</option>
                    <option value="approuvé">Approuvé</option>
                    <option value="remboursé">Remboursé</option>
                    <option value="compensé">Compensé</option>
                    <option value="annulé">Annulé</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.creditNote?.affectsStock || false}
                      onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, affectsStock: e.target.checked})}
                    />
                    Impact sur stock
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.creditNote?.taxDeductible || false}
                      onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, taxDeductible: e.target.checked})}
                    />
                    Déductible fiscalement
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.creditNote?.clientNotified || false}
                      onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, clientNotified: e.target.checked})}
                    />
                    Client notifié
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Motif détaillé</label>
                <textarea
                  value={formData.creditNote?.reason || ''}
                  onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, reason: e.target.value})}
                  placeholder="Détailler le motif de l'émission de l'avoir (retour, défaut, erreur...)"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Conditions de remboursement</label>
                <textarea
                  value={formData.creditNote?.refundConditions || ''}
                  onChange={(e) => handleInputChange('creditNote', {...formData.creditNote, refundConditions: e.target.value})}
                  placeholder="Conditions particulières pour le remboursement ou la compensation"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'account-statement' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Relevé de compte client
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Entreprise (Nom)</label>
                  <input
                    type="text"
                    value={formData.company?.name || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, name: e.target.value})}
                    placeholder="Nom de votre entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Entreprise</label>
                  <input
                    type="text"
                    value={formData.company?.nif || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse entreprise</label>
                  <input
                    type="text"
                    value={formData.company?.address || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, address: e.target.value})}
                    placeholder="Adresse complète de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Client</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Nom du client"
                  />
                </div>
                <div className="form-group">
                  <label>Code client</label>
                  <input
                    type="text"
                    value={formData.client?.code || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, code: e.target.value})}
                    placeholder="Code d'identification client"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Client</label>
                  <input
                    type="text"
                    value={formData.client?.nif || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, nif: e.target.value})}
                    placeholder="NIF du client"
                  />
                </div>
                <div className="form-group">
                  <label>Période du</label>
                  <input
                    type="date"
                    value={formData.period?.startDate || ''}
                    onChange={(e) => handleInputChange('period', {...formData.period, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Période au</label>
                  <input
                    type="date"
                    value={formData.period?.endDate || ''}
                    onChange={(e) => handleInputChange('period', {...formData.period, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Solde d'ouverture (DZD)</label>
                  <input
                    type="number"
                    value={formData.balances?.opening || 0}
                    onChange={(e) => handleInputChange('balances', {...formData.balances, opening: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Total facturé (DZD)</label>
                  <input
                    type="number"
                    value={formData.balances?.totalInvoiced || 0}
                    onChange={(e) => handleInputChange('balances', {...formData.balances, totalInvoiced: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Total payé (DZD)</label>
                  <input
                    type="number"
                    value={formData.balances?.totalPaid || 0}
                    onChange={(e) => handleInputChange('balances', {...formData.balances, totalPaid: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Total avoirs (DZD)</label>
                  <input
                    type="number"
                    value={formData.balances?.totalCredits || 0}
                    onChange={(e) => handleInputChange('balances', {...formData.balances, totalCredits: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Solde de clôture (DZD)</label>
                  <input
                    type="number"
                    value={formData.balances?.closing || 0}
                    onChange={(e) => handleInputChange('balances', {...formData.balances, closing: parseFloat(e.target.value)})}
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Créances échues (DZD)</label>
                  <input
                    type="number"
                    value={formData.aging?.overdue || 0}
                    onChange={(e) => handleInputChange('aging', {...formData.aging, overdue: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Créances à 30 jours (DZD)</label>
                  <input
                    type="number"
                    value={formData.aging?.days30 || 0}
                    onChange={(e) => handleInputChange('aging', {...formData.aging, days30: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Créances à 60 jours (DZD)</label>
                  <input
                    type="number"
                    value={formData.aging?.days60 || 0}
                    onChange={(e) => handleInputChange('aging', {...formData.aging, days60: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Créances à 90+ jours (DZD)</label>
                  <input
                    type="number"
                    value={formData.aging?.days90Plus || 0}
                    onChange={(e) => handleInputChange('aging', {...formData.aging, days90Plus: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Limite de crédit (DZD)</label>
                  <input
                    type="number"
                    value={formData.client?.creditLimit || 0}
                    onChange={(e) => handleInputChange('client', {...formData.client, creditLimit: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <select
                    value={formData.client?.paymentTerms || '30_jours'}
                    onChange={(e) => handleInputChange('client', {...formData.client, paymentTerms: e.target.value})}
                  >
                    <option value="comptant">Comptant</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                    <option value="45_jours">45 jours</option>
                    <option value="60_jours">60 jours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut du compte</label>
                  <select
                    value={formData.client?.accountStatus || 'actif'}
                    onChange={(e) => handleInputChange('client', {...formData.client, accountStatus: e.target.value})}
                  >
                    <option value="actif">Actif</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="bloqué">Bloqué</option>
                    <option value="contentieux">En contentieux</option>
                    <option value="fermé">Fermé</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.client?.creditExceeded || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, creditExceeded: e.target.checked})}
                    />
                    Limite de crédit dépassée
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.client?.paymentDelay || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, paymentDelay: e.target.checked})}
                    />
                    Retards de paiement récurrents
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.client?.legalAction || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, legalAction: e.target.checked})}
                    />
                    Procédure juridique en cours
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations sur le compte</label>
                <textarea
                  value={formData.client?.observations || ''}
                  onChange={(e) => handleInputChange('client', {...formData.client, observations: e.target.value})}
                  placeholder="Remarques particulières sur le comportement de paiement du client"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Actions recommandées</label>
                <textarea
                  value={formData.client?.recommendedActions || ''}
                  onChange={(e) => handleInputChange('client', {...formData.client, recommendedActions: e.target.value})}
                  placeholder="Actions à entreprendre (relance, blocage, contentieux...)"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'advance-invoice' && (
            <div className="form-section">
              <h3 className="section-title">
                <DollarSign size={20} />
                Facture d'acompte
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Vendeur (Nom)</label>
                  <input
                    type="text"
                    value={formData.seller?.name || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, name: e.target.value})}
                    placeholder="Nom de l'entreprise vendeuse"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Vendeur</label>
                  <input
                    type="text"
                    value={formData.seller?.nif || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse vendeur</label>
                  <input
                    type="text"
                    value={formData.seller?.address || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, address: e.target.value})}
                    placeholder="Adresse complète du vendeur"
                  />
                </div>
                <div className="form-group">
                  <label>Acheteur (Client)</label>
                  <input
                    type="text"
                    value={formData.buyer?.name || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, name: e.target.value})}
                    placeholder="Nom du client acheteur"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse acheteur</label>
                  <input
                    type="text"
                    value={formData.buyer?.address || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, address: e.target.value})}
                    placeholder="Adresse de l'acheteur"
                  />
                </div>
                <div className="form-group">
                  <label>NIF Acheteur</label>
                  <input
                    type="text"
                    value={formData.buyer?.nif || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, nif: e.target.value})}
                    placeholder="NIF de l'acheteur"
                  />
                </div>
                <div className="form-group">
                  <label>Contrat/Commande de référence</label>
                  <input
                    type="text"
                    value={formData.advance?.contractReference || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, contractReference: e.target.value})}
                    placeholder="Numéro de contrat ou bon de commande"
                  />
                </div>
                <div className="form-group">
                  <label>Date du contrat</label>
                  <input
                    type="date"
                    value={formData.advance?.contractDate || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, contractDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant total du contrat (DZD)</label>
                  <input
                    type="number"
                    value={formData.advance?.totalContractAmount || 0}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, totalContractAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Pourcentage d'acompte (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.advance?.percentage || 30}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, percentage: parseFloat(e.target.value)})}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label>Montant de l'acompte (DZD)</label>
                  <input
                    type="number"
                    value={formData.advance?.amount || 0}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, amount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>TVA sur acompte (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.advance?.vatRate || 19}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, vatRate: parseFloat(e.target.value)})}
                    placeholder="19"
                  />
                </div>
                <div className="form-group">
                  <label>Montant TVA (DZD)</label>
                  <input
                    type="number"
                    value={formData.advance?.vatAmount || 0}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, vatAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Montant total TTC (DZD)</label>
                  <input
                    type="number"
                    value={formData.advance?.totalAmount || 0}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, totalAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Type d'acompte</label>
                  <select
                    value={formData.advance?.type || 'signature'}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, type: e.target.value})}
                  >
                    <option value="signature">À la signature</option>
                    <option value="commande">À la commande</option>
                    <option value="livraison">Avant livraison</option>
                    <option value="étape">Par étape de réalisation</option>
                    <option value="mensuel">Acompte mensuel</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Échéance de paiement</label>
                  <select
                    value={formData.advance?.paymentTerm || 'immédiat'}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, paymentTerm: e.target.value})}
                  >
                    <option value="immédiat">Immédiat</option>
                    <option value="8_jours">8 jours</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'échéance</label>
                  <input
                    type="date"
                    value={formData.advance?.dueDate || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, dueDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Mode de paiement accepté</label>
                  <select
                    value={formData.advance?.paymentMethod || 'virement'}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, paymentMethod: e.target.value})}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="chèque">Chèque</option>
                    <option value="espèces">Espèces</option>
                    <option value="traite">Traite</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut de l'acompte</label>
                  <select
                    value={formData.advance?.status || 'facturé'}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, status: e.target.value})}
                  >
                    <option value="facturé">Facturé</option>
                    <option value="payé">Payé</option>
                    <option value="en_attente">En attente</option>
                    <option value="en_retard">En retard</option>
                    <option value="annulé">Annulé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de livraison prévue</label>
                  <input
                    type="date"
                    value={formData.advance?.expectedDeliveryDate || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, expectedDeliveryDate: e.target.value})}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.advance?.deductibleFromFinal || true}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, deductibleFromFinal: e.target.checked})}
                    />
                    Déductible de la facture finale
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.advance?.refundableIfCancelled || false}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, refundableIfCancelled: e.target.checked})}
                    />
                    Remboursable en cas d'annulation
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.advance?.guaranteeRequired || false}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, guaranteeRequired: e.target.checked})}
                    />
                    Garantie bancaire requise
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Objet de l'acompte</label>
                <textarea
                  value={formData.advance?.purpose || ''}
                  onChange={(e) => handleInputChange('advance', {...formData.advance, purpose: e.target.value})}
                  placeholder="Détailler l'objet de l'acompte (produits, services, étape de réalisation...)"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Conditions de remboursement</label>
                <textarea
                  value={formData.advance?.refundConditions || ''}
                  onChange={(e) => handleInputChange('advance', {...formData.advance, refundConditions: e.target.value})}
                  placeholder="Conditions de remboursement en cas d'annulation ou de non-exécution"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* OFFRES COMMERCIALES Documents - Specialized Forms */}




          {selectedType.id === 'attachment' && (
          <div className="form-section">
            <h3 className="section-title">
                <Paperclip size={20} />
                Cahier des charges
            </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Type de cahier des charges</label>
                  <select
                    value={formData.attachment?.type || 'technique'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, type: e.target.value})}
                  >
                    <option value="technique">Technique</option>
                    <option value="fonctionnel">Fonctionnel</option>
                    <option value="commercial">Commercial</option>
                    <option value="juridique">Juridique</option>
                    <option value="qualité">Qualité</option>
                    <option value="sécurité">Sécurité</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Domaine d'application</label>
                  <select
                    value={formData.attachment?.domain || 'informatique'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, domain: e.target.value})}
                  >
                    <option value="informatique">Informatique</option>
                    <option value="btp">BTP</option>
                    <option value="industrie">Industrie</option>
                    <option value="énergie">Énergie</option>
                    <option value="transport">Transport</option>
                    <option value="santé">Santé</option>
                    <option value="éducation">Éducation</option>
                    <option value="agriculture">Agriculture</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priorité du projet</label>
                  <select
                    value={formData.attachment?.priority || 'normale'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, priority: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="importante">Importante</option>
                    <option value="urgente">Urgente</option>
                    <option value="critique">Critique</option>
                    <option value="stratégique">Stratégique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Chef de projet</label>
                  <input
                    type="text"
                    value={formData.attachment?.projectManager || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, projectManager: e.target.value})}
                    placeholder="Nom du chef de projet"
                  />
                </div>
                <div className="form-group">
                  <label>Client/Donneur d'ordre</label>
                  <input
                    type="text"
                    value={formData.attachment?.client || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, client: e.target.value})}
                    placeholder="Nom du client"
                  />
                </div>
                <div className="form-group">
                  <label>Budget estimé (DZD)</label>
                  <input
                    type="number"
                    value={formData.attachment?.budget || 0}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, budget: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Date de début prévue</label>
                  <input
                    type="date"
                    value={formData.attachment?.startDate || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin prévue</label>
                  <input
                    type="date"
                    value={formData.attachment?.endDate || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Durée estimée</label>
                  <input
                    type="text"
                    value={formData.attachment?.duration || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, duration: e.target.value})}
                    placeholder="Ex: 6 mois"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre d'intervenants</label>
                  <input
                    type="number"
                    value={formData.attachment?.teamSize || 1}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, teamSize: parseInt(e.target.value)})}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Normes applicables</label>
                  <input
                    type="text"
                    value={formData.attachment?.standards || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, standards: e.target.value})}
                    placeholder="Ex: ISO 9001, NF, DTR..."
                  />
                </div>
                <div className="form-group">
                  <label>Niveau de confidentialité</label>
                  <select
                    value={formData.attachment?.confidentiality || 'normal'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, confidentiality: e.target.value})}
                  >
                    <option value="public">Public</option>
                    <option value="normal">Normal</option>
                    <option value="confidentiel">Confidentiel</option>
                    <option value="secret">Secret</option>
                    <option value="très_secret">Très secret</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut du document</label>
                  <select
                    value={formData.attachment?.status || 'brouillon'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, status: e.target.value})}
                  >
                    <option value="brouillon">Brouillon</option>
                    <option value="en_cours">En cours</option>
                    <option value="en_révision">En révision</option>
                    <option value="validé">Validé</option>
                    <option value="approuvé">Approuvé</option>
                    <option value="archivé">Archivé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Version du document</label>
                  <input
                    type="text"
                    value={formData.attachment?.version || '1.0'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, version: e.target.value})}
                    placeholder="1.0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.requiresApproval || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, requiresApproval: e.target.checked})}
                    />
                    Approbation requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.technicalValidation || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, technicalValidation: e.target.checked})}
                    />
                    Validation technique
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.legalReview || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, legalReview: e.target.checked})}
                    />
                    Révision juridique
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Objectifs du projet</label>
                <textarea
                  value={formData.attachment?.objectives || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, objectives: e.target.value})}
                  placeholder="Décrivez les objectifs principaux du projet"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Contraintes techniques</label>
                <textarea
                  value={formData.attachment?.technicalConstraints || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, technicalConstraints: e.target.value})}
                  placeholder="Contraintes techniques, environnementales, réglementaires..."
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Livrables attendus</label>
                <textarea
                  value={formData.attachment?.deliverables || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, deliverables: e.target.value})}
                  placeholder="Liste des livrables attendus"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Critères d'acceptation</label>
                <textarea
                  value={formData.attachment?.acceptanceCriteria || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, acceptanceCriteria: e.target.value})}
                  placeholder="Critères de validation et d'acceptation"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* LOGISTIQUE Documents */}
          {selectedType.id === 'delivery-note' && (
            <div className="form-section">
              <h3 className="section-title">
                <Truck size={20} />
                Bon de livraison
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Expéditeur (Nom)</label>
                  <input
                    type="text"
                    value={formData.sender?.name || ''}
                    onChange={(e) => handleInputChange('sender', {...formData.sender, name: e.target.value})}
                    placeholder="Nom de l'entreprise expéditrice"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse expéditeur</label>
                  <input
                    type="text"
                    value={formData.sender?.address || ''}
                    onChange={(e) => handleInputChange('sender', {...formData.sender, address: e.target.value})}
                    placeholder="Adresse complète de l'expéditeur"
                  />
                </div>
                <div className="form-group">
                  <label>Destinataire</label>
                  <input
                    type="text"
                    value={formData.recipient?.name || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, name: e.target.value})}
                    placeholder="Nom du destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse de livraison</label>
                  <input
                    type="text"
                    value={formData.recipient?.address || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, address: e.target.value})}
                    placeholder="Adresse de livraison"
                  />
                </div>
                <div className="form-group">
                  <label>Bon de commande</label>
                  <input
                    type="text"
                    value={formData.delivery?.orderReference || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, orderReference: e.target.value})}
                    placeholder="Référence du bon de commande"
                  />
                </div>
                <div className="form-group">
                  <label>Date de livraison</label>
                  <input
                    type="date"
                    value={formData.delivery?.date || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de livraison</label>
                  <input
                    type="time"
                    value={formData.delivery?.time || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, time: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Transporteur</label>
                  <input
                    type="text"
                    value={formData.delivery?.carrier || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, carrier: e.target.value})}
                    placeholder="Nom du transporteur"
                  />
                </div>
                <div className="form-group">
                  <label>Chauffeur</label>
                  <input
                    type="text"
                    value={formData.delivery?.driver || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, driver: e.target.value})}
                    placeholder="Nom du chauffeur"
                  />
                </div>
                <div className="form-group">
                  <label>Véhicule (Immatriculation)</label>
                  <input
                    type="text"
                    value={formData.delivery?.vehicleRegistration || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, vehicleRegistration: e.target.value})}
                    placeholder="Numéro d'immatriculation"
                  />
                </div>
                <div className="form-group">
                  <label>Type de véhicule</label>
                  <select
                    value={formData.delivery?.vehicleType || 'camion'}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, vehicleType: e.target.value})}
                  >
                    <option value="camion">Camion</option>
                    <option value="camionnette">Camionnette</option>
                    <option value="fourgon">Fourgon</option>
                    <option value="remorque">Remorque</option>
                    <option value="semi_remorque">Semi-remorque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conditions de livraison</label>
                  <select
                    value={formData.delivery?.conditions || 'franco'}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, conditions: e.target.value})}
                  >
                    <option value="franco">Franco (livré)</option>
                    <option value="départ_usine">Départ usine</option>
                    <option value="port_dû">Port dû</option>
                    <option value="port_payé">Port payé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut de livraison</label>
                  <select
                    value={formData.delivery?.status || 'en_cours'}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, status: e.target.value})}
                  >
                    <option value="en_cours">En cours</option>
                    <option value="livrée">Livrée</option>
                    <option value="partiellement_livrée">Partiellement livrée</option>
                    <option value="refusée">Refusée</option>
                    <option value="en_attente">En attente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reçu par</label>
                  <input
                    type="text"
                    value={formData.delivery?.receivedBy || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, receivedBy: e.target.value})}
                    placeholder="Nom de la personne qui reçoit"
                  />
                </div>
                <div className="form-group">
                  <label>Signature date</label>
                  <input
                    type="date"
                    value={formData.delivery?.signatureDate || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, signatureDate: e.target.value})}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.fragile || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, fragile: e.target.checked})}
                    />
                    Marchandises fragiles
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.refrigerated || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, refrigerated: e.target.checked})}
                    />
                    Transport frigorifique
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.hazardous || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, hazardous: e.target.checked})}
                    />
                    Matières dangereuses
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations de livraison</label>
                <textarea
                  value={formData.delivery?.observations || ''}
                  onChange={(e) => handleInputChange('delivery', {...formData.delivery, observations: e.target.value})}
                  placeholder="Remarques particulières sur la livraison"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'shipping-slip' && (
            <div className="form-section">
              <h3 className="section-title">
                <Send size={20} />
                Bordereau d'envoi
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Expéditeur</label>
                  <input
                    type="text"
                    value={formData.sender?.name || ''}
                    onChange={(e) => handleInputChange('sender', {...formData.sender, name: e.target.value})}
                    placeholder="Nom de l'expéditeur"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse expéditeur</label>
                  <input
                    type="text"
                    value={formData.sender?.address || ''}
                    onChange={(e) => handleInputChange('sender', {...formData.sender, address: e.target.value})}
                    placeholder="Adresse de l'expéditeur"
                  />
                </div>
                <div className="form-group">
                  <label>Destinataire</label>
                  <input
                    type="text"
                    value={formData.recipient?.name || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, name: e.target.value})}
                    placeholder="Nom du destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse destinataire</label>
                  <input
                    type="text"
                    value={formData.recipient?.address || ''}
                    onChange={(e) => handleInputChange('recipient', {...formData.recipient, address: e.target.value})}
                    placeholder="Adresse du destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Date d'expédition</label>
                  <input
                    type="date"
                    value={formData.shipment?.date || ''}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Mode d'expédition</label>
                  <select
                    value={formData.shipment?.method || 'route'}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, method: e.target.value})}
                  >
                    <option value="route">Route</option>
                    <option value="rail">Rail</option>
                    <option value="aérien">Aérien</option>
                    <option value="maritime">Maritime</option>
                    <option value="poste">Poste</option>
                    <option value="coursier">Coursier</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Transporteur</label>
                  <input
                    type="text"
                    value={formData.shipment?.carrier || ''}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, carrier: e.target.value})}
                    placeholder="Nom du transporteur"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de suivi</label>
                  <input
                    type="text"
                    value={formData.shipment?.trackingNumber || ''}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, trackingNumber: e.target.value})}
                    placeholder="Numéro de suivi"
                  />
                </div>
                <div className="form-group">
                  <label>Type de colis</label>
                  <select
                    value={formData.shipment?.packageType || 'carton'}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, packageType: e.target.value})}
                  >
                    <option value="carton">Carton</option>
                    <option value="palette">Palette</option>
                    <option value="caisse">Caisse</option>
                    <option value="sac">Sac</option>
                    <option value="conteneur">Conteneur</option>
                    <option value="vrac">Vrac</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nombre de colis</label>
                  <input
                    type="number"
                    value={formData.shipment?.packageCount || 1}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, packageCount: parseInt(e.target.value)})}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Poids total (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.shipment?.totalWeight || 0}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, totalWeight: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Volume (m³)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.shipment?.volume || 0}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, volume: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Valeur déclarée (DZD)</label>
                  <input
                    type="number"
                    value={formData.shipment?.declaredValue || 0}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, declaredValue: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Priorité</label>
                  <select
                    value={formData.shipment?.priority || 'normale'}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, priority: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="express">Express</option>
                    <option value="économique">Économique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Assurance</label>
                  <select
                    value={formData.shipment?.insurance || 'non'}
                    onChange={(e) => handleInputChange('shipment', {...formData.shipment, insurance: e.target.value})}
                  >
                    <option value="non">Non assurée</option>
                    <option value="standard">Assurance standard</option>
                    <option value="renforcée">Assurance renforcée</option>
                    <option value="tous_risques">Tous risques</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.shipment?.signatureRequired || false}
                      onChange={(e) => handleInputChange('shipment', {...formData.shipment, signatureRequired: e.target.checked})}
                    />
                    Signature requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.shipment?.fragile || false}
                      onChange={(e) => handleInputChange('shipment', {...formData.shipment, fragile: e.target.checked})}
                    />
                    Fragile
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.shipment?.returnable || false}
                      onChange={(e) => handleInputChange('shipment', {...formData.shipment, returnable: e.target.checked})}
                    />
                    Retour possible
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Instructions spéciales</label>
                <textarea
                  value={formData.shipment?.instructions || ''}
                  onChange={(e) => handleInputChange('shipment', {...formData.shipment, instructions: e.target.value})}
                  placeholder="Instructions particulières pour l'expédition"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'reception-note' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Bon de réception
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Réceptionnaire</label>
                  <input
                    type="text"
                    value={formData.receiver?.name || ''}
                    onChange={(e) => handleInputChange('receiver', {...formData.receiver, name: e.target.value})}
                    placeholder="Nom de l'entreprise réceptrice"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse de réception</label>
                  <input
                    type="text"
                    value={formData.receiver?.address || ''}
                    onChange={(e) => handleInputChange('receiver', {...formData.receiver, address: e.target.value})}
                    placeholder="Adresse de réception"
                  />
                </div>
                <div className="form-group">
                  <label>Fournisseur</label>
                  <input
                    type="text"
                    value={formData.supplier?.name || ''}
                    onChange={(e) => handleInputChange('supplier', {...formData.supplier, name: e.target.value})}
                    placeholder="Nom du fournisseur"
                  />
                </div>
                <div className="form-group">
                  <label>Bon de livraison</label>
                  <input
                    type="text"
                    value={formData.reception?.deliveryNoteRef || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, deliveryNoteRef: e.target.value})}
                    placeholder="Référence du bon de livraison"
                  />
                </div>
                <div className="form-group">
                  <label>Bon de commande</label>
                  <input
                    type="text"
                    value={formData.reception?.orderRef || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, orderRef: e.target.value})}
                    placeholder="Référence du bon de commande"
                  />
                </div>
                <div className="form-group">
                  <label>Date de réception</label>
                  <input
                    type="date"
                    value={formData.reception?.date || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de réception</label>
                  <input
                    type="time"
                    value={formData.reception?.time || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, time: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Réceptionné par</label>
                  <input
                    type="text"
                    value={formData.reception?.receivedBy || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, receivedBy: e.target.value})}
                    placeholder="Nom de la personne qui réceptionne"
                  />
                </div>
                <div className="form-group">
                  <label>Contrôlé par</label>
                  <input
                    type="text"
                    value={formData.reception?.controlledBy || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, controlledBy: e.target.value})}
                    placeholder="Nom du contrôleur qualité"
                  />
                </div>
                <div className="form-group">
                  <label>État général</label>
                  <select
                    value={formData.reception?.overallCondition || 'bon'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, overallCondition: e.target.value})}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="bon">Bon</option>
                    <option value="satisfaisant">Satisfaisant</option>
                    <option value="défectueux">Défectueux</option>
                    <option value="endommagé">Endommagé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conformité quantité</label>
                  <select
                    value={formData.reception?.quantityCompliance || 'conforme'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, quantityCompliance: e.target.value})}
                  >
                    <option value="conforme">Conforme</option>
                    <option value="surplus">Surplus</option>
                    <option value="manquant">Manquant</option>
                    <option value="partiel">Livraison partielle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conformité qualité</label>
                  <select
                    value={formData.reception?.qualityCompliance || 'conforme'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, qualityCompliance: e.target.value})}
                  >
                    <option value="conforme">Conforme</option>
                    <option value="non_conforme">Non conforme</option>
                    <option value="à_vérifier">À vérifier</option>
                    <option value="avec_réserves">Avec réserves</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Destination stock</label>
                  <select
                    value={formData.reception?.stockDestination || 'entrepôt_principal'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, stockDestination: e.target.value})}
                  >
                    <option value="entrepôt_principal">Entrepôt principal</option>
                    <option value="entrepôt_secondaire">Entrepôt secondaire</option>
                    <option value="quarantaine">Quarantaine</option>
                    <option value="retour_fournisseur">Retour fournisseur</option>
                    <option value="mise_au_rebut">Mise au rebut</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut de réception</label>
                  <select
                    value={formData.reception?.status || 'acceptée'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, status: e.target.value})}
                  >
                    <option value="acceptée">Acceptée</option>
                    <option value="acceptée_avec_réserves">Acceptée avec réserves</option>
                    <option value="refusée">Refusée</option>
                    <option value="en_attente">En attente de contrôle</option>
                    <option value="retournée">Retournée au fournisseur</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.documentsComplete || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, documentsComplete: e.target.checked})}
                    />
                    Documents complets
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.packagingIntact || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, packagingIntact: e.target.checked})}
                    />
                    Emballage intact
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.urgentProcessing || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, urgentProcessing: e.target.checked})}
                    />
                    Traitement urgent
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations de réception</label>
                <textarea
                  value={formData.reception?.observations || ''}
                  onChange={(e) => handleInputChange('reception', {...formData.reception, observations: e.target.value})}
                  placeholder="Remarques sur l'état des marchandises reçues"
                  rows="3"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'return-slip' && (
            <div className="form-section">
              <h3 className="section-title">
                <ArrowLeft size={20} />
                Bordereau de retour
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Fournisseur destinataire</label>
                  <input
                    type="text"
                    value={formData.supplier?.name || ''}
                    onChange={(e) => handleInputChange('supplier', {...formData.supplier, name: e.target.value})}
                    placeholder="Nom du fournisseur destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Livraison d'origine</label>
                  <input
                    type="text"
                    value={formData.return?.originalDeliveryRef || ''}
                    onChange={(e) => handleInputChange('return', {...formData.return, originalDeliveryRef: e.target.value})}
                    placeholder="Référence livraison d'origine"
                  />
                </div>
                <div className="form-group">
                  <label>Facture d'origine</label>
                  <input
                    type="text"
                    value={formData.return?.originalInvoiceRef || ''}
                    onChange={(e) => handleInputChange('return', {...formData.return, originalInvoiceRef: e.target.value})}
                    placeholder="Référence facture d'origine"
                  />
                </div>
                <div className="form-group">
                  <label>Date de retour</label>
                  <input
                    type="date"
                    value={formData.return?.date || ''}
                    onChange={(e) => handleInputChange('return', {...formData.return, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Motif de retour</label>
                  <select
                    value={formData.return?.reason || 'défectueux'}
                    onChange={(e) => handleInputChange('return', {...formData.return, reason: e.target.value})}
                  >
                    <option value="défectueux">Produit défectueux</option>
                    <option value="non_conforme">Non conforme</option>
                    <option value="erreur_livraison">Erreur de livraison</option>
                    <option value="endommagé">Endommagé transport</option>
                    <option value="fin_contrat">Fin de contrat</option>
                    <option value="changement_besoin">Changement de besoin</option>
                    <option value="garantie">Sous garantie</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Type de retour</label>
                  <select
                    value={formData.return?.type || 'échange'}
                    onChange={(e) => handleInputChange('return', {...formData.return, type: e.target.value})}
                  >
                    <option value="échange">Échange</option>
                    <option value="remboursement">Remboursement</option>
                    <option value="réparation">Réparation</option>
                    <option value="avoir">Avoir</option>
                    <option value="destruction">Destruction</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Transporteur retour</label>
                  <input
                    type="text"
                    value={formData.return?.carrier || ''}
                    onChange={(e) => handleInputChange('return', {...formData.return, carrier: e.target.value})}
                    placeholder="Nom du transporteur"
                  />
                </div>
                <div className="form-group">
                  <label>État des marchandises</label>
                  <select
                    value={formData.return?.condition || 'emballage_original'}
                    onChange={(e) => handleInputChange('return', {...formData.return, condition: e.target.value})}
                  >
                    <option value="emballage_original">Emballage d'origine</option>
                    <option value="bon_état">Bon état</option>
                    <option value="état_moyen">État moyen</option>
                    <option value="endommagé">Endommagé</option>
                    <option value="inutilisable">Inutilisable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Contrôlé par</label>
                  <input
                    type="text"
                    value={formData.return?.inspector || ''}
                    onChange={(e) => handleInputChange('return', {...formData.return, inspector: e.target.value})}
                    placeholder="Nom de l'inspecteur"
                  />
                </div>
                <div className="form-group">
                  <label>Coût de retour (DZD)</label>
                  <input
                    type="number"
                    value={formData.return?.cost || 0}
                    onChange={(e) => handleInputChange('return', {...formData.return, cost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Frais de transport (DZD)</label>
                  <input
                    type="number"
                    value={formData.return?.transportCost || 0}
                    onChange={(e) => handleInputChange('return', {...formData.return, transportCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Statut du retour</label>
                  <select
                    value={formData.return?.status || 'en_cours'}
                    onChange={(e) => handleInputChange('return', {...formData.return, status: e.target.value})}
                  >
                    <option value="en_cours">En cours</option>
                    <option value="accepté">Accepté</option>
                    <option value="refusé">Refusé</option>
                    <option value="traité">Traité</option>
                    <option value="remboursé">Remboursé</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.return?.qualityCheck || false}
                      onChange={(e) => handleInputChange('return', {...formData.return, qualityCheck: e.target.checked})}
                    />
                    Contrôle qualité effectué
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.return?.restockable || false}
                      onChange={(e) => handleInputChange('return', {...formData.return, restockable: e.target.checked})}
                    />
                    Remise en stock possible
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.return?.creditNote || false}
                      onChange={(e) => handleInputChange('return', {...formData.return, creditNote: e.target.checked})}
                    />
                    Avoir à émettre
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Détail du problème</label>
                <textarea
                  value={formData.return?.problemDescription || ''}
                  onChange={(e) => handleInputChange('return', {...formData.return, problemDescription: e.target.value})}
                  placeholder="Description détaillée du problème constaté"
                  rows="3"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'material-exit' && (
            <div className="form-section">
              <h3 className="section-title">
                <ArrowRight size={20} />
                Bon de sortie matériel
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Entrepôt/Magasin</label>
                  <input
                    type="text"
                    value={formData.warehouse?.name || ''}
                    onChange={(e) => handleInputChange('warehouse', {...formData.warehouse, name: e.target.value})}
                    placeholder="Nom de l'entrepôt"
                  />
                </div>
                <div className="form-group">
                  <label>Emplacement</label>
                  <input
                    type="text"
                    value={formData.warehouse?.location || ''}
                    onChange={(e) => handleInputChange('warehouse', {...formData.warehouse, location: e.target.value})}
                    placeholder="Zone/Allée/Rayon"
                  />
                </div>
                <div className="form-group">
                  <label>Responsable magasin</label>
                  <input
                    type="text"
                    value={formData.warehouse?.manager || ''}
                    onChange={(e) => handleInputChange('warehouse', {...formData.warehouse, manager: e.target.value})}
                    placeholder="Nom du responsable"
                  />
                </div>
                <div className="form-group">
                  <label>Référence produit</label>
                  <input
                    type="text"
                    value={formData.product?.reference || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, reference: e.target.value})}
                    placeholder="Code/Référence produit"
                  />
                </div>
                <div className="form-group">
                  <label>Désignation produit</label>
                  <input
                    type="text"
                    value={formData.product?.name || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, name: e.target.value})}
                    placeholder="Nom du produit"
                  />
                </div>
                <div className="form-group">
                  <label>Catégorie produit</label>
                  <select
                    value={formData.product?.category || 'marchandise'}
                    onChange={(e) => handleInputChange('product', {...formData.product, category: e.target.value})}
                  >
                    <option value="marchandise">Marchandise</option>
                    <option value="matière_première">Matière première</option>
                    <option value="produit_fini">Produit fini</option>
                    <option value="composant">Composant</option>
                    <option value="consommable">Consommable</option>
                    <option value="outillage">Outillage</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantité sortie</label>
                  <input
                    type="number"
                    value={formData.materialExit?.quantity || 0}
                    onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, quantity: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Unité</label>
                  <select
                    value={formData.materialExit?.unit || 'unité'}
                    onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, unit: e.target.value})}
                  >
                    <option value="unité">Unité</option>
                    <option value="kg">Kilogramme</option>
                    <option value="litre">Litre</option>
                    <option value="mètre">Mètre</option>
                    <option value="m²">Mètre carré</option>
                    <option value="m³">Mètre cube</option>
                    <option value="palette">Palette</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Numéro de lot</label>
                  <input
                    type="text"
                    value={formData.product?.batchNumber || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, batchNumber: e.target.value})}
                    placeholder="Numéro de lot/série"
                  />
                </div>
                <div className="form-group">
                  <label>Date d'expiration</label>
                  <input
                    type="date"
                    value={formData.product?.expirationDate || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, expirationDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Document de référence</label>
                  <input
                    type="text"
                    value={formData.materialExit?.documentRef || ''}
                    onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, documentRef: e.target.value})}
                    placeholder="BL, BC, Facture..."
                  />
                </div>
                <div className="form-group">
                  <label>Opérateur</label>
                  <input
                    type="text"
                    value={formData.materialExit?.operator || ''}
                    onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, operator: e.target.value})}
                    placeholder="Nom de l'opérateur"
                  />
                </div>
                <div className="form-group">
                  <label>Validé par</label>
                  <input
                    type="text"
                    value={formData.materialExit?.approvedBy || ''}
                    onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, approvedBy: e.target.value})}
                    placeholder="Nom du validateur"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations</label>
                <textarea
                  value={formData.materialExit?.observations || ''}
                  onChange={(e) => handleInputChange('materialExit', {...formData.materialExit, observations: e.target.value})}
                  placeholder="Remarques sur le mouvement de stock"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'material-exit' && (
            <div className="form-section">
              <h3 className="section-title">
                <ArrowRight size={20} />
                Bon de sortie matériel
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Demandeur</label>
                  <input
                    type="text"
                    value={formData.requester?.name || ''}
                    onChange={(e) => handleInputChange('requester', {...formData.requester, name: e.target.value})}
                    placeholder="Nom du demandeur"
                  />
                </div>
                <div className="form-group">
                  <label>Service/Département</label>
                  <input
                    type="text"
                    value={formData.requester?.department || ''}
                    onChange={(e) => handleInputChange('requester', {...formData.requester, department: e.target.value})}
                    placeholder="Service demandeur"
                  />
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={formData.requester?.position || ''}
                    onChange={(e) => handleInputChange('requester', {...formData.requester, position: e.target.value})}
                    placeholder="Fonction du demandeur"
                  />
                </div>
                <div className="form-group">
                  <label>Projet/Chantier</label>
                  <input
                    type="text"
                    value={formData.exit?.project || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, project: e.target.value})}
                    placeholder="Nom du projet"
                  />
                </div>
                <div className="form-group">
                  <label>Date de sortie</label>
                  <input
                    type="date"
                    value={formData.exit?.date || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Objet de la sortie</label>
                  <select
                    value={formData.exit?.purpose || 'utilisation'}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, purpose: e.target.value})}
                  >
                    <option value="utilisation">Utilisation</option>
                    <option value="prêt">Prêt</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="réparation">Réparation</option>
                    <option value="test">Test/Essai</option>
                    <option value="formation">Formation</option>
                    <option value="démonstration">Démonstration</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Entrepôt de sortie</label>
                  <input
                    type="text"
                    value={formData.exit?.warehouse || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, warehouse: e.target.value})}
                    placeholder="Nom de l'entrepôt"
                  />
                </div>
                <div className="form-group">
                  <label>Magasinier</label>
                  <input
                    type="text"
                    value={formData.exit?.warehouseKeeper || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, warehouseKeeper: e.target.value})}
                    placeholder="Nom du magasinier"
                  />
                </div>
                <div className="form-group">
                  <label>Autorisé par</label>
                  <input
                    type="text"
                    value={formData.exit?.approvedBy || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, approvedBy: e.target.value})}
                    placeholder="Nom de l'autorisant"
                  />
                </div>
                <div className="form-group">
                  <label>Urgence</label>
                  <select
                    value={formData.exit?.urgency || 'normale'}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, urgency: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="très_urgente">Très urgente</option>
                    <option value="programmée">Programmée</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Destination</label>
                  <input
                    type="text"
                    value={formData.exit?.destination || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, destination: e.target.value})}
                    placeholder="Lieu de destination"
                  />
                </div>
                <div className="form-group">
                  <label>Ordre de travail</label>
                  <input
                    type="text"
                    value={formData.exit?.workOrder || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, workOrder: e.target.value})}
                    placeholder="Numéro d'ordre de travail"
                  />
                </div>
                <div className="form-group">
                  <label>Centre de coût</label>
                  <input
                    type="text"
                    value={formData.exit?.costCenter || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, costCenter: e.target.value})}
                    placeholder="Code centre de coût"
                  />
                </div>
                <div className="form-group">
                  <label>Date de retour prévue</label>
                  <input
                    type="date"
                    value={formData.exit?.expectedReturnDate || ''}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, expectedReturnDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Statut de sortie</label>
                  <select
                    value={formData.exit?.status || 'en_cours'}
                    onChange={(e) => handleInputChange('exit', {...formData.exit, status: e.target.value})}
                  >
                    <option value="en_cours">En cours</option>
                    <option value="sortie">Sortie</option>
                    <option value="retournée">Retournée</option>
                    <option value="perdue">Perdue</option>
                    <option value="consommée">Consommée</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.exit?.returnExpected || false}
                      onChange={(e) => handleInputChange('exit', {...formData.exit, returnExpected: e.target.checked})}
                    />
                    Retour attendu
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.exit?.conditionCheck || false}
                      onChange={(e) => handleInputChange('exit', {...formData.exit, conditionCheck: e.target.checked})}
                    />
                    Vérification état effectuée
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.exit?.insuranceRequired || false}
                      onChange={(e) => handleInputChange('exit', {...formData.exit, insuranceRequired: e.target.checked})}
                    />
                    Assurance requise
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Justification de la demande</label>
                <textarea
                  value={formData.exit?.justification || ''}
                  onChange={(e) => handleInputChange('exit', {...formData.exit, justification: e.target.value})}
                  placeholder="Motif et justification de la sortie de matériel"
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* PROJETS & TRAVAUX Documents */}
          {selectedType.id === 'reception-report' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Procès-verbal de réception
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Maître d'ouvrage</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Nom du maître d'ouvrage"
                  />
                </div>
                <div className="form-group">
                  <label>Représentant client</label>
                  <input
                    type="text"
                    value={formData.client?.representative || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, representative: e.target.value})}
                    placeholder="Nom du représentant"
                  />
                </div>
                <div className="form-group">
                  <label>Entreprise exécutante</label>
                  <input
                    type="text"
                    value={formData.contractor?.name || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, name: e.target.value})}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Responsable travaux</label>
                  <input
                    type="text"
                    value={formData.contractor?.supervisor || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, supervisor: e.target.value})}
                    placeholder="Chef de chantier/Responsable"
                  />
                </div>
                <div className="form-group">
                  <label>Bureau de contrôle</label>
                  <input
                    type="text"
                    value={formData.supervision?.controlOffice || ''}
                    onChange={(e) => handleInputChange('supervision', {...formData.supervision, controlOffice: e.target.value})}
                    placeholder="Nom du bureau de contrôle"
                  />
                </div>
                <div className="form-group">
                  <label>Contrôleur technique</label>
                  <input
                    type="text"
                    value={formData.supervision?.technicalController || ''}
                    onChange={(e) => handleInputChange('supervision', {...formData.supervision, technicalController: e.target.value})}
                    placeholder="Nom du contrôleur"
                  />
                </div>
                <div className="form-group">
                  <label>Référence du marché</label>
                  <input
                    type="text"
                    value={formData.project?.contractReference || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, contractReference: e.target.value})}
                    placeholder="Numéro/référence du contrat"
                  />
                </div>
                <div className="form-group">
                  <label>Nature des travaux</label>
                  <select
                    value={formData.project?.workType || 'construction'}
                    onChange={(e) => handleInputChange('project', {...formData.project, workType: e.target.value})}
                  >
                    <option value="construction">Construction</option>
                    <option value="rénovation">Rénovation</option>
                    <option value="aménagement">Aménagement</option>
                    <option value="infrastructure">Infrastructure</option>
                    <option value="équipement">Équipement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Localisation</label>
                  <input
                    type="text"
                    value={formData.project?.location || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, location: e.target.value})}
                    placeholder="Adresse du chantier"
                  />
                </div>
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.project?.startDate || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin prévue</label>
                  <input
                    type="date"
                    value={formData.project?.plannedEndDate || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, plannedEndDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin réelle</label>
                  <input
                    type="date"
                    value={formData.project?.actualEndDate || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, actualEndDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant des travaux (DZD)</label>
                  <input
                    type="number"
                    value={formData.project?.contractAmount || 0}
                    onChange={(e) => handleInputChange('project', {...formData.project, contractAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Type de réception</label>
                  <select
                    value={formData.reception?.type || 'provisoire'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, type: e.target.value})}
                  >
                    <option value="provisoire">Réception provisoire</option>
                    <option value="définitive">Réception définitive</option>
                    <option value="partielle">Réception partielle</option>
                    <option value="avec_réserves">Avec réserves</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de réception</label>
                  <input
                    type="date"
                    value={formData.reception?.date || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Conformité globale</label>
                  <select
                    value={formData.reception?.overallCompliance || 'conforme'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, overallCompliance: e.target.value})}
                  >
                    <option value="conforme">Conforme</option>
                    <option value="conforme_avec_réserves">Conforme avec réserves</option>
                    <option value="non_conforme">Non conforme</option>
                    <option value="en_attente">En attente de correction</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Qualité d'exécution</label>
                  <select
                    value={formData.reception?.executionQuality || 'satisfaisante'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, executionQuality: e.target.value})}
                  >
                    <option value="excellente">Excellente</option>
                    <option value="très_bonne">Très bonne</option>
                    <option value="bonne">Bonne</option>
                    <option value="satisfaisante">Satisfaisante</option>
                    <option value="insuffisante">Insuffisante</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Respect des délais</label>
                  <select
                    value={formData.reception?.timelineCompliance || 'respecté'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, timelineCompliance: e.target.value})}
                  >
                    <option value="respecté">Respecté</option>
                    <option value="retard_mineur">Retard mineur</option>
                    <option value="retard_important">Retard important</option>
                    <option value="avance">En avance</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nombre de réserves</label>
                  <input
                    type="number"
                    value={formData.reception?.reservationCount || 0}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, reservationCount: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Délai de levée des réserves</label>
                  <input
                    type="number"
                    value={formData.reception?.reservationDeadline || 30}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, reservationDeadline: parseInt(e.target.value)})}
                    placeholder="30 jours"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.documentsProvided || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, documentsProvided: e.target.checked})}
                    />
                    Dossier technique fourni
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.warrantyActivated || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, warrantyActivated: e.target.checked})}
                    />
                    Garantie activée
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.reception?.maintenanceHandover || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, maintenanceHandover: e.target.checked})}
                    />
                    Passation maintenance effectuée
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Observations générales</label>
                <textarea
                  value={formData.reception?.observations || ''}
                  onChange={(e) => handleInputChange('reception', {...formData.reception, observations: e.target.value})}
                  placeholder="Observations et remarques sur la réception des travaux"
                  rows="3"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'intervention-report' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Rapport d'intervention
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Technicien intervenant</label>
                  <input
                    type="text"
                    value={formData.technician?.name || ''}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, name: e.target.value})}
                    placeholder="Nom du technicien"
                  />
                </div>
                <div className="form-group">
                  <label>Qualification</label>
                  <select
                    value={formData.technician?.qualification || 'technicien'}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, qualification: e.target.value})}
                  >
                    <option value="technicien">Technicien</option>
                    <option value="technicien_supérieur">Technicien supérieur</option>
                    <option value="ingénieur">Ingénieur</option>
                    <option value="expert">Expert</option>
                    <option value="spécialiste">Spécialiste</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    value={formData.technician?.company || ''}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, company: e.target.value})}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Client/Site</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Nom du client ou site"
                  />
                </div>
                <div className="form-group">
                  <label>Contact client</label>
                  <input
                    type="text"
                    value={formData.client?.contact || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, contact: e.target.value})}
                    placeholder="Personne de contact"
                  />
                </div>
                <div className="form-group">
                  <label>Lieu d'intervention</label>
                  <input
                    type="text"
                    value={formData.intervention?.location || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, location: e.target.value})}
                    placeholder="Adresse précise"
                  />
                </div>
                <div className="form-group">
                  <label>Date d'intervention</label>
                  <input
                    type="date"
                    value={formData.intervention?.date || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de début</label>
                  <input
                    type="time"
                    value={formData.intervention?.startTime || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, startTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de fin</label>
                  <input
                    type="time"
                    value={formData.intervention?.endTime || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, endTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Type d'intervention</label>
                  <select
                    value={formData.intervention?.type || 'maintenance'}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, type: e.target.value})}
                  >
                    <option value="maintenance">Maintenance préventive</option>
                    <option value="dépannage">Dépannage</option>
                    <option value="installation">Installation</option>
                    <option value="diagnostic">Diagnostic</option>
                    <option value="mise_en_service">Mise en service</option>
                    <option value="contrôle">Contrôle technique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Urgence</label>
                  <select
                    value={formData.intervention?.urgency || 'normale'}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, urgency: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="critique">Critique</option>
                    <option value="planifiée">Planifiée</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Équipement concerné</label>
                  <input
                    type="text"
                    value={formData.equipment?.name || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, name: e.target.value})}
                    placeholder="Nom/type de l'équipement"
                  />
                </div>
                <div className="form-group">
                  <label>Référence/Série</label>
                  <input
                    type="text"
                    value={formData.equipment?.reference || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, reference: e.target.value})}
                    placeholder="Numéro de série ou référence"
                  />
                </div>
                <div className="form-group">
                  <label>Marque/Modèle</label>
                  <input
                    type="text"
                    value={formData.equipment?.brand || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, brand: e.target.value})}
                    placeholder="Marque et modèle"
                  />
                </div>
                <div className="form-group">
                  <label>État initial</label>
                  <select
                    value={formData.equipment?.initialState || 'fonctionnel'}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, initialState: e.target.value})}
                  >
                    <option value="fonctionnel">Fonctionnel</option>
                    <option value="défaillant">Défaillant</option>
                    <option value="arrêté">Arrêté</option>
                    <option value="dégradé">Fonctionnement dégradé</option>
                    <option value="neuf">Neuf (installation)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>État final</label>
                  <select
                    value={formData.equipment?.finalState || 'fonctionnel'}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, finalState: e.target.value})}
                  >
                    <option value="fonctionnel">Fonctionnel</option>
                    <option value="réparé">Réparé</option>
                    <option value="installé">Installé</option>
                    <option value="en_attente">En attente pièces</option>
                    <option value="non_réparable">Non réparable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Temps d'intervention (h)</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.intervention?.duration || 0}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, duration: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Coût intervention (DZD)</label>
                  <input
                    type="number"
                    value={formData.intervention?.cost || 0}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, cost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.partsUsed || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, partsUsed: e.target.checked})}
                    />
                    Pièces utilisées
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.clientSatisfied || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, clientSatisfied: e.target.checked})}
                    />
                    Client satisfait
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.followUpRequired || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, followUpRequired: e.target.checked})}
                    />
                    Suivi requis
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Description du problème</label>
                <textarea
                  value={formData.intervention?.problemDescription || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, problemDescription: e.target.value})}
                  placeholder="Description détaillée du problème rencontré"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Travaux effectués</label>
                <textarea
                  value={formData.intervention?.workPerformed || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, workPerformed: e.target.value})}
                  placeholder="Description détaillée des travaux et réparations effectués"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Recommandations</label>
                <textarea
                  value={formData.intervention?.recommendations || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, recommendations: e.target.value})}
                  placeholder="Recommandations pour la maintenance future"
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'non-compliance' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Fiche de non-conformité
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Détecteur</label>
                  <input
                    type="text"
                    value={formData.detector?.name || ''}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, name: e.target.value})}
                    placeholder="Nom de la personne qui a détecté"
                  />
                </div>
                <div className="form-group">
                  <label>Fonction du détecteur</label>
                  <select
                    value={formData.detector?.role || 'contrôleur'}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, role: e.target.value})}
                  >
                    <option value="contrôleur">Contrôleur qualité</option>
                    <option value="superviseur">Superviseur</option>
                    <option value="technicien">Technicien</option>
                    <option value="client">Client</option>
                    <option value="auditeur">Auditeur</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Service/Département</label>
                  <input
                    type="text"
                    value={formData.detector?.department || ''}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, department: e.target.value})}
                    placeholder="Service concerné"
                  />
                </div>
                <div className="form-group">
                  <label>Date de détection</label>
                  <input
                    type="date"
                    value={formData.nonCompliance?.detectionDate || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, detectionDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Lieu de détection</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.location || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, location: e.target.value})}
                    placeholder="Zone/atelier/chantier"
                  />
                </div>
                <div className="form-group">
                  <label>Type de non-conformité</label>
                  <select
                    value={formData.nonCompliance?.type || 'qualité'}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, type: e.target.value})}
                  >
                    <option value="qualité">Qualité produit</option>
                    <option value="processus">Processus</option>
                    <option value="documentation">Documentation</option>
                    <option value="sécurité">Sécurité</option>
                    <option value="environnement">Environnement</option>
                    <option value="livraison">Livraison</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.nonCompliance?.category || 'mineure'}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, category: e.target.value})}
                  >
                    <option value="critique">Critique</option>
                    <option value="majeure">Majeure</option>
                    <option value="mineure">Mineure</option>
                    <option value="observation">Observation</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Criticité</label>
                  <select
                    value={formData.nonCompliance?.severity || 'moyenne'}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, severity: e.target.value})}
                  >
                    <option value="très_élevée">Très élevée</option>
                    <option value="élevée">Élevée</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="faible">Faible</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Produit/Service concerné</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.product || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, product: e.target.value})}
                    placeholder="Désignation du produit/service"
                  />
                </div>
                <div className="form-group">
                  <label>Référence/Lot</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.reference || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, reference: e.target.value})}
                    placeholder="Numéro de lot ou référence"
                  />
                </div>
                <div className="form-group">
                  <label>Quantité affectée</label>
                  <input
                    type="number"
                    value={formData.nonCompliance?.affectedQuantity || 0}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, affectedQuantity: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Norme/Spécification</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.standard || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, standard: e.target.value})}
                    placeholder="Référence norme non respectée"
                  />
                </div>
                <div className="form-group">
                  <label>Responsable traitement</label>
                  <input
                    type="text"
                    value={formData.treatment?.responsible || ''}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, responsible: e.target.value})}
                    placeholder="Nom du responsable"
                  />
                </div>
                <div className="form-group">
                  <label>Date limite traitement</label>
                  <input
                    type="date"
                    value={formData.treatment?.deadline || ''}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, deadline: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.treatment?.status || 'ouverte'}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, status: e.target.value})}
                  >
                    <option value="ouverte">Ouverte</option>
                    <option value="en_cours">En cours de traitement</option>
                    <option value="fermée">Fermée</option>
                    <option value="vérifiée">Vérifiée</option>
                    <option value="annulée">Annulée</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Coût estimé (DZD)</label>
                  <input
                    type="number"
                    value={formData.treatment?.estimatedCost || 0}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, estimatedCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.nonCompliance?.clientNotified || false}
                      onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, clientNotified: e.target.checked})}
                    />
                    Client informé
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.nonCompliance?.productBlocked || false}
                      onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, productBlocked: e.target.checked})}
                    />
                    Produit bloqué
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.treatment?.immediateAction || false}
                      onChange={(e) => handleInputChange('treatment', {...formData.treatment, immediateAction: e.target.checked})}
                    />
                    Action immédiate requise
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Description de la non-conformité</label>
                <textarea
                  value={formData.nonCompliance?.description || ''}
                  onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, description: e.target.value})}
                  placeholder="Description détaillée de la non-conformité constatée"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Cause probable</label>
                <textarea
                  value={formData.nonCompliance?.probableCause || ''}
                  onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, probableCause: e.target.value})}
                  placeholder="Analyse des causes probables"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Actions correctives</label>
                <textarea
                  value={formData.treatment?.correctiveActions || ''}
                  onChange={(e) => handleInputChange('treatment', {...formData.treatment, correctiveActions: e.target.value})}
                  placeholder="Actions correctives à mettre en œuvre"
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* DOCUMENTS LÉGAUX */}
          {selectedType.id === 'tax-certificate' && (
          <div className="form-section">
            <h3 className="section-title">
                <FileText size={20} />
                Attestation fiscale
            </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label>Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={formData.applicant?.name || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, name: e.target.value})}
                    placeholder="Raison sociale"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique</label>
                  <select
                    value={formData.applicant?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">Entreprise individuelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF</label>
                  <input
                    type="text"
                    value={formData.applicant?.nif || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>NIS</label>
                  <input
                    type="text"
                    value={formData.applicant?.nis || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, nis: e.target.value})}
                    placeholder="Numéro d'identification statistique"
                  />
                </div>
                <div className="form-group">
                  <label>Article d'imposition (AI)</label>
                  <input
                    type="text"
                    value={formData.applicant?.ai || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, ai: e.target.value})}
                    placeholder="Numéro article d'imposition"
                  />
                </div>
                <div className="form-group">
                  <label>Registre de commerce</label>
                  <input
                    type="text"
                    value={formData.applicant?.commercialRegister || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, commercialRegister: e.target.value})}
                    placeholder="Numéro RC"
                  />
                </div>
                <div className="form-group">
                  <label>Capital social (DZD)</label>
                  <input
                    type="number"
                    value={formData.applicant?.capital || 0}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, capital: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Secteur d'activité</label>
                  <input
                    type="text"
                    value={formData.applicant?.activity || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, activity: e.target.value})}
                    placeholder="Description de l'activité"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'attestation</label>
                  <select
                    value={formData.certificateDetails?.type || 'fiscal'}
                    onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, type: e.target.value})}
                  >
                    <option value="fiscal">Attestation fiscale</option>
                    <option value="non_redevable">Non redevable TVA</option>
                    <option value="exonération">Exonération fiscale</option>
                    <option value="régularité">Régularité fiscale</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Objet de la demande</label>
                  <select
                    value={formData.certificateDetails?.purpose || 'marchés_publics'}
                    onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, purpose: e.target.value})}
                  >
                    <option value="marchés_publics">Marchés publics</option>
                    <option value="appel_offres">Appel d'offres</option>
                    <option value="banque">Dossier bancaire</option>
                    <option value="partenariat">Partenariat commercial</option>
                    <option value="subvention">Demande de subvention</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Année fiscale</label>
                  <input
                    type="number"
                    value={formData.taxPeriod?.taxYear || new Date().getFullYear()}
                    onChange={(e) => handleInputChange('taxPeriod', {...formData.taxPeriod, taxYear: parseInt(e.target.value)})}
                    placeholder={new Date().getFullYear().toString()}
                  />
                </div>
                <div className="form-group">
                  <label>Statut déclarations</label>
                  <select
                    value={formData.taxCompliance?.declarationStatus || 'à_jour'}
                    onChange={(e) => handleInputChange('taxCompliance', {...formData.taxCompliance, declarationStatus: e.target.value})}
                  >
                    <option value="à_jour">À jour</option>
                    <option value="en_retard">En retard</option>
                    <option value="non_déposées">Non déposées</option>
                    <option value="partielles">Partielles</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut paiements</label>
                  <select
                    value={formData.taxCompliance?.paymentStatus || 'à_jour'}
                    onChange={(e) => handleInputChange('taxCompliance', {...formData.taxCompliance, paymentStatus: e.target.value})}
                  >
                    <option value="à_jour">À jour</option>
                    <option value="arriérés">Arriérés</option>
                    <option value="échéancier">Échéancier en cours</option>
                    <option value="contentieux">Contentieux</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Chiffre d'affaires (DZD)</label>
                  <input
                    type="number"
                    value={formData.businessData?.turnover || 0}
                    onChange={(e) => handleInputChange('businessData', {...formData.businessData, turnover: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre d'employés</label>
                  <input
                    type="number"
                    value={formData.businessData?.employeeCount || 0}
                    onChange={(e) => handleInputChange('businessData', {...formData.businessData, employeeCount: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>IRG dû (DZD)</label>
                  <input
                    type="number"
                    value={formData.taxDetails?.irg?.due || 0}
                    onChange={(e) => handleInputChange('taxDetails', {...formData.taxDetails, irg: {...formData.taxDetails.irg, due: parseFloat(e.target.value)}})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>IBS dû (DZD)</label>
                  <input
                    type="number"
                    value={formData.taxDetails?.ibs?.due || 0}
                    onChange={(e) => handleInputChange('taxDetails', {...formData.taxDetails, ibs: {...formData.taxDetails.ibs, due: parseFloat(e.target.value)}})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>TVA due (DZD)</label>
                  <input
                    type="number"
                    value={formData.taxDetails?.tva?.due || 0}
                    onChange={(e) => handleInputChange('taxDetails', {...formData.taxDetails, tva: {...formData.taxDetails.tva, due: parseFloat(e.target.value)}})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Autorité émettrice</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuingAuthority || 'Direction des Impôts'}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuingAuthority: e.target.value})}
                    placeholder="Direction des Impôts"
                  />
                </div>
                <div className="form-group">
                  <label>Bureau émetteur</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuingOffice || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuingOffice: e.target.value})}
                    placeholder="Nom du bureau/centre"
                  />
                </div>
                <div className="form-group">
                  <label>Période de validité</label>
                  <select
                    value={formData.issuance?.validityPeriod || '12 mois'}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, validityPeriod: e.target.value})}
                  >
                    <option value="3 mois">3 mois</option>
                    <option value="6 mois">6 mois</option>
                    <option value="12 mois">12 mois</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'work-certificate' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Certificat de travail effectué
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={formData.contractor?.name || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, name: e.target.value})}
                    placeholder="Raison sociale de l'entrepreneur"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique</label>
                  <select
                    value={formData.contractor?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="entreprise_individuelle">Entreprise individuelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Entrepreneur</label>
                  <input
                    type="text"
                    value={formData.contractor?.nif || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Représentant légal</label>
                  <input
                    type="text"
                    value={formData.contractor?.legalRepresentative || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, legalRepresentative: e.target.value})}
                    placeholder="Nom du gérant/directeur"
                  />
                </div>
                <div className="form-group">
                  <label>Directeur technique</label>
                  <input
                    type="text"
                    value={formData.contractor?.technicalDirector || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, technicalDirector: e.target.value})}
                    placeholder="Nom du directeur technique"
                  />
                </div>
                <div className="form-group">
                  <label>Nom du client</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder="Maître d'ouvrage"
                  />
                </div>
                <div className="form-group">
                  <label>Chef de projet client</label>
                  <input
                    type="text"
                    value={formData.client?.projectManager || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, projectManager: e.target.value})}
                    placeholder="Responsable projet côté client"
                  />
                </div>
                <div className="form-group">
                  <label>Référence du contrat</label>
                  <input
                    type="text"
                    value={formData.contractDetails?.reference || ''}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, reference: e.target.value})}
                    placeholder="Numéro/référence du marché"
                  />
                </div>
                <div className="form-group">
                  <label>Date de signature</label>
                  <input
                    type="date"
                    value={formData.contractDetails?.signatureDate || ''}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, signatureDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.contractDetails?.startDate || ''}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date d'achèvement</label>
                  <input
                    type="date"
                    value={formData.contractDetails?.actualCompletionDate || ''}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, actualCompletionDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Montant du contrat (DZD)</label>
                  <input
                    type="number"
                    value={formData.contractDetails?.contractAmount || 0}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, contractAmount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Catégorie de travaux</label>
                  <select
                    value={formData.workDescription?.category || 'construction'}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, category: e.target.value})}
                  >
                    <option value="construction">Construction</option>
                    <option value="rénovation">Rénovation</option>
                    <option value="infrastructure">Infrastructure</option>
                    <option value="génie_civil">Génie civil</option>
                    <option value="équipement">Équipement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Type de bâtiment</label>
                  <select
                    value={formData.workDescription?.type || 'bâtiment'}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, type: e.target.value})}
                  >
                    <option value="bâtiment">Bâtiment</option>
                    <option value="route">Route</option>
                    <option value="pont">Pont</option>
                    <option value="usine">Usine</option>
                    <option value="logement">Logement</option>
                    <option value="bureau">Bureau</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Surface totale (m²)</label>
                  <input
                    type="number"
                    value={formData.workDescription?.totalSurface || 0}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, totalSurface: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre de niveaux</label>
                  <input
                    type="number"
                    value={formData.workDescription?.numberOfLevels || 0}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, numberOfLevels: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Normes de conformité</label>
                  <select
                    value={formData.compliance?.conformityStandards || 'DTR algériens'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, conformityStandards: e.target.value})}
                  >
                    <option value="DTR algériens">DTR algériens</option>
                    <option value="DTR + normes internationales">DTR + normes internationales</option>
                    <option value="normes européennes">Normes européennes</option>
                    <option value="normes françaises">Normes françaises</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau de qualité global</label>
                  <select
                    value={formData.qualityAssessment?.overallLevel || 'conforme'}
                    onChange={(e) => handleInputChange('qualityAssessment', {...formData.qualityAssessment, overallLevel: e.target.value})}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="très_bon">Très bon</option>
                    <option value="bon">Bon</option>
                    <option value="conforme">Conforme</option>
                    <option value="acceptable">Acceptable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Qualité des matériaux</label>
                  <select
                    value={formData.qualityAssessment?.materialQuality || 'satisfaisante'}
                    onChange={(e) => handleInputChange('qualityAssessment', {...formData.qualityAssessment, materialQuality: e.target.value})}
                  >
                    <option value="excellente">Excellente</option>
                    <option value="très_bonne">Très bonne</option>
                    <option value="bonne">Bonne</option>
                    <option value="satisfaisante">Satisfaisante</option>
                    <option value="insuffisante">Insuffisante</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Garantie structurelle</label>
                  <select
                    value={formData.warranties?.structuralWarranty || '10 ans'}
                    onChange={(e) => handleInputChange('warranties', {...formData.warranties, structuralWarranty: e.target.value})}
                  >
                    <option value="5 ans">5 ans</option>
                    <option value="10 ans">10 ans</option>
                    <option value="15 ans">15 ans</option>
                    <option value="20 ans">20 ans</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Satisfaction client</label>
                  <select
                    value={formData.performance?.clientSatisfaction || 'satisfait'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, clientSatisfaction: e.target.value})}
                  >
                    <option value="très_satisfait">Très satisfait</option>
                    <option value="satisfait">Satisfait</option>
                    <option value="acceptable">Acceptable</option>
                    <option value="insatisfait">Insatisfait</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.performance?.timelyCompletion || false}
                      onChange={(e) => handleInputChange('performance', {...formData.performance, timelyCompletion: e.target.checked})}
                    />
                    Achèvement dans les délais
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.performance?.budgetRespected || false}
                      onChange={(e) => handleInputChange('performance', {...formData.performance, budgetRespected: e.target.checked})}
                    />
                    Budget respecté
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.environmentalCompliance || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, environmentalCompliance: e.target.checked})}
                    />
                    Conformité environnementale
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'legal-notice' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Notification légale
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Expéditeur (Nom)</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.name || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, name: e.target.value}})}
                    placeholder="Nom de l'expéditeur"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique expéditeur</label>
                  <select
                    value={formData.parties?.sender?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, legalForm: e.target.value}})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="personne_physique">Personne physique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Expéditeur</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.nif || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, nif: e.target.value}})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Représentant légal</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.legalRepresentative || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, legalRepresentative: e.target.value}})}
                    placeholder="Nom du représentant"
                  />
                </div>
                <div className="form-group">
                  <label>Avocat (optionnel)</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.lawyer || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, lawyer: e.target.value}})}
                    placeholder="Nom de l'avocat"
                  />
                </div>
                <div className="form-group">
                  <label>Destinataire (Nom)</label>
                  <input
                    type="text"
                    value={formData.parties?.recipient?.name || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, recipient: {...formData.parties.recipient, name: e.target.value}})}
                    placeholder="Nom du destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Adresse destinataire</label>
                  <input
                    type="text"
                    value={formData.parties?.recipient?.address || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, recipient: {...formData.parties.recipient, address: e.target.value}})}
                    placeholder="Adresse complète"
                  />
                </div>
                <div className="form-group">
                  <label>Type de notification</label>
                  <select
                    value={formData.noticeDetails?.type || 'contractuelle'}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, type: e.target.value})}
                  >
                    <option value="contractuelle">Contractuelle</option>
                    <option value="commerciale">Commerciale</option>
                    <option value="administrative">Administrative</option>
                    <option value="judiciaire">Judiciaire</option>
                    <option value="réglementaire">Réglementaire</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.noticeDetails?.category || 'mise_en_demeure'}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, category: e.target.value})}
                  >
                    <option value="mise_en_demeure">Mise en demeure</option>
                    <option value="résiliation">Résiliation</option>
                    <option value="convocation">Convocation</option>
                    <option value="sommation">Sommation</option>
                    <option value="notification">Notification simple</option>
                    <option value="assignation">Assignation</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau d'urgence</label>
                  <select
                    value={formData.noticeDetails?.urgency || 'normale'}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, urgency: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="très_urgente">Très urgente</option>
                    <option value="exceptionnelle">Exceptionnelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Objet principal</label>
                  <input
                    type="text"
                    value={formData.noticeDetails?.subject || ''}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, subject: e.target.value})}
                    placeholder="Objet de la notification"
                  />
                </div>
                <div className="form-group">
                  <label>Référence du contrat</label>
                  <input
                    type="text"
                    value={formData.noticeDetails?.relatedContract || ''}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, relatedContract: e.target.value})}
                    placeholder="Numéro/référence du contrat"
                  />
                </div>
                <div className="form-group">
                  <label>Base légale</label>
                  <input
                    type="text"
                    value={formData.legalFramework?.legalBasis || ''}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, legalBasis: e.target.value})}
                    placeholder="Article de loi ou règlement"
                  />
                </div>
                <div className="form-group">
                  <label>Juridiction compétente</label>
                  <select
                    value={formData.legalFramework?.jurisdiction || 'Tribunaux algériens'}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, jurisdiction: e.target.value})}
                  >
                    <option value="Tribunaux algériens">Tribunaux algériens</option>
                    <option value="Tribunal de commerce">Tribunal de commerce</option>
                    <option value="Tribunal civil">Tribunal civil</option>
                    <option value="Conseil d'État">Conseil d'État</option>
                    <option value="Arbitrage">Arbitrage</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Droit applicable</label>
                  <select
                    value={formData.legalFramework?.applicableLaw || 'Droit algérien'}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, applicableLaw: e.target.value})}
                  >
                    <option value="Droit algérien">Droit algérien</option>
                    <option value="Code civil">Code civil</option>
                    <option value="Code de commerce">Code de commerce</option>
                    <option value="Code du travail">Code du travail</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Mode de remise</label>
                  <select
                    value={formData.delivery?.method || 'recommandé'}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, method: e.target.value})}
                  >
                    <option value="recommandé">Recommandé avec AR</option>
                    <option value="huissier">Huissier de justice</option>
                    <option value="main_propre">Remise en main propre</option>
                    <option value="électronique">Signification électronique</option>
                    <option value="affichage">Affichage légal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Délai de réponse (jours)</label>
                  <input
                    type="number"
                    value={formData.followUp?.responseDeadline || ''}
                    onChange={(e) => handleInputChange('followUp', {...formData.followUp, responseDeadline: e.target.value})}
                    placeholder="Nombre de jours"
                  />
                </div>
                <div className="form-group">
                  <label>Résolution alternative</label>
                  <select
                    value={formData.followUp?.alternativeResolution || 'médiation'}
                    onChange={(e) => handleInputChange('followUp', {...formData.followUp, alternativeResolution: e.target.value})}
                  >
                    <option value="médiation">Médiation</option>
                    <option value="conciliation">Conciliation</option>
                    <option value="arbitrage">Arbitrage</option>
                    <option value="négociation">Négociation directe</option>
                    <option value="aucune">Aucune</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.recipientSignature || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, recipientSignature: e.target.checked})}
                    />
                    Signature du destinataire requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.witnessRequired || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, witnessRequired: e.target.checked})}
                    />
                    Témoin requis
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.followUp?.responseRequired || false}
                      onChange={(e) => handleInputChange('followUp', {...formData.followUp, responseRequired: e.target.checked})}
                    />
                    Réponse obligatoire
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.authentication?.officialStamp || false}
                      onChange={(e) => handleInputChange('authentication', {...formData.authentication, officialStamp: e.target.checked})}
                    />
                    Cachet officiel requis
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Motif de la demande</label>
                <textarea
                  value={formData.certificateDetails?.requestReason || ''}
                  onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, requestReason: e.target.value})}
                  placeholder="Expliquez en détail les motifs et circonstances de cette notification légale"
                  rows="4"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'employment-certificate' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Attestation de travail
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom de l'employeur</label>
                  <input
                    type="text"
                    value={formData.employer?.name || ''}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, name: e.target.value})}
                    placeholder="Raison sociale de l'entreprise"
                  />
                </div>
                <div className="form-group">
                  <label>Forme juridique</label>
                  <select
                    value={formData.employer?.legalForm || 'SARL'}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>NIF Employeur</label>
                  <input
                    type="text"
                    value={formData.employer?.nif || ''}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, nif: e.target.value})}
                    placeholder="Numéro d'identification fiscale"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de l'employé</label>
                  <input
                    type="text"
                    value={formData.employee?.name || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, name: e.target.value})}
                    placeholder="Nom de famille"
                  />
                </div>
                <div className="form-group">
                  <label>Prénom de l'employé</label>
                  <input
                    type="text"
                    value={formData.employee?.firstName || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, firstName: e.target.value})}
                    placeholder="Prénom"
                  />
                </div>
                <div className="form-group">
                  <label>Matricule</label>
                  <input
                    type="text"
                    value={formData.employee?.employeeId || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, employeeId: e.target.value})}
                    placeholder="Numéro matricule"
                  />
                </div>
                <div className="form-group">
                  <label>Poste occupé</label>
                  <input
                    type="text"
                    value={formData.employee?.position || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, position: e.target.value})}
                    placeholder="Intitulé du poste"
                  />
                </div>
                <div className="form-group">
                  <label>Département</label>
                  <input
                    type="text"
                    value={formData.employee?.department || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, department: e.target.value})}
                    placeholder="Service/Département"
                  />
                </div>
                <div className="form-group">
                  <label>Type de contrat</label>
                  <select
                    value={formData.employment?.contractType || 'CDI'}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, contractType: e.target.value})}
                  >
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="stage">Stage</option>
                    <option value="apprentissage">Apprentissage</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.employment?.startDate || ''}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin</label>
                  <input
                    type="date"
                    value={formData.employment?.endDate || ''}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Statut actuel</label>
                  <select
                    value={formData.employment?.currentStatus || 'en_service'}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, currentStatus: e.target.value})}
                  >
                    <option value="en_service">En service</option>
                    <option value="démission">Démission</option>
                    <option value="licenciement">Licenciement</option>
                    <option value="fin_contrat">Fin de contrat</option>
                    <option value="retraite">Retraite</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Dernier salaire (DZD)</label>
                  <input
                    type="number"
                    value={formData.remuneration?.lastSalary || 0}
                    onChange={(e) => handleInputChange('remuneration', {...formData.remuneration, lastSalary: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Évaluation globale</label>
                  <select
                    value={formData.performance?.overallRating || 'satisfaisante'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, overallRating: e.target.value})}
                  >
                    <option value="excellente">Excellente</option>
                    <option value="très_bonne">Très bonne</option>
                    <option value="bonne">Bonne</option>
                    <option value="satisfaisante">Satisfaisante</option>
                    <option value="insuffisante">Insuffisante</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Ponctualité</label>
                  <select
                    value={formData.performance?.punctuality || 'excellente'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, punctuality: e.target.value})}
                  >
                    <option value="excellente">Excellente</option>
                    <option value="bonne">Bonne</option>
                    <option value="satisfaisante">Satisfaisante</option>
                    <option value="insuffisante">Insuffisante</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Objet de l'attestation</label>
                  <select
                    value={formData.certification?.purpose || 'administrative'}
                    onChange={(e) => handleInputChange('certification', {...formData.certification, purpose: e.target.value})}
                  >
                    <option value="administrative">Démarche administrative</option>
                    <option value="banque">Dossier bancaire</option>
                    <option value="visa">Demande de visa</option>
                    <option value="logement">Dossier logement</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Émis par</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuedBy: e.target.value})}
                    placeholder="Nom du responsable RH"
                  />
                </div>
                <div className="form-group">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={formData.issuance?.position || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, position: e.target.value})}
                    placeholder="Fonction du signataire"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'leave-request' && (
            <div className="form-section">
              <h3 className="section-title">
                <Calendar size={20} />
                Demande de congé
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom de l'employé</label>
                  <input
                    type="text"
                    value={formData.employee?.name || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, name: e.target.value})}
                    placeholder="Nom de famille"
                  />
                </div>
                <div className="form-group">
                  <label>Prénom de l'employé</label>
                  <input
                    type="text"
                    value={formData.employee?.firstName || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, firstName: e.target.value})}
                    placeholder="Prénom"
                  />
                </div>
                <div className="form-group">
                  <label>Matricule</label>
                  <input
                    type="text"
                    value={formData.employee?.employeeId || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, employeeId: e.target.value})}
                    placeholder="Numéro matricule"
                  />
                </div>
                <div className="form-group">
                  <label>Poste</label>
                  <input
                    type="text"
                    value={formData.employee?.position || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, position: e.target.value})}
                    placeholder="Intitulé du poste"
                  />
                </div>
                <div className="form-group">
                  <label>Département</label>
                  <input
                    type="text"
                    value={formData.employee?.department || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, department: e.target.value})}
                    placeholder="Service/Département"
                  />
                </div>
                <div className="form-group">
                  <label>Superviseur direct</label>
                  <input
                    type="text"
                    value={formData.employee?.supervisor || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, supervisor: e.target.value})}
                    placeholder="Nom du superviseur"
                  />
                </div>
                <div className="form-group">
                  <label>Type de congé</label>
                  <select
                    value={formData.leaveDetails?.type || 'congé_annuel'}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, type: e.target.value})}
                  >
                    <option value="congé_annuel">Congé annuel</option>
                    <option value="congé_maladie">Congé maladie</option>
                    <option value="congé_maternité">Congé maternité</option>
                    <option value="congé_paternité">Congé paternité</option>
                    <option value="congé_exceptionnel">Congé exceptionnel</option>
                    <option value="congé_sans_solde">Congé sans solde</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.leaveDetails?.category || 'repos'}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, category: e.target.value})}
                  >
                    <option value="repos">Repos</option>
                    <option value="familial">Familial</option>
                    <option value="médical">Médical</option>
                    <option value="formation">Formation</option>
                    <option value="personnel">Personnel</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={formData.leaveDetails?.startDate || ''}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin</label>
                  <input
                    type="date"
                    value={formData.leaveDetails?.endDate || ''}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, endDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Nombre de jours</label>
                  <input
                    type="number"
                    value={formData.leaveDetails?.totalDays || 0}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, totalDays: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Solde congés (jours)</label>
                  <input
                    type="number"
                    value={formData.entitlement?.remainingDays || 30}
                    onChange={(e) => handleInputChange('entitlement', {...formData.entitlement, remainingDays: parseInt(e.target.value)})}
                    placeholder="30"
                  />
                </div>
                <div className="form-group">
                  <label>Urgence</label>
                  <select
                    value={formData.justification?.urgency || 'normale'}
                    onChange={(e) => handleInputChange('justification', {...formData.justification, urgency: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                    <option value="exceptionnelle">Exceptionnelle</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Remplaçant temporaire</label>
                  <input
                    type="text"
                    value={formData.workCoverage?.temporary || ''}
                    onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, temporary: e.target.value})}
                    placeholder="Nom du remplaçant"
                  />
                </div>
                <div className="form-group">
                  <label>Impact sur les projets</label>
                  <select
                    value={formData.workCoverage?.projectImpact || 'aucun'}
                    onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, projectImpact: e.target.value})}
                  >
                    <option value="aucun">Aucun</option>
                    <option value="mineur">Mineur</option>
                    <option value="modéré">Modéré</option>
                    <option value="important">Important</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Contact d'urgence</label>
                  <input
                    type="text"
                    value={formData.emergencyContacts?.primaryContact || ''}
                    onChange={(e) => handleInputChange('emergencyContacts', {...formData.emergencyContacts, primaryContact: e.target.value})}
                    placeholder="Nom de la personne à contacter"
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone d'urgence</label>
                  <input
                    type="tel"
                    value={formData.emergencyContacts?.primaryPhone || ''}
                    onChange={(e) => handleInputChange('emergencyContacts', {...formData.emergencyContacts, primaryPhone: e.target.value})}
                    placeholder="Numéro de téléphone"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.justification?.medicalCertificate || false}
                      onChange={(e) => handleInputChange('justification', {...formData.justification, medicalCertificate: e.target.checked})}
                    />
                    Certificat médical fourni
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.workCoverage?.handoverCompleted || false}
                      onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, handoverCompleted: e.target.checked})}
                    />
                    Passation effectuée
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.approval?.supervisorApproval || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, supervisorApproval: e.target.checked})}
                    />
                    Approuvé par le superviseur
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Motif de la demande</label>
                <textarea
                  value={formData.justification?.reason || ''}
                  onChange={(e) => handleInputChange('justification', {...formData.justification, reason: e.target.value})}
                  placeholder="Expliquez le motif de votre demande de congé"
                  rows="3"
                />
              </div>
            </div>
          )}

          {/* PRODUCTION & QUALITÉ Documents */}
          {selectedType.id === 'production-sheet' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Fiche de production
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom du produit</label>
                  <input
                    type="text"
                    value={formData.productInfo?.name || ''}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, name: e.target.value})}
                    placeholder="Nom du produit à fabriquer"
                  />
                </div>
                <div className="form-group">
                  <label>Code produit</label>
                  <input
                    type="text"
                    value={formData.productInfo?.code || ''}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, code: e.target.value})}
                    placeholder="Code de référence"
                  />
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.productInfo?.category || 'produit_fini'}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, category: e.target.value})}
                  >
                    <option value="produit_fini">Produit fini</option>
                    <option value="sous_ensemble">Sous-ensemble</option>
                    <option value="composant">Composant</option>
                    <option value="matière_première">Matière première</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Numéro d'ordre</label>
                  <input
                    type="text"
                    value={formData.productionOrder?.number || ''}
                    onChange={(e) => handleInputChange('productionOrder', {...formData.productionOrder, number: e.target.value})}
                    placeholder="Numéro d'ordre de fabrication"
                  />
                </div>
                <div className="form-group">
                  <label>Quantité cible</label>
                  <input
                    type="number"
                    value={formData.productionOrder?.targetQuantity || 0}
                    onChange={(e) => handleInputChange('productionOrder', {...formData.productionOrder, targetQuantity: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Quantité réelle</label>
                  <input
                    type="number"
                    value={formData.productionOrder?.actualQuantity || 0}
                    onChange={(e) => handleInputChange('productionOrder', {...formData.productionOrder, actualQuantity: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de lot</label>
                  <input
                    type="text"
                    value={formData.batchInfo?.batchNumber || ''}
                    onChange={(e) => handleInputChange('batchInfo', {...formData.batchInfo, batchNumber: e.target.value})}
                    placeholder="Numéro de lot de production"
                  />
                </div>
                <div className="form-group">
                  <label>Taille du lot</label>
                  <input
                    type="number"
                    value={formData.batchInfo?.batchSize || 0}
                    onChange={(e) => handleInputChange('batchInfo', {...formData.batchInfo, batchSize: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Opérateur</label>
                  <input
                    type="text"
                    value={formData.team?.operator || ''}
                    onChange={(e) => handleInputChange('team', {...formData.team, operator: e.target.value})}
                    placeholder="Nom de l'opérateur"
                  />
                </div>
                <div className="form-group">
                  <label>Superviseur</label>
                  <input
                    type="text"
                    value={formData.team?.supervisor || ''}
                    onChange={(e) => handleInputChange('team', {...formData.team, supervisor: e.target.value})}
                    placeholder="Nom du superviseur"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'équipe</label>
                  <select
                    value={formData.team?.shiftType || 'jour'}
                    onChange={(e) => handleInputChange('team', {...formData.team, shiftType: e.target.value})}
                  >
                    <option value="jour">Équipe de jour</option>
                    <option value="nuit">Équipe de nuit</option>
                    <option value="weekend">Week-end</option>
                    <option value="continu">Service continu</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Machine principale</label>
                  <input
                    type="text"
                    value={formData.equipment?.primaryMachine || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, primaryMachine: e.target.value})}
                    placeholder="Nom de la machine"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de machine</label>
                  <input
                    type="text"
                    value={formData.equipment?.machineNumber || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, machineNumber: e.target.value})}
                    placeholder="Numéro d'identification"
                  />
                </div>
                <div className="form-group">
                  <label>Date de début prévue</label>
                  <input
                    type="date"
                    value={formData.schedule?.plannedStartDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, plannedStartDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin prévue</label>
                  <input
                    type="date"
                    value={formData.schedule?.plannedEndDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, plannedEndDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Efficacité (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.performance?.efficiency || 100}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, efficiency: parseFloat(e.target.value)})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group">
                  <label>Taux de conformité (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.performance?.conformityRate || 100}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, conformityRate: parseFloat(e.target.value)})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group">
                  <label>Coût total (DZD)</label>
                  <input
                    type="number"
                    value={formData.costs?.totalCost || 0}
                    onChange={(e) => handleInputChange('costs', {...formData.costs, totalCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'quality-control' && (
            <div className="form-section">
              <h3 className="section-title">
                <CheckCircle size={20} />
                Contrôle qualité
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom du produit</label>
                  <input
                    type="text"
                    value={formData.productDetails?.name || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, name: e.target.value})}
                    placeholder="Nom du produit contrôlé"
                  />
                </div>
                <div className="form-group">
                  <label>Code produit</label>
                  <input
                    type="text"
                    value={formData.productDetails?.code || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, code: e.target.value})}
                    placeholder="Code de référence"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de lot</label>
                  <input
                    type="text"
                    value={formData.productDetails?.batchNumber || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, batchNumber: e.target.value})}
                    placeholder="Numéro de lot"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'inspection</label>
                  <select
                    value={formData.inspectionInfo?.type || 'réception'}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, type: e.target.value})}
                  >
                    <option value="réception">Contrôle réception</option>
                    <option value="en_cours">Contrôle en cours</option>
                    <option value="final">Contrôle final</option>
                    <option value="expédition">Contrôle expédition</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Stade de contrôle</label>
                  <select
                    value={formData.inspectionInfo?.stage || 'final'}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, stage: e.target.value})}
                  >
                    <option value="matières_premières">Matières premières</option>
                    <option value="composants">Composants</option>
                    <option value="sous_ensembles">Sous-ensembles</option>
                    <option value="final">Produit final</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Méthode d'échantillonnage</label>
                  <select
                    value={formData.inspectionInfo?.sampling || 'statistique'}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, sampling: e.target.value})}
                  >
                    <option value="statistique">Échantillonnage statistique</option>
                    <option value="aléatoire">Échantillonnage aléatoire</option>
                    <option value="systématique">Échantillonnage systématique</option>
                    <option value="complet">Contrôle 100%</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Taille de l'échantillon</label>
                  <input
                    type="number"
                    value={formData.inspectionInfo?.sampleSize || 0}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, sampleSize: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Taille du lot</label>
                  <input
                    type="number"
                    value={formData.inspectionInfo?.lotSize || 0}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, lotSize: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Inspecteur</label>
                  <input
                    type="text"
                    value={formData.inspectionInfo?.inspector || ''}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, inspector: e.target.value})}
                    placeholder="Nom de l'inspecteur"
                  />
                </div>
                <div className="form-group">
                  <label>Norme principale</label>
                  <select
                    value={formData.standards?.primaryStandard || 'ISO 9001'}
                    onChange={(e) => handleInputChange('standards', {...formData.standards, primaryStandard: e.target.value})}
                  >
                    <option value="ISO 9001">ISO 9001</option>
                    <option value="ISO 14001">ISO 14001</option>
                    <option value="IATF 16949">IATF 16949</option>
                    <option value="AS9100">AS9100</option>
                    <option value="ISO 13485">ISO 13485</option>
                    <option value="FDA">FDA</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Finition de surface</label>
                  <select
                    value={formData.visualInspection?.surfaceFinish || 'acceptable'}
                    onChange={(e) => handleInputChange('visualInspection', {...formData.visualInspection, surfaceFinish: e.target.value})}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="acceptable">Acceptable</option>
                    <option value="limite">Limite</option>
                    <option value="inacceptable">Inacceptable</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conformité couleur</label>
                  <select
                    value={formData.visualInspection?.colorConformity || 'conforme'}
                    onChange={(e) => handleInputChange('visualInspection', {...formData.visualInspection, colorConformity: e.target.value})}
                  >
                    <option value="conforme">Conforme</option>
                    <option value="acceptable">Acceptable</option>
                    <option value="non_conforme">Non conforme</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Résultat global</label>
                  <select
                    value={formData.results?.overallResult || 'conforme'}
                    onChange={(e) => handleInputChange('results', {...formData.results, overallResult: e.target.value})}
                  >
                    <option value="conforme">Conforme</option>
                    <option value="acceptable">Acceptable</option>
                    <option value="non_conforme">Non conforme</option>
                    <option value="rejeté">Rejeté</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Unités conformes</label>
                  <input
                    type="number"
                    value={formData.results?.conformingUnits || 0}
                    onChange={(e) => handleInputChange('results', {...formData.results, conformingUnits: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Unités non conformes</label>
                  <input
                    type="number"
                    value={formData.results?.nonConformingUnits || 0}
                    onChange={(e) => handleInputChange('results', {...formData.results, nonConformingUnits: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Taux de conformité (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.results?.conformityRate || 100}
                    onChange={(e) => handleInputChange('results', {...formData.results, conformityRate: parseFloat(e.target.value)})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.decisions?.lotAccepted || false}
                      onChange={(e) => handleInputChange('decisions', {...formData.decisions, lotAccepted: e.target.checked})}
                    />
                    Lot accepté
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.decisions?.reworkRequired || false}
                      onChange={(e) => handleInputChange('decisions', {...formData.decisions, reworkRequired: e.target.checked})}
                    />
                    Retouche requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.followUp?.monitoringRequired || false}
                      onChange={(e) => handleInputChange('followUp', {...formData.followUp, monitoringRequired: e.target.checked})}
                    />
                    Surveillance requise
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'manufacturing-order' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileCheck size={20} />
                Bon de fabrication
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Émis par</label>
                  <input
                    type="text"
                    value={formData.orderInfo?.issuedBy || ''}
                    onChange={(e) => handleInputChange('orderInfo', {...formData.orderInfo, issuedBy: e.target.value})}
                    placeholder="Nom du responsable"
                  />
                </div>
                <div className="form-group">
                  <label>Département</label>
                  <select
                    value={formData.orderInfo?.department || 'production'}
                    onChange={(e) => handleInputChange('orderInfo', {...formData.orderInfo, department: e.target.value})}
                  >
                    <option value="production">Production</option>
                    <option value="assemblage">Assemblage</option>
                    <option value="usinage">Usinage</option>
                    <option value="peinture">Peinture</option>
                    <option value="contrôle">Contrôle qualité</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nom du produit</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.name || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, name: e.target.value})}
                    placeholder="Nom du produit à fabriquer"
                  />
                </div>
                <div className="form-group">
                  <label>Code produit</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.code || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, code: e.target.value})}
                    placeholder="Code de référence"
                  />
                </div>
                <div className="form-group">
                  <label>Révision</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.revision || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, revision: e.target.value})}
                    placeholder="Version/Révision"
                  />
                </div>
                <div className="form-group">
                  <label>Qualité</label>
                  <select
                    value={formData.productSpecification?.quality || 'standard'}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, quality: e.target.value})}
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="prototype">Prototype</option>
                    <option value="série">Série</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantité commandée</label>
                  <input
                    type="number"
                    value={formData.quantity?.ordered || 0}
                    onChange={(e) => handleInputChange('quantity', {...formData.quantity, ordered: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Production planifiée</label>
                  <input
                    type="number"
                    value={formData.quantity?.plannedProduction || 0}
                    onChange={(e) => handleInputChange('quantity', {...formData.quantity, plannedProduction: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Rendement prévu (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.quantity?.yield || 95}
                    onChange={(e) => handleInputChange('quantity', {...formData.quantity, yield: parseFloat(e.target.value)})}
                    placeholder="95"
                  />
                </div>
                <div className="form-group">
                  <label>Date demandée</label>
                  <input
                    type="date"
                    value={formData.scheduling?.requestedDate || ''}
                    onChange={(e) => handleInputChange('scheduling', {...formData.scheduling, requestedDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Début planifié</label>
                  <input
                    type="date"
                    value={formData.scheduling?.plannedStartDate || ''}
                    onChange={(e) => handleInputChange('scheduling', {...formData.scheduling, plannedStartDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Fin planifiée</label>
                  <input
                    type="date"
                    value={formData.scheduling?.plannedEndDate || ''}
                    onChange={(e) => handleInputChange('scheduling', {...formData.scheduling, plannedEndDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Équipe</label>
                  <select
                    value={formData.scheduling?.shift || 'jour'}
                    onChange={(e) => handleInputChange('scheduling', {...formData.scheduling, shift: e.target.value})}
                  >
                    <option value="jour">Équipe de jour</option>
                    <option value="nuit">Équipe de nuit</option>
                    <option value="weekend">Week-end</option>
                    <option value="continu">Service continu</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Machine principale</label>
                  <input
                    type="text"
                    value={formData.resources?.primaryMachine || ''}
                    onChange={(e) => handleInputChange('resources', {...formData.resources, primaryMachine: e.target.value})}
                    placeholder="Nom de la machine"
                  />
                </div>
                <div className="form-group">
                  <label>Coût total (DZD)</label>
                  <input
                    type="number"
                    value={formData.costing?.totalCost || 0}
                    onChange={(e) => handleInputChange('costing', {...formData.costing, totalCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Marge cible (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.costing?.marginTarget || 25}
                    onChange={(e) => handleInputChange('costing', {...formData.costing, marginTarget: parseFloat(e.target.value)})}
                    placeholder="25"
                  />
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.tracking?.status || 'planifié'}
                    onChange={(e) => handleInputChange('tracking', {...formData.tracking, status: e.target.value})}
                  >
                    <option value="planifié">Planifié</option>
                    <option value="approuvé">Approuvé</option>
                    <option value="en_cours">En cours</option>
                    <option value="suspendu">Suspendu</option>
                    <option value="terminé">Terminé</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.approval?.productionManager || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, productionManager: e.target.checked})}
                    />
                    Approuvé par le chef de production
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.approval?.qualityManager || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, qualityManager: e.target.checked})}
                    />
                    Approuvé par le responsable qualité
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'failure-report' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Rapport de défaillance
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>ID équipement</label>
                  <input
                    type="text"
                    value={formData.equipment?.id || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, id: e.target.value})}
                    placeholder="Identifiant de l'équipement"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de l'équipement</label>
                  <input
                    type="text"
                    value={formData.equipment?.name || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, name: e.target.value})}
                    placeholder="Nom de l'équipement"
                  />
                </div>
                <div className="form-group">
                  <label>Type d'équipement</label>
                  <input
                    type="text"
                    value={formData.equipment?.type || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, type: e.target.value})}
                    placeholder="Type d'équipement"
                  />
                </div>
                <div className="form-group">
                  <label>Fabricant</label>
                  <input
                    type="text"
                    value={formData.equipment?.manufacturer || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, manufacturer: e.target.value})}
                    placeholder="Nom du fabricant"
                  />
                </div>
                <div className="form-group">
                  <label>Type de défaillance</label>
                  <select
                    value={formData.failure?.type || 'panne'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, type: e.target.value})}
                  >
                    <option value="panne">Panne</option>
                    <option value="dysfonctionnement">Dysfonctionnement</option>
                    <option value="dégradation">Dégradation</option>
                    <option value="arrêt">Arrêt imprévu</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.failure?.category || 'électrique'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, category: e.target.value})}
                  >
                    <option value="électrique">Électrique</option>
                    <option value="mécanique">Mécanique</option>
                    <option value="hydraulique">Hydraulique</option>
                    <option value="pneumatique">Pneumatique</option>
                    <option value="électronique">Électronique</option>
                    <option value="logiciel">Logiciel</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sévérité</label>
                  <select
                    value={formData.failure?.severity || 'moyenne'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, severity: e.target.value})}
                  >
                    <option value="mineure">Mineure</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="majeure">Majeure</option>
                    <option value="critique">Critique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Impact</label>
                  <select
                    value={formData.failure?.impact || 'local'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, impact: e.target.value})}
                  >
                    <option value="local">Local</option>
                    <option value="ligne">Ligne de production</option>
                    <option value="atelier">Atelier</option>
                    <option value="usine">Usine complète</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date de détection</label>
                  <input
                    type="date"
                    value={formData.failure?.detectionDate || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, detectionDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de détection</label>
                  <input
                    type="time"
                    value={formData.failure?.detectionTime || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, detectionTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Détecté par</label>
                  <input
                    type="text"
                    value={formData.failure?.detectedBy || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, detectedBy: e.target.value})}
                    placeholder="Nom de la personne"
                  />
                </div>
                <div className="form-group">
                  <label>Mode de fonctionnement</label>
                  <select
                    value={formData.conditions?.operatingMode || 'automatique'}
                    onChange={(e) => handleInputChange('conditions', {...formData.conditions, operatingMode: e.target.value})}
                  >
                    <option value="automatique">Automatique</option>
                    <option value="manuel">Manuel</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="test">Test</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Charge de travail</label>
                  <select
                    value={formData.conditions?.workload || 'normale'}
                    onChange={(e) => handleInputChange('conditions', {...formData.conditions, workload: e.target.value})}
                  >
                    <option value="faible">Faible</option>
                    <option value="normale">Normale</option>
                    <option value="élevée">Élevée</option>
                    <option value="surcharge">Surcharge</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Méthode d'investigation</label>
                  <select
                    value={formData.investigation?.investigationMethod || '5 pourquoi'}
                    onChange={(e) => handleInputChange('investigation', {...formData.investigation, investigationMethod: e.target.value})}
                  >
                    <option value="5 pourquoi">5 Pourquoi</option>
                    <option value="ishikawa">Diagramme d'Ishikawa</option>
                    <option value="amdec">AMDEC</option>
                    <option value="pareto">Analyse de Pareto</option>
                    <option value="rca">Root Cause Analysis</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Perte de production (h)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.impact?.downtimeHours || 0}
                    onChange={(e) => handleInputChange('impact', {...formData.impact, downtimeHours: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Impact financier (DZD)</label>
                  <input
                    type="number"
                    value={formData.impact?.financialImpact || 0}
                    onChange={(e) => handleInputChange('impact', {...formData.impact, financialImpact: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Coût total (DZD)</label>
                  <input
                    type="number"
                    value={formData.resources?.totalCost || 0}
                    onChange={(e) => handleInputChange('resources', {...formData.resources, totalCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.resolution?.resolved || false}
                      onChange={(e) => handleInputChange('resolution', {...formData.resolution, resolved: e.target.checked})}
                    />
                    Défaillance résolue
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.resolution?.followUpRequired || false}
                      onChange={(e) => handleInputChange('resolution', {...formData.resolution, followUpRequired: e.target.checked})}
                    />
                    Suivi requis
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.documentation?.photos || false}
                      onChange={(e) => handleInputChange('documentation', {...formData.documentation, photos: e.target.checked})}
                    />
                    Photos disponibles
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Symptômes initiaux</label>
                <textarea
                  value={formData.symptoms?.initialSymptoms || ''}
                  onChange={(e) => handleInputChange('symptoms', {...formData.symptoms, initialSymptoms: e.target.value})}
                  placeholder="Description des premiers symptômes observés"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Cause immédiate</label>
                <textarea
                  value={formData.investigation?.immediateCause || ''}
                  onChange={(e) => handleInputChange('investigation', {...formData.investigation, immediateCause: e.target.value})}
                  placeholder="Cause directe de la défaillance"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Cause racine</label>
                <textarea
                  value={formData.investigation?.rootCause || ''}
                  onChange={(e) => handleInputChange('investigation', {...formData.investigation, rootCause: e.target.value})}
                  placeholder="Cause fondamentale identifiée"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Leçons apprises</label>
                <textarea
                  value={formData.lessons?.lessonsLearned || ''}
                  onChange={(e) => handleInputChange('lessons', {...formData.lessons, lessonsLearned: e.target.value})}
                  placeholder="Enseignements tirés de cette défaillance"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* TRANSPORT & LIVRAISON Documents */}
          {selectedType.id === 'route-sheet' && (
            <div className="form-section">
              <h3 className="section-title">
                <Truck size={20} />
                Informations de tournée
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom du chauffeur</label>
                  <input
                    type="text"
                    value={formData.driver?.name || ''}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, name: e.target.value})}
                    placeholder="Nom et prénom du chauffeur"
                  />
                </div>
                <div className="form-group">
                  <label>Numéro de permis</label>
                  <input
                    type="text"
                    value={formData.driver?.licenseNumber || ''}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, licenseNumber: e.target.value})}
                    placeholder="Numéro de permis de conduire"
                  />
                </div>
                <div className="form-group">
                  <label>Expérience chauffeur</label>
                  <select
                    value={formData.driver?.experience || 'confirmé'}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, experience: e.target.value})}
                  >
                    <option value="débutant">Débutant (moins de 2 ans)</option>
                    <option value="confirmé">Confirmé (2-5 ans)</option>
                    <option value="expérimenté">Expérimenté (plus de 5 ans)</option>
                    <option value="expert">Expert (plus de 10 ans)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Immatriculation véhicule</label>
                  <input
                    type="text"
                    value={formData.vehicle?.registration || ''}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, registration: e.target.value})}
                    placeholder="Numéro d'immatriculation"
                  />
                </div>
                <div className="form-group">
                  <label>Type de véhicule</label>
                  <select
                    value={formData.vehicle?.type || 'camion'}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, type: e.target.value})}
                  >
                    <option value="camion">Camion</option>
                    <option value="camionnette">Camionnette</option>
                    <option value="fourgon">Fourgon</option>
                    <option value="semi-remorque">Semi-remorque</option>
                    <option value="utilitaire">Utilitaire</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Capacité de charge (kg)</label>
                  <input
                    type="number"
                    value={formData.vehicle?.capacity || 0}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, capacity: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de la tournée</label>
                  <input
                    type="text"
                    value={formData.route?.name || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, name: e.target.value})}
                    placeholder="Ex: Tournée Nord-Alger"
                  />
                </div>
                <div className="form-group">
                  <label>Territoire de livraison</label>
                  <input
                    type="text"
                    value={formData.route?.territory || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, territory: e.target.value})}
                    placeholder="Zone géographique couverte"
                  />
                </div>
                <div className="form-group">
                  <label>Distance totale (km)</label>
                  <input
                    type="number"
                    value={formData.route?.totalDistance || 0}
                    onChange={(e) => handleInputChange('route', {...formData.route, totalDistance: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Date de départ</label>
                  <input
                    type="date"
                    value={formData.schedule?.departureDate || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, departureDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de départ</label>
                  <input
                    type="time"
                    value={formData.schedule?.departureTime || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, departureTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de retour prévue</label>
                  <input
                    type="time"
                    value={formData.schedule?.plannedReturnTime || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, plannedReturnTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Poids total (kg)</label>
                  <input
                    type="number"
                    value={formData.cargo?.totalWeight || 0}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, totalWeight: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Difficulté de la tournée</label>
                  <select
                    value={formData.route?.difficultyLevel || 'normale'}
                    onChange={(e) => handleInputChange('route', {...formData.route, difficultyLevel: e.target.value})}
                  >
                    <option value="facile">Facile</option>
                    <option value="normale">Normale</option>
                    <option value="difficile">Difficile</option>
                    <option value="très difficile">Très difficile</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Budget carburant (DZD)</label>
                  <input
                    type="number"
                    value={formData.costs?.fuelBudget || 0}
                    onChange={(e) => handleInputChange('costs', {...formData.costs, fuelBudget: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.tracking?.gpsEnabled || false}
                      onChange={(e) => handleInputChange('tracking', {...formData.tracking, gpsEnabled: e.target.checked})}
                    />
                    Suivi GPS activé
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.cargo?.temperatureControlled || false}
                      onChange={(e) => handleInputChange('cargo', {...formData.cargo, temperatureControlled: e.target.checked})}
                    />
                    Marchandises frigorifiques
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.tachographCheck || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, tachographCheck: e.target.checked})}
                    />
                    Contrôle tachygraphe
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'delivery-report' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Rapport de livraison
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Numéro de tournée</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.routeNumber || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, routeNumber: e.target.value})}
                    placeholder="Référence de la tournée"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de la tournée</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.routeName || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, routeName: e.target.value})}
                    placeholder="Ex: Tournée Ouest-Alger"
                  />
                </div>
                <div className="form-group">
                  <label>Chauffeur</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.driver || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, driver: e.target.value})}
                    placeholder="Nom du chauffeur"
                  />
                </div>
                <div className="form-group">
                  <label>Véhicule utilisé</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.vehicle || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, vehicle: e.target.value})}
                    placeholder="Immatriculation du véhicule"
                  />
                </div>
                <div className="form-group">
                  <label>Heure de départ</label>
                  <input
                    type="time"
                    value={formData.timing?.departureTime || ''}
                    onChange={(e) => handleInputChange('timing', {...formData.timing, departureTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Heure de retour</label>
                  <input
                    type="time"
                    value={formData.timing?.returnTime || ''}
                    onChange={(e) => handleInputChange('timing', {...formData.timing, returnTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Livraisons prévues</label>
                  <input
                    type="number"
                    value={formData.performance?.deliveriesPlanned || 0}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, deliveriesPlanned: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Livraisons réalisées</label>
                  <input
                    type="number"
                    value={formData.performance?.deliveriesCompleted || 0}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, deliveriesCompleted: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Livraisons échouées</label>
                  <input
                    type="number"
                    value={formData.performance?.deliveriesFailed || 0}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, deliveriesFailed: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Kilométrage début</label>
                  <input
                    type="number"
                    value={formData.vehicle?.mileageStart || 0}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, mileageStart: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Kilométrage fin</label>
                  <input
                    type="number"
                    value={formData.vehicle?.mileageEnd || 0}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, mileageEnd: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Carburant consommé (L)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.vehicle?.fuelConsumed || 0}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, fuelConsumed: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Coût carburant (DZD)</label>
                  <input
                    type="number"
                    value={formData.vehicle?.fuelCost || 0}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, fuelCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>État du véhicule</label>
                  <select
                    value={formData.vehicle?.condition || 'bon'}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, condition: e.target.value})}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="bon">Bon</option>
                    <option value="satisfaisant">Satisfaisant</option>
                    <option value="médiocre">Médiocre</option>
                    <option value="mauvais">Mauvais</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Total des frais (DZD)</label>
                  <input
                    type="number"
                    value={formData.expenses?.total || 0}
                    onChange={(e) => handleInputChange('expenses', {...formData.expenses, total: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Taux de réussite (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.performance?.deliverySuccessRate || 100}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, deliverySuccessRate: parseFloat(e.target.value)})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.cargo?.damageReported || false}
                      onChange={(e) => handleInputChange('cargo', {...formData.cargo, damageReported: e.target.checked})}
                    />
                    Dommages signalés
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.expenses?.receipts || false}
                      onChange={(e) => handleInputChange('expenses', {...formData.expenses, receipts: e.target.checked})}
                    />
                    Reçus fournis
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.safety?.accidentsReported === 0 ? false : true}
                      onChange={(e) => handleInputChange('safety', {...formData.safety, accidentsReported: e.target.checked ? 1 : 0})}
                    />
                    Accidents signalés
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'transport-voucher' && (
            <div className="form-section">
              <h3 className="section-title">
                <Send size={20} />
                Bon de transport
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Expéditeur</label>
                  <input
                    type="text"
                    value={formData.shipper?.name || ''}
                    onChange={(e) => handleInputChange('shipper', {...formData.shipper, name: e.target.value})}
                    placeholder="Nom de l'expéditeur"
                  />
                </div>
                <div className="form-group">
                  <label>Transporteur</label>
                  <input
                    type="text"
                    value={formData.carrier?.company || ''}
                    onChange={(e) => handleInputChange('carrier', {...formData.carrier, company: e.target.value})}
                    placeholder="Entreprise de transport"
                  />
                </div>
                <div className="form-group">
                  <label>Destinataire</label>
                  <input
                    type="text"
                    value={formData.consignee?.name || ''}
                    onChange={(e) => handleInputChange('consignee', {...formData.consignee, name: e.target.value})}
                    placeholder="Nom du destinataire"
                  />
                </div>
                <div className="form-group">
                  <label>Mode de transport</label>
                  <select
                    value={formData.transport?.mode || 'routier'}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, mode: e.target.value})}
                  >
                    <option value="routier">Routier</option>
                    <option value="ferroviaire">Ferroviaire</option>
                    <option value="maritime">Maritime</option>
                    <option value="aérien">Aérien</option>
                    <option value="multimodal">Multimodal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Type de service</label>
                  <select
                    value={formData.transport?.type || 'livraison'}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, type: e.target.value})}
                  >
                    <option value="livraison">Livraison directe</option>
                    <option value="messagerie">Messagerie</option>
                    <option value="groupage">Groupage</option>
                    <option value="déménagement">Déménagement</option>
                    <option value="frigorifique">Transport frigorifique</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau d'urgence</label>
                  <select
                    value={formData.transport?.urgency || 'normale'}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, urgency: e.target.value})}
                  >
                    <option value="normale">Normale</option>
                    <option value="prioritaire">Prioritaire</option>
                    <option value="urgente">Urgente</option>
                    <option value="express">Express</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'enlèvement</label>
                  <input
                    type="date"
                    value={formData.transport?.pickupDate || ''}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, pickupDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Date de livraison</label>
                  <input
                    type="date"
                    value={formData.transport?.deliveryDate || ''}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, deliveryDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Type d'emballage</label>
                  <select
                    value={formData.cargo?.packagingType || 'carton'}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, packagingType: e.target.value})}
                  >
                    <option value="carton">Carton</option>
                    <option value="caisse">Caisse bois</option>
                    <option value="palette">Palette</option>
                    <option value="fût">Fût</option>
                    <option value="vrac">Vrac</option>
                    <option value="conteneur">Conteneur</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nombre de colis</label>
                  <input
                    type="number"
                    value={formData.cargo?.packages || 0}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, packages: parseInt(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Poids total (kg)</label>
                  <input
                    type="number"
                    value={formData.cargo?.weight || 0}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, weight: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Valeur déclarée (DZD)</label>
                  <input
                    type="number"
                    value={formData.cargo?.value || 0}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, value: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Coût transport (DZD)</label>
                  <input
                    type="number"
                    value={formData.pricing?.transportCost || 0}
                    onChange={(e) => handleInputChange('pricing', {...formData.pricing, transportCost: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Distance (km)</label>
                  <input
                    type="number"
                    value={formData.route?.distance || 0}
                    onChange={(e) => handleInputChange('route', {...formData.route, distance: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Origine</label>
                  <input
                    type="text"
                    value={formData.route?.origin || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, origin: e.target.value})}
                    placeholder="Adresse de départ"
                  />
                </div>
                <div className="form-group">
                  <label>Destination</label>
                  <input
                    type="text"
                    value={formData.route?.destination || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, destination: e.target.value})}
                    placeholder="Adresse de livraison"
                  />
                </div>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <select
                    value={formData.pricing?.paymentTerms || '30 jours'}
                    onChange={(e) => handleInputChange('pricing', {...formData.pricing, paymentTerms: e.target.value})}
                  >
                    <option value="comptant">Comptant</option>
                    <option value="15 jours">15 jours</option>
                    <option value="30 jours">30 jours</option>
                    <option value="60 jours">60 jours</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.fragile || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, fragile: e.target.checked})}
                    />
                    Marchandise fragile
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.hazardous || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, hazardous: e.target.checked})}
                    />
                    Matières dangereuses
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.insurance?.required || false}
                      onChange={(e) => handleInputChange('insurance', {...formData.insurance, required: e.target.checked})}
                    />
                    Assurance requise
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.temperatureControlled || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, temperatureControlled: e.target.checked})}
                    />
                    Transport frigorifique
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Description marchandise</label>
                <textarea
                  value={formData.cargo?.description || ''}
                  onChange={(e) => handleInputChange('cargo', {...formData.cargo, description: e.target.value})}
                  placeholder="Description détaillée de la marchandise"
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label>Adresse de livraison complète</label>
                <textarea
                  value={formData.consignee?.address || ''}
                  onChange={(e) => handleInputChange('consignee', {...formData.consignee, address: e.target.value})}
                  placeholder="Adresse complète de livraison"
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* Taxes and Totals - Only for documents with financial calculations */}
          {(formData.items || (selectedType.id === 'quote-request' && formData.specifications)) && (
            <div className="form-section">
              <h3 className="section-title">
                <DollarSign size={20} />
                {selectedType.id === 'quote' ? 'Remise et totaux' : 'Taxes et totaux'}
              </h3>

              <div className="totals-container">
                <div className="taxes-config">
                  {selectedType.id === 'quote' && (
                    <div className="form-group">
                      <label>Remise (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.discount || 0}
                        onChange={(e) => handleInputChange('discount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  )}
                  
                  {/* TVA Toggle and Input */}
                  <div className="form-group toggle-group">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.showTva !== false}
                        onChange={(e) => handleInputChange('showTva', e.target.checked)}
                      />
                      <span className="toggle-text">TVA (%)</span>
                    </label>
                    {formData.showTva !== false && (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.tvaRate ?? 19}
                        onChange={(e) => handleInputChange('tvaRate', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        className="toggle-input"
                      />
                    )}
                  </div>

                  {/* TAP Toggle and Input (for proforma) */}
                  {selectedType.id === 'proforma' && (
                    <div className="form-group toggle-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={formData.showTap !== false}
                          onChange={(e) => handleInputChange('showTap', e.target.checked)}
                        />
                        <span className="toggle-text">TAP (%)</span>
                      </label>
                      {formData.showTap !== false && (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.tapRate || 2}
                          onChange={(e) => handleInputChange('tapRate', parseFloat(e.target.value) || 0)}
                          className="toggle-input"
                        />
                      )}
                    </div>
                  )}

                  {/* Remise de garantie Toggle (not for proforma) */}
                  {selectedType.id !== 'proforma' && (
                    <div className="form-group toggle-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={formData.showGuarantee || false}
                          onChange={(e) => handleInputChange('showGuarantee', e.target.checked)}
                        />
                        <span className="toggle-text">Remise de garantie</span>
                      </label>
                      {formData.showGuarantee && (
                        <input
                          type="number"
                          step="0.01"
                          value={formData.guarantee || 0}
                          onChange={(e) => handleInputChange('guarantee', parseFloat(e.target.value) || 0)}
                          className="toggle-input"
                        />
                      )}
                    </div>
                  )}

                  {/* Timbre Toggle (not for proforma) */}
                  {selectedType.id !== 'proforma' && (
                    <div className="form-group toggle-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={formData.showStamp || false}
                          onChange={(e) => handleInputChange('showStamp', e.target.checked)}
                        />
                        <span className="toggle-text">Timbre</span>
                      </label>
                      {formData.showStamp && (
                        <input
                          type="number"
                          step="0.01"
                          value={formData.stamp || 0}
                          onChange={(e) => handleInputChange('stamp', parseFloat(e.target.value) || 0)}
                          className="toggle-input"
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="totals-summary">
                  {(() => {
                    const items = formData.items || [];
                    
                    // Calculate subtotal from items or specifications
                    let subtotal = 0;
                    
                    // Handle specifications for quote-request documents
                    if (selectedType.id === 'quote-request' && formData.specifications && formData.specifications.length > 0) {
                      subtotal = formData.specifications.reduce((sum, spec) => {
                        const budget = parseFloat(spec.budget) || 0;
                        const quantity = parseInt(spec.quantity) || 1;
                        return sum + (budget * quantity);
                      }, 0);
                    } else {
                      // Standard calculation for other document types
                      subtotal = items.reduce((sum, item) => {
                      if (item.total) {
                        return sum + parseFloat(item.total);
                      } else {
                        const unitPrice = parseFloat(item.price || item.unitPrice) || 0;
                        const quantity = parseInt(item.quantity) || 1;
                        return sum + (unitPrice * quantity);
                      }
                    }, 0);
                    }
                    
                    // Calculate taxes based on toggle states
                    const showTva = formData.showTva !== false;
                    const showTap = formData.showTap !== false && selectedType.id === 'proforma';
                    const showGuarantee = formData.showGuarantee && selectedType.id !== 'proforma';
                    const showStamp = formData.showStamp && selectedType.id !== 'proforma';
                    
                    // Always use 19% as default TVA rate if not explicitly set
                    const tvaRate = showTva ? (parseFloat(formData.tvaRate) || 19) : 0;
                    const tapRate = showTap ? parseFloat(formData.tapRate || 2) : 0;
                    const tvaAmount = subtotal * (tvaRate / 100);
                    const tapAmount = subtotal * (tapRate / 100);
                    const totalTTC = subtotal + tvaAmount + tapAmount;
                    const guarantee = showGuarantee ? parseFloat(formData.guarantee) || 0 : 0;
                    const stamp = showStamp ? parseFloat(formData.stamp) || 0 : 0;
                    const netToPay = totalTTC - guarantee + stamp;
                    
                    return (
                      <>
                        <div className="total-line">
                          <span>Total HT:</span>
                          <span>{formatDZD(subtotal)}</span>
                        </div>
                        {showTva && (
                          <div className="total-line">
                            <span>TVA ({tvaRate}%):</span>
                            <span>{formatDZD(tvaAmount)}</span>
                          </div>
                        )}
                        {showTap && (
                          <div className="total-line">
                            <span>TAP ({tapRate}%):</span>
                            <span>{formatDZD(tapAmount)}</span>
                          </div>
                        )}
                        <div className="total-line">
                          <span>Total TTC:</span>
                          <span>{formatDZD(totalTTC)}</span>
                        </div>
                        {showGuarantee && (
                          <div className="total-line">
                            <span>Remise de garantie:</span>
                            <span>{formatDZD(guarantee)}</span>
                          </div>
                        )}
                        {showStamp && (
                          <div className="total-line">
                            <span>Timbre:</span>
                            <span>{formatDZD(stamp)}</span>
                          </div>
                        )}
                        <div className="total-line total">
                          <span>Net à Payé:</span>
                          <span>{formatDZD(netToPay)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Attachment Description Section */}
          {selectedType.id === 'attachment' && (
            <div className="form-section">
              <h3 className="section-title">
                <FileText size={20} />
                Description du document
              </h3>
              
              <div className="form-group">
                <label>Description détaillée</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Décrivez le contenu et l'objectif du document..."
                  rows="4"
                />
              </div>
            </div>
          )}

          {/* Notes and Conditions */}
          <div className="form-section">
            <h3 className="section-title">
              <FileText size={20} />
              {selectedType.id === 'quote-request' ? 'Exigences spéciales' :
               selectedType.id === 'quote' ? 'Conditions commerciales' :
               selectedType.id === 'purchase-order' ? 'Conditions de commande' :
               selectedType.id === 'delivery-note' ? 'Conditions de livraison' :
               selectedType.id === 'shipping-slip' ? 'Instructions d\'expédition' :
               selectedType.id === 'attachment' ? 'Notes' : 'Notes et conditions'}
            </h3>

            {selectedType.id === 'quote' && (
              <>
                <div className="form-group">
                  <label>Conditions de paiement</label>
                  <select
                    value={formData.paymentConditions}
                    onChange={(e) => handleInputChange('paymentConditions', e.target.value)}
                  >
                    <option value="Paiement à 30 jours">Paiement à 30 jours</option>
                    <option value="Paiement à 15 jours">Paiement à 15 jours</option>
                    <option value="Paiement comptant">Paiement comptant</option>
                    <option value="Paiement à la livraison">Paiement à la livraison</option>
                    <option value="50% à la commande, 50% à la livraison">50% à la commande, 50% à la livraison</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Conditions particulières</label>
                  <textarea
                    value={formData.conditions}
                    onChange={(e) => handleInputChange('conditions', e.target.value)}
                    placeholder="Conditions particulières du devis..."
                    rows="4"
                  />
                </div>
              </>
            )}

            {selectedType.id === 'purchase-order' && (
              <>
                <div className="form-group">
                  <label>Approuvé par</label>
                  <input
                    type="text"
                    value={formData.approvedBy}
                    onChange={(e) => handleInputChange('approvedBy', e.target.value)}
                    placeholder="Nom du responsable approbateur"
                  />
                </div>
                <div className="form-group">
                  <label>Conditions de commande</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => handleInputChange('terms', e.target.value)}
                    placeholder="Conditions particulières de la commande..."
                    rows="4"
                  />
                </div>
              </>
            )}

            {selectedType.id === 'delivery-note' && (
              <>
                <div className="form-group">
                  <label>Reçu par</label>
                  <input
                    type="text"
                    value={formData.receivedBy}
                    onChange={(e) => handleInputChange('receivedBy', e.target.value)}
                    placeholder="Nom de la personne ayant reçu"
                  />
                </div>
                <div className="form-group">
                  <label>Conditions de livraison</label>
                  <textarea
                    value={formData.deliveryConditions}
                    onChange={(e) => handleInputChange('deliveryConditions', e.target.value)}
                    placeholder="État des produits livrés, observations..."
                    rows="3"
                  />
                </div>
              </>
            )}

            {selectedType.id === 'shipping-slip' && (
              <div className="form-group">
                <label>Instructions d'expédition</label>
                <textarea
                  value={formData.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  placeholder="Instructions particulières pour l'expédition..."
                  rows="3"
                />
              </div>
            )}

            {(selectedType.id === 'proforma' || 
              selectedType.id === 'quote-request' || selectedType.id === 'attachment') && (
              <div className="form-group">
                <textarea
                  value={selectedType.id === 'quote-request' ? formData.requirements : 
                         selectedType.id === 'proforma' ? formData.conditions :
                         selectedType.id === 'attachment' ? formData.notes : formData.notes}
                  onChange={(e) => handleInputChange(
                    selectedType.id === 'quote-request' ? 'requirements' : 
                    selectedType.id === 'proforma' ? 'conditions' :
                    selectedType.id === 'attachment' ? 'notes' : 'notes', 
                    e.target.value
                  )}
                  placeholder={
                    selectedType.id === 'quote-request' ? 'Décrivez vos exigences particulières...' :
                    selectedType.id === 'proforma' ? 'Conditions particulières de la facture proforma...' :
                    selectedType.id === 'attachment' ? 'Notes additionnelles...' :
                    'Notes ou conditions particulières...'
                  }
                  rows="4"
                />
              </div>
            )}

            {(selectedType.id === 'invoice' || 
              !['quote', 'purchase-order', 'delivery-note', 'shipping-slip', 'quote-request', 'proforma', 'attachment'].includes(selectedType.id)) && (
              <div className="form-group">
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notes ou conditions particulières..."
                  rows="4"
                />
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="document-creation fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <Plus className="title-icon" />
          Créer un document
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>
          Choisissez le type de document que vous souhaitez créer
        </p>
      </div>

      <div className="creation-content">
        <div className="document-categories">
          {documentCategories.map((category, categoryIndex) => (
            <div key={category.title} className="category-section" style={{'--category-color': category.color}}>
              <div className="category-header">
                <h2 className="category-title">
                  {category.title}
                  {category.beta && (
                    <span className="beta-badge">BETA</span>
                  )}
                </h2>
                <div className="category-line"></div>
              </div>
              
              <div className="documents-grid">
                {category.documents.map((document) => {
                  const Icon = document.icon;
                  const isSelected = selectedType?.id === document.id;
                  
                  return (
                    <div
                      key={document.id}
                      className={`document-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleDocumentSelect(document)}
                      style={{'--doc-color': document.color}}
                    >
                      <div className="document-icon">
                        <Icon size={24} />
                      </div>
                      <div className="document-content">
                        <h3 className="document-title">{document.title}</h3>
                        <p className="document-subtitle">{document.subtitle}</p>
                        <p className="document-description">{document.description}</p>
                      </div>
                      <div className="document-arrow">
                        <ArrowRight size={20} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DocumentCreation;